export const carePlans = [
  // Basic & General Care
  {
    id: 'acute-confusion',
    specialtyId: 'basic-general-care',
    title: 'Acute Confusion (Delirium) and Altered Mental Status',
    description: 'Nursing care plan for patients experiencing acute confusion and delirium',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Acute confusion related to physiological changes, medication effects, or environmental factors',
        'Risk for injury related to altered mental status and disorientation',
        'Disturbed sleep pattern related to agitation and confusion',
        'Anxiety (family) related to patient\'s altered mental status and behavioral changes'
      ],
      goals: [
        'Patient will demonstrate improved orientation and cognitive function',
        'Patient will remain free from injury during confused state',
        'Patient will achieve adequate rest and sleep patterns',
        'Family will demonstrate understanding of delirium and coping strategies'
      ],
      interventions: [
        'Assess mental status using standardized tools (CAM, GCS) every 4 hours',
        'Identify and treat underlying causes of confusion (infection, medications, electrolyte imbalance)',
        'Maintain consistent caregivers and daily routines',
        'Provide frequent reorientation to person, place, and time',
        'Ensure adequate lighting and minimize environmental stimuli',
        'Keep familiar objects and family photos at bedside',
        'Monitor for signs of agitation and implement calming techniques',
        'Educate family about delirium and ways to support patient'
      ],
      evaluation: [
        'Patient shows improved orientation to person, place, and time',
        'No falls or injuries occurred during confused episodes',
        'Patient sleeps 6-8 hours with minimal interruptions',
        'Family verbalizes understanding of condition and demonstrates supportive behaviors'
      ]
    }
  },
  {
    id: 'acute-pain',
    specialtyId: 'basic-general-care',
    title: 'Acute Pain and Pain Management',
    description: 'Comprehensive nursing care plan for acute pain management',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Acute pain related to tissue injury, inflammation, or surgical procedure',
        'Impaired mobility related to pain and discomfort',
        'Sleep disturbance related to pain and discomfort',
        'Anxiety related to pain experience and fear of inadequate pain relief'
      ],
      goals: [
        'Patient will report pain level of 3 or less on 0-10 pain scale',
        'Patient will demonstrate improved mobility and functional activity',
        'Patient will achieve adequate rest and sleep',
        'Patient will verbalize effective pain management strategies'
      ],
      interventions: [
        'Assess pain using standardized pain scale (0-10) every 2-4 hours',
        'Administer prescribed analgesics around-the-clock for first 24-48 hours',
        'Monitor for side effects of pain medications (respiratory depression, constipation)',
        'Apply non-pharmacological pain relief methods (ice, heat, positioning)',
        'Teach relaxation techniques and guided imagery',
        'Encourage early mobilization as tolerated',
        'Assess effectiveness of pain interventions 30 minutes after administration',
        'Educate patient on pain management options and when to report pain'
      ],
      evaluation: [
        'Patient reports pain level consistently at 3 or below',
        'Patient participates in activities of daily living with minimal pain',
        'Patient sleeps for 6-8 hour periods without pain interruption',
        'Patient demonstrates understanding of pain management techniques'
      ]
    }
  },
  {
    id: 'activity-intolerance',
    specialtyId: 'basic-general-care',
    title: 'Activity Intolerance and Generalized Weakness',
    description: 'Care plan for patients with activity intolerance and weakness',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Activity intolerance related to decreased strength, endurance, or cardiovascular deconditioning',
        'Risk for falls related to weakness and decreased mobility',
        'Self-care deficit related to fatigue and decreased functional capacity',
        'Risk for complications of immobility (thrombosis, pneumonia, pressure ulcers)'
      ],
      goals: [
        'Patient will tolerate increased activity without excessive fatigue or dyspnea',
        'Patient will maintain safety during mobility and transfers',
        'Patient will perform activities of daily living with minimal assistance',
        'Patient will remain free from complications related to immobility'
      ],
      interventions: [
        'Assess baseline activity tolerance and vital sign response to activity',
        'Plan activities after rest periods and coordinate with meals',
        'Gradually increase activity levels as tolerated',
        'Monitor vital signs before, during, and after activities',
        'Provide assistive devices as needed (walker, cane, wheelchair)',
        'Teach energy conservation techniques',
        'Encourage active and passive range of motion exercises',
        'Implement fall prevention measures and safe transfer techniques'
      ],
      evaluation: [
        'Patient demonstrates improved endurance with activities',
        'No falls or injuries occurred during activity progression',
        'Patient performs self-care activities with increased independence',
        'Patient shows no signs of immobility complications'
      ]
    }
  },
  {
    id: 'cancer-oncology',
    specialtyId: 'basic-general-care',
    title: 'Cancer (Oncology Nursing)',
    description: 'Comprehensive oncology nursing care plan',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Risk for infection related to immunosuppression from chemotherapy',
        'Imbalanced nutrition: less than body requirements related to nausea, vomiting, and decreased appetite',
        'Acute pain related to disease process and treatment effects',
        'Anxiety and fear related to cancer diagnosis and treatment uncertainties'
      ],
      goals: [
        'Patient will remain free from infection throughout treatment',
        'Patient will maintain adequate nutritional status and stable weight',
        'Patient will report manageable pain levels (≤3/10)',
        'Patient will demonstrate effective coping strategies and reduced anxiety'
      ],
      interventions: [
        'Monitor for signs of infection and implement neutropenic precautions when indicated',
        'Assess nutritional status and collaborate with dietitian for meal planning',
        'Administer anti-emetics before and after chemotherapy as prescribed',
        'Provide small, frequent meals and nutritional supplements',
        'Assess pain regularly and administer analgesics as prescribed',
        'Provide emotional support and facilitate access to counseling services',
        'Educate patient and family about treatment, side effects, and self-care',
        'Monitor laboratory values (CBC, chemistry panel) before each treatment'
      ],
      evaluation: [
        'Patient shows no signs of infection (normal WBC, afebrile)',
        'Patient maintains weight within 5% of baseline',
        'Patient reports pain as manageable and does not interfere with daily activities',
        'Patient verbalizes reduced anxiety and demonstrates positive coping behaviors'
      ]
    }
  },
  {
    id: 'caregiver-strain',
    specialtyId: 'basic-general-care',
    title: 'Caregiver Role Strain and Family Caregiver Support Systems',
    description: 'Care plan for caregiver support and strain management',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Caregiver role strain related to complexity of care needs and lack of respite',
        'Risk for compromised family coping related to prolonged stress and burden',
        'Deficient knowledge related to caregiving techniques and available resources',
        'Risk for caregiver injury related to improper body mechanics and transfer techniques'
      ],
      goals: [
        'Caregiver will demonstrate reduced signs of strain and stress',
        'Family will utilize effective coping strategies and support systems',
        'Caregiver will demonstrate competency in patient care techniques',
        'Caregiver will remain free from injury during care activities'
      ],
      interventions: [
        'Assess caregiver\'s physical and emotional health status regularly',
        'Provide education on caregiving techniques, medication management, and safety',
        'Connect caregiver with community resources and support groups',
        'Arrange for respite care services when available',
        'Teach proper body mechanics and use of assistive devices',
        'Encourage caregiver to maintain personal health and social connections',
        'Provide emotional support and validate caregiver experiences',
        'Develop emergency contact list and care plan for crisis situations'
      ],
      evaluation: [
        'Caregiver reports decreased stress levels and improved well-being',
        'Family demonstrates use of support systems and coping strategies',
        'Caregiver performs care tasks safely and effectively',
        'No caregiver injuries related to patient care activities'
      ]
    }
  },
  {
    id: 'chronic-confusion',
    specialtyId: 'basic-general-care',
    title: 'Chronic Confusion (Dementia)',
    description: 'Nursing care plan for dementia and chronic confusion',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Chronic confusion related to neurological changes and cognitive decline',
        'Risk for injury related to impaired judgment and wandering behavior',
        'Self-care deficit related to cognitive impairment and memory loss',
        'Disturbed sleep pattern related to sundowning and agitation'
      ],
      goals: [
        'Patient will maintain optimal cognitive function within current capabilities',
        'Patient will remain safe and free from injury',
        'Patient will receive assistance with activities of daily living as needed',
        'Patient will maintain adequate sleep and rest patterns'
      ],
      interventions: [
        'Assess cognitive status using standardized tools (MMSE, Montreal Cognitive Assessment)',
        'Maintain consistent daily routines and familiar environment',
        'Use validation therapy and avoid confronting confused statements',
        'Implement safety measures including ID bracelet and door alarms',
        'Provide structured activities appropriate to cognitive level',
        'Assist with personal care while encouraging independence when possible',
        'Use calm, simple communication with one instruction at a time',
        'Manage sundowning with consistent bedtime routines and adequate lighting'
      ],
      evaluation: [
        'Patient demonstrates stable cognitive function without further decline',
        'No injuries or incidents related to confusion or wandering',
        'Patient receives appropriate assistance with daily care needs',
        'Patient sleeps through the night with minimal agitation or wandering'
      ]
    }
  },
  {
    id: 'end-of-life-care',
    specialtyId: 'basic-general-care',
    title: 'End-of-Life Care (Hospice Care or Palliative)',
    description: 'Palliative and hospice nursing care plan',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Chronic pain related to disease progression and terminal illness',
        'Anticipatory grieving related to impending loss of life',
        'Spiritual distress related to questions about meaning and afterlife',
        'Compromised family coping related to impending death of loved one'
      ],
      goals: [
        'Patient will achieve optimal comfort and pain control',
        'Patient and family will process grief in healthy manner',
        'Patient will experience spiritual comfort and peace',
        'Family will demonstrate effective coping and support systems'
      ],
      interventions: [
        'Assess pain regularly and administer medications to maintain comfort',
        'Provide emotional support and active listening for patient and family',
        'Facilitate spiritual care and chaplain visits as desired',
        'Ensure dignity and respect for patient preferences and cultural values',
        'Educate family about what to expect during dying process',
        'Coordinate with hospice team for comprehensive care approach',
        'Provide bereavement support and resources to family members',
        'Honor advance directives and patient autonomy in decision-making'
      ],
      evaluation: [
        'Patient reports pain is well-controlled and manageable',
        'Patient and family express feelings and process grief appropriately',
        'Patient demonstrates peace and spiritual comfort',
        'Family shows effective coping and utilizes available support resources'
      ]
    }
  },
  {
    id: 'fall-risk',
    specialtyId: 'basic-general-care',
    title: 'Fall Risk and Fall Prevention',
    description: 'Evidence-based fall prevention nursing care plan',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Risk for falls related to muscle weakness, medication effects, or environmental hazards',
        'Risk for injury related to impaired mobility and balance',
        'Fear related to previous fall experience or risk of falling',
        'Impaired physical mobility related to fall risk precautions and safety measures'
      ],
      goals: [
        'Patient will remain free from falls during hospitalization or care period',
        'Patient will demonstrate safe mobility and transfer techniques',
        'Patient will verbalize understanding of fall prevention strategies',
        'Patient will maintain functional mobility while ensuring safety'
      ],
      interventions: [
        'Complete fall risk assessment using standardized tool (Morse Fall Scale)',
        'Implement fall prevention protocol including bed alarms and assistance with ambulation',
        'Keep call light within reach and respond promptly to requests',
        'Ensure adequate lighting and remove environmental hazards',
        'Provide non-slip footwear and encourage use of assistive devices',
        'Perform hourly rounding to assess patient needs and safety',
        'Educate patient and family about fall prevention strategies',
        'Review medications for those that increase fall risk (sedatives, diuretics)'
      ],
      evaluation: [
        'No falls occurred during care period',
        'Patient demonstrates safe transfer and mobility techniques',
        'Patient and family verbalize understanding of fall prevention measures',
        'Patient maintains independence in activities while following safety protocols'
      ]
    }
  },
  {
    id: 'fatigue',
    specialtyId: 'basic-general-care',
    title: 'Fatigue and Lethargy',
    description: 'Care plan for managing patient fatigue and lethargy',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Fatigue related to disease process, treatment effects, or sleep deprivation',
        'Activity intolerance related to overwhelming exhaustion and weakness',
        'Self-care deficit related to lack of energy for daily activities',
        'Risk for injury related to decreased alertness and coordination'
      ],
      goals: [
        'Patient will report improved energy levels and reduced fatigue',
        'Patient will demonstrate increased tolerance for daily activities',
        'Patient will perform self-care activities with assistance as needed',
        'Patient will remain safe and free from injury related to fatigue'
      ],
      interventions: [
        'Assess fatigue level using standardized fatigue scale (0-10)',
        'Identify and treat underlying causes of fatigue (anemia, infection, medications)',
        'Plan activities during patient\'s peak energy times',
        'Encourage frequent rest periods between activities',
        'Teach energy conservation techniques and pacing strategies',
        'Ensure adequate nutrition and hydration to support energy levels',
        'Promote good sleep hygiene and adequate nighttime rest',
        'Provide assistance with activities of daily living as needed'
      ],
      evaluation: [
        'Patient reports decreased fatigue levels (≤5/10 on fatigue scale)',
        'Patient participates in planned activities without excessive exhaustion',
        'Patient completes self-care activities with appropriate level of assistance',
        'No injuries or incidents related to fatigue or decreased alertness'
      ]
    }
  },
  {
    id: 'geriatric-nursing',
    specialtyId: 'basic-general-care',
    title: 'Geriatric Nursing (Older Adult)',
    description: 'Comprehensive geriatric nursing care plan',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Risk for falls related to age-related changes in balance and mobility',
        'Risk for polypharmacy complications related to multiple medications',
        'Risk for social isolation related to decreased social networks and mobility',
        'Risk for cognitive decline related to aging process and comorbidities'
      ],
      goals: [
        'Patient will maintain safety and independence in daily activities',
        'Patient will demonstrate understanding of medication management',
        'Patient will maintain social connections and community involvement',
        'Patient will maintain optimal cognitive function for age'
      ],
      interventions: [
        'Conduct comprehensive geriatric assessment including cognitive, functional, and social domains',
        'Review all medications for appropriateness and potential interactions',
        'Implement fall prevention strategies and home safety assessment',
        'Encourage social activities and community involvement',
        'Provide education on age-appropriate health promotion activities',
        'Assess for depression and elder abuse or neglect',
        'Coordinate care with interdisciplinary geriatric team',
        'Promote advance directive completion and end-of-life planning'
      ],
      evaluation: [
        'Patient demonstrates safe mobility and no falls occurred',
        'Medication regimen is appropriate with no adverse drug interactions',
        'Patient maintains meaningful social relationships and activities',
        'Patient shows stable or improved cognitive function'
      ]
    }
  },
  {
    id: 'grieving-loss',
    specialtyId: 'basic-general-care',
    title: 'Grieving and Loss',
    description: 'Nursing care plan for grief and bereavement support',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Complicated grieving related to loss of loved one or significant life change',
        'Risk for social isolation related to withdrawal during grief process',
        'Disturbed sleep pattern related to emotional distress and grief',
        'Risk for ineffective coping related to overwhelming grief and loss'
      ],
      goals: [
        'Patient will progress through grief process in healthy manner',
        'Patient will maintain social connections and support systems',
        'Patient will achieve adequate rest and sleep',
        'Patient will demonstrate effective coping strategies for dealing with loss'
      ],
      interventions: [
        'Assess patient\'s grief response and stage of grieving process',
        'Provide therapeutic communication and active listening',
        'Encourage expression of feelings through talking, writing, or creative outlets',
        'Connect patient with grief support groups and counseling resources',
        'Respect cultural and spiritual practices related to grief and loss',
        'Encourage maintenance of social relationships and daily routines',
        'Provide education about normal grief responses and when to seek help',
        'Collaborate with chaplain, social worker, or mental health professionals'
      ],
      evaluation: [
        'Patient expresses feelings appropriately and shows progress in grief work',
        'Patient maintains relationships and participates in meaningful activities',
        'Patient reports improved sleep quality and duration',
        'Patient demonstrates healthy coping mechanisms and seeks support when needed'
      ]
    }
  },
  {
    id: 'hypothermia',
    specialtyId: 'basic-general-care',
    title: 'Hypothermia and Cold Injuries',
    description: 'Care plan for hypothermia and cold-related injuries',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Hypothermia related to exposure to cold environment or impaired thermoregulation',
        'Risk for tissue injury related to cold exposure and frostbite',
        'Risk for decreased cardiac output related to hypothermia-induced bradycardia',
        'Risk for infection related to tissue damage from cold injury'
      ],
      goals: [
        'Patient will achieve and maintain normal body temperature (98.6°F/37°C)',
        'Patient will maintain tissue integrity without frostbite or cold injury',
        'Patient will maintain stable cardiac output and hemodynamic status',
        'Patient will remain free from infection related to cold injury'
      ],
      interventions: [
        'Monitor core body temperature continuously using appropriate method',
        'Implement gradual rewarming techniques to prevent shock and arrhythmias',
        'Provide warm, dry clothing and blankets as appropriate',
        'Monitor cardiac rhythm and vital signs every 15 minutes during rewarming',
        'Assess extremities for signs of frostbite or tissue damage',
        'Handle patient gently to prevent triggering cardiac arrhythmias',
        'Administer warm, humidified oxygen if indicated',
        'Educate patient on prevention of cold exposure and hypothermia'
      ],
      evaluation: [
        'Patient maintains core body temperature within normal range',
        'No evidence of frostbite or cold-related tissue damage',
        'Patient maintains stable heart rate and blood pressure',
        'Patient shows no signs of infection at cold injury sites'
      ]
    }
  },
  {
    id: 'hyperthermia-fever',
    specialtyId: 'basic-general-care',
    title: 'Hyperthermia (Fever)',
    description: 'Nursing care plan for fever management and hyperthermia',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Hyperthermia related to infection, inflammation, or impaired thermoregulation',
        'Risk for fluid volume deficit related to increased metabolic demands and diaphoresis',
        'Discomfort related to fever, chills, and associated symptoms',
        'Risk for seizures related to elevated body temperature (especially in children)'
      ],
      goals: [
        'Patient will achieve and maintain body temperature within normal range',
        'Patient will maintain adequate fluid and electrolyte balance',
        'Patient will report increased comfort and decreased fever-related symptoms',
        'Patient will remain free from fever-related complications'
      ],
      interventions: [
        'Monitor temperature every 2-4 hours and PRN using consistent method',
        'Administer antipyretic medications as prescribed (acetaminophen, ibuprofen)',
        'Implement cooling measures: light clothing, cool environment, tepid sponging',
        'Encourage increased fluid intake (oral or IV as appropriate)',
        'Monitor for signs of dehydration and electrolyte imbalance',
        'Assess for underlying cause of fever and implement appropriate treatments',
        'Provide comfort measures and rest periods',
        'Monitor for signs of febrile seizures, especially in pediatric patients'
      ],
      evaluation: [
        'Patient maintains temperature below 101°F (38.3°C)',
        'Patient shows adequate hydration with normal urine output and skin turgor',
        'Patient reports decreased discomfort and improved sense of well-being',
        'No fever-related complications (seizures, dehydration) occurred'
      ]
    }
  },
  {
    id: 'impaired-swallowing',
    specialtyId: 'basic-general-care',
    title: 'Impaired Swallowing (Dysphagia)',
    description: 'Care plan for dysphagia and swallowing difficulties',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Impaired swallowing related to neurological dysfunction, structural abnormalities, or muscle weakness',
        'Risk for aspiration related to difficulty coordinating swallowing mechanism',
        'Imbalanced nutrition: less than body requirements related to difficulty eating',
        'Risk for dehydration related to reduced fluid intake'
      ],
      goals: [
        'Patient will maintain safe swallowing function without aspiration',
        'Patient will demonstrate reduced risk of aspiration pneumonia',
        'Patient will maintain adequate nutritional status and body weight',
        'Patient will maintain adequate hydration status'
      ],
      interventions: [
        'Assess swallowing ability and conduct bedside swallow evaluation',
        'Collaborate with speech-language pathologist for comprehensive evaluation',
        'Position patient upright at 90 degrees for eating and drinking',
        'Modify food textures and liquid consistency as recommended',
        'Supervise meals and provide cueing for safe swallowing techniques',
        'Monitor for signs of aspiration during and after meals',
        'Consider alternative feeding methods if oral intake insufficient',
        'Educate patient and family on safe swallowing strategies'
      ],
      evaluation: [
        'Patient demonstrates improved swallowing function with no choking episodes',
        'No signs of aspiration pneumonia or respiratory complications',
        'Patient maintains stable weight and adequate nutritional intake',
        'Patient shows adequate hydration with normal urine output'
      ]
    }
  },
  {
    id: 'insomnia',
    specialtyId: 'basic-general-care',
    title: 'Insomnia and Sleep Deprivation',
    description: 'Nursing care plan for sleep disorders and insomnia',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Insomnia related to anxiety, pain, medications, or environmental factors',
        'Sleep deprivation related to inability to maintain adequate sleep patterns',
        'Fatigue related to insufficient restorative sleep',
        'Risk for injury related to decreased alertness and concentration'
      ],
      goals: [
        'Patient will achieve 6-8 hours of uninterrupted sleep per night',
        'Patient will demonstrate improved sleep quality and restfulness',
        'Patient will report increased daytime energy and alertness',
        'Patient will remain safe and free from injury related to sleep deprivation'
      ],
      interventions: [
        'Assess sleep patterns, habits, and factors contributing to insomnia',
        'Establish consistent bedtime routine and sleep-wake schedule',
        'Create optimal sleep environment: quiet, dark, comfortable temperature',
        'Limit caffeine, alcohol, and large meals before bedtime',
        'Encourage relaxation techniques: deep breathing, progressive muscle relaxation',
        'Minimize daytime napping and increase daytime activity',
        'Review medications that may interfere with sleep',
        'Provide sleep hygiene education and sleep diary if appropriate'
      ],
      evaluation: [
        'Patient reports sleeping 6-8 hours without frequent awakenings',
        'Patient verbalizes feeling rested and refreshed upon waking',
        'Patient demonstrates improved daytime functioning and energy levels',
        'No accidents or injuries related to fatigue or decreased alertness'
      ]
    }
  },
  {
    id: 'prolonged-bed-rest',
    specialtyId: 'basic-general-care',
    title: 'Prolonged Bed Rest',
    description: 'Care plan for complications of immobility and bed rest',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Risk for disuse syndrome related to prolonged immobility and bed rest',
        'Risk for pressure ulcers related to prolonged pressure on bony prominences',
        'Risk for thromboembolism related to venous stasis and immobility',
        'Risk for pneumonia related to decreased lung expansion and secretion retention'
      ],
      goals: [
        'Patient will maintain muscle strength and joint mobility within current limitations',
        'Patient will maintain skin integrity without pressure ulcer development',
        'Patient will remain free from thromboembolic complications',
        'Patient will maintain clear lungs and effective breathing patterns'
      ],
      interventions: [
        'Perform range of motion exercises (active/passive) every 4 hours',
        'Reposition patient every 2 hours using turning schedule',
        'Use pressure-relieving devices (mattresses, cushions, heel protectors)',
        'Assess skin integrity every shift, especially over bony prominences',
        'Encourage deep breathing and coughing exercises every 2 hours while awake',
        'Apply sequential compression devices or elastic stockings as ordered',
        'Maintain adequate hydration and nutrition for tissue health',
        'Progress mobility as tolerated: sitting, standing, walking'
      ],
      evaluation: [
        'Patient maintains joint mobility and muscle strength without contractures',
        'Patient\'s skin remains intact without pressure ulcers or breakdown',
        'No signs of deep vein thrombosis or pulmonary embolism',
        'Patient maintains clear lung sounds and effective cough'
      ]
    }
  },
  {
    id: 'risk-for-injury',
    specialtyId: 'basic-general-care',
    title: 'Risk for Injury and Patient Safety',
    description: 'Comprehensive patient safety and injury prevention care plan',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Risk for injury related to environmental hazards, cognitive impairment, or physical limitations',
        'Risk for falls related to mobility issues, medications, or environmental factors',
        'Risk for medication errors related to polypharmacy or confusion',
        'Risk for burns or cuts related to decreased sensation or cognitive impairment'
      ],
      goals: [
        'Patient will remain free from preventable injuries during care',
        'Patient will demonstrate understanding of safety measures',
        'Patient will utilize safety equipment and precautions appropriately',
        'Environmental hazards will be identified and minimized'
      ],
      interventions: [
        'Conduct comprehensive safety assessment of patient and environment',
        'Implement fall prevention measures: non-slip socks, bed alarms, clear pathways',
        'Ensure proper lighting and remove trip hazards from environment',
        'Review medications for those that increase injury risk',
        'Provide patient identification band and allergy alerts',
        'Educate patient and family about safety measures and precautions',
        'Use protective equipment as needed: helmets, padding, restraints (last resort)',
        'Maintain equipment in good working order and ensure proper use'
      ],
      evaluation: [
        'No preventable injuries occurred during care period',
        'Patient and family demonstrate understanding of safety measures',
        'Patient consistently uses safety equipment when appropriate',
        'Environmental hazards have been identified and addressed'
      ]
    }
  },
  {
    id: 'self-care-adls',
    specialtyId: 'basic-general-care',
    title: 'Self-Care and Activities of Daily Living (ADLs)',
    description: 'Care plan for promoting independence in daily activities',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Self-care deficit: bathing related to physical limitations or cognitive impairment',
        'Self-care deficit: dressing related to decreased mobility or coordination',
        'Self-care deficit: toileting related to physical or cognitive barriers',
        'Self-care deficit: feeding related to motor impairment or swallowing difficulties'
      ],
      goals: [
        'Patient will perform self-care activities with appropriate level of assistance',
        'Patient will demonstrate improved independence in daily activities',
        'Patient will maintain dignity and autonomy in personal care',
        'Patient will utilize adaptive equipment effectively to promote independence'
      ],
      interventions: [
        'Assess current level of function in all ADL categories',
        'Encourage independence while providing assistance as needed',
        'Break down complex tasks into manageable steps',
        'Provide adaptive equipment: reachers, button hooks, shower chairs',
        'Allow adequate time for patient to complete activities',
        'Maintain patient privacy and dignity during personal care',
        'Collaborate with occupational therapy for ADL training',
        'Educate family on ways to support patient independence'
      ],
      evaluation: [
        'Patient demonstrates maximum possible independence in self-care activities',
        'Patient shows improved confidence and satisfaction with personal care',
        'Patient maintains dignity and personal preferences in care routines',
        'Patient effectively uses adaptive equipment to enhance independence'
      ]
    }
  },
  {
    id: 'systemic-lupus',
    specialtyId: 'basic-general-care',
    title: 'Systemic Lupus Erythematosus',
    description: 'Nursing care plan for lupus management',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Chronic pain related to joint inflammation and systemic effects of lupus',
        'Fatigue related to chronic inflammatory process and medication effects',
        'Risk for infection related to immunosuppressive medications',
        'Disturbed body image related to skin rashes and physical changes'
      ],
      goals: [
        'Patient will report manageable pain levels and improved joint function',
        'Patient will demonstrate energy conservation techniques and improved activity tolerance',
        'Patient will remain free from opportunistic infections',
        'Patient will express acceptance of body changes and maintain positive self-image'
      ],
      interventions: [
        'Assess pain levels and joint function using standardized tools',
        'Administer prescribed medications: NSAIDs, corticosteroids, immunosuppressants',
        'Monitor for medication side effects and therapeutic response',
        'Encourage gentle exercise and physical therapy as tolerated',
        'Provide sun protection education and recommend protective clothing',
        'Monitor for signs of infection and implement protective measures',
        'Provide emotional support and connect with lupus support groups',
        'Educate about disease process, flare triggers, and self-management'
      ],
      evaluation: [
        'Patient reports reduced pain and improved joint mobility',
        'Patient demonstrates effective energy management and pacing strategies',
        'No infections occurred; patient shows good infection prevention practices',
        'Patient verbalizes improved body image and coping with appearance changes'
      ]
    }
  },
  {
    id: 'total-parenteral-nutrition',
    specialtyId: 'basic-general-care',
    title: 'Total Parenteral Nutrition',
    description: 'Care plan for TPN administration and monitoring',
    category: 'Basic & General Care',
    pgie: {
      problems: [
        'Risk for infection related to central venous access and concentrated nutrition solution',
        'Risk for metabolic complications related to high glucose and electrolyte content',
        'Risk for fluid volume imbalance related to osmotic effects of TPN',
        'Imbalanced nutrition: less than body requirements related to inability to tolerate enteral feeding'
      ],
      goals: [
        'Patient will remain free from catheter-related infections',
        'Patient will maintain stable blood glucose and electrolyte levels',
        'Patient will maintain appropriate fluid balance without overload',
        'Patient will achieve and maintain adequate nutritional status'
      ],
      interventions: [
        'Maintain strict aseptic technique during TPN administration and line care',
        'Monitor blood glucose levels every 6 hours initially, then per protocol',
        'Check electrolytes, liver function, and nutritional parameters regularly',
        'Monitor daily weights and intake/output to assess fluid balance',
        'Inspect central line insertion site for signs of infection or complications',
        'Use dedicated line for TPN administration; avoid interruptions',
        'Collaborate with pharmacist and dietitian for TPN formulation adjustments',
        'Educate patient and family about TPN therapy and line care'
      ],
      evaluation: [
        'No signs of catheter-related bloodstream infection or line complications',
        'Blood glucose and electrolytes remain within normal ranges',
        'Patient maintains appropriate fluid balance with stable weight',
        'Nutritional parameters improve: albumin, prealbumin, weight gain'
      ]
    }
  },

  // Cardiac Care
  {
    id: 'angina-pectoris',
    specialtyId: 'cardiac-care',
    title: 'Angina Pectoris (Coronary Artery Disease)',
    description: 'Nursing care plan for angina and coronary artery disease',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Acute pain related to myocardial ischemia and decreased oxygen supply',
        'Activity intolerance related to decreased cardiac output and chest pain',
        'Anxiety related to chest pain and fear of myocardial infarction',
        'Deficient knowledge related to disease process and lifestyle modifications'
      ],
      goals: [
        'Patient will report absence or reduction of chest pain episodes',
        'Patient will demonstrate improved activity tolerance without chest pain',
        'Patient will verbalize reduced anxiety and effective coping strategies',
        'Patient will demonstrate understanding of angina management and prevention'
      ],
      interventions: [
        'Assess chest pain using PQRST method and 0-10 pain scale',
        'Administer prescribed nitroglycerin and monitor response',
        'Monitor vital signs and cardiac rhythm during pain episodes',
        'Encourage rest during chest pain episodes and provide oxygen as ordered',
        'Educate patient on recognizing angina triggers and warning signs',
        'Provide instruction on proper use of sublingual nitroglycerin',
        'Collaborate with dietitian for cardiac diet education and meal planning',
        'Encourage gradual increase in activity as tolerated and prescribed'
      ],
      evaluation: [
        'Patient experiences fewer episodes of chest pain',
        'Patient participates in activities without triggering angina',
        'Patient demonstrates reduced anxiety about chest pain episodes',
        'Patient verbalizes understanding of medication use and lifestyle changes'
      ]
    }
  },
  {
    id: 'cardiac-arrhythmia',
    specialtyId: 'cardiac-care',
    title: 'Cardiac Arrhythmia (Digitalis Toxicity)',
    description: 'Care plan for cardiac rhythm disorders',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Decreased cardiac output related to altered heart rate and rhythm',
        'Risk for injury related to dizziness and syncope from arrhythmias',
        'Anxiety related to palpitations and irregular heartbeat sensations',
        'Risk for digitalis toxicity related to medication therapy'
      ],
      goals: [
        'Patient will maintain stable cardiac rhythm and adequate cardiac output',
        'Patient will remain free from injury related to arrhythmia symptoms',
        'Patient will verbalize reduced anxiety about heart rhythm irregularities',
        'Patient will maintain therapeutic digitalis levels without toxicity'
      ],
      interventions: [
        'Monitor cardiac rhythm continuously and document any changes',
        'Assess vital signs every 4 hours and with any rhythm changes',
        'Monitor serum electrolytes, especially potassium and magnesium levels',
        'Administer prescribed antiarrhythmic medications as ordered',
        'Monitor digitalis levels and assess for signs of toxicity',
        'Teach patient to monitor pulse rate and rhythm daily',
        'Educate patient on when to seek immediate medical attention',
        'Implement fall precautions due to risk of dizziness and syncope'
      ],
      evaluation: [
        'Patient maintains stable heart rhythm within normal parameters',
        'No falls or injuries occurred related to arrhythmia symptoms',
        'Patient reports decreased anxiety about heart rhythm irregularities',
        'Digitalis levels remain within therapeutic range without signs of toxicity'
      ]
    }
  },
  {
    id: 'cardiac-catheterization',
    specialtyId: 'cardiac-care',
    title: 'Cardiac Catheterization',
    description: 'Nursing care plan for cardiac catheterization procedures',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Risk for bleeding related to arterial puncture site and anticoagulation',
        'Risk for injury related to contrast media and procedural complications',
        'Anxiety related to invasive procedure and potential findings',
        'Risk for impaired tissue perfusion related to thrombosis or embolism'
      ],
      goals: [
        'Patient will remain free from bleeding complications at puncture site',
        'Patient will experience no adverse reactions to contrast media or procedure',
        'Patient will verbalize reduced anxiety and understanding of procedure',
        'Patient will maintain adequate tissue perfusion without complications'
      ],
      interventions: [
        'Monitor puncture site for bleeding, hematoma, or swelling every 15 minutes x4, then every hour',
        'Maintain bed rest with leg straight for prescribed time (usually 4-6 hours)',
        'Monitor vital signs every 15 minutes x4, then every hour for 4 hours',
        'Assess distal pulses, color, and sensation of affected extremity',
        'Encourage fluid intake to promote contrast media elimination',
        'Monitor for allergic reactions to contrast media',
        'Provide pre-procedure education about what to expect',
        'Administer prescribed medications for comfort and anxiety'
      ],
      evaluation: [
        'Puncture site remains intact without bleeding or hematoma formation',
        'Patient experienced no adverse reactions to procedure or contrast',
        'Patient verbalized understanding and demonstrated reduced anxiety',
        'Distal circulation remains intact with palpable pulses and normal sensation'
      ]
    }
  },
  {
    id: 'cardiogenic-shock',
    specialtyId: 'cardiac-care',
    title: 'Cardiogenic Shock',
    description: 'Emergency care plan for cardiogenic shock',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Decreased cardiac output related to severe left ventricular dysfunction',
        'Impaired tissue perfusion related to inadequate cardiac output and hypotension',
        'Risk for acute kidney injury related to decreased renal perfusion',
        'Anxiety related to life-threatening condition and intensive monitoring'
      ],
      goals: [
        'Patient will demonstrate improved cardiac output and hemodynamic stability',
        'Patient will maintain adequate tissue perfusion to vital organs',
        'Patient will maintain normal kidney function without acute injury',
        'Patient will demonstrate reduced anxiety with supportive interventions'
      ],
      interventions: [
        'Monitor hemodynamic parameters continuously: blood pressure, heart rate, CVP',
        'Administer prescribed inotropic medications (dobutamine, dopamine) via pump',
        'Monitor arterial blood gases and oxygen saturation continuously',
        'Assess urine output hourly and monitor for signs of kidney dysfunction',
        'Administer oxygen therapy to maintain saturation >95%',
        'Monitor for signs of fluid overload while maintaining adequate preload',
        'Provide emotional support and explain procedures to reduce anxiety',
        'Collaborate with cardiology for potential mechanical support devices'
      ],
      evaluation: [
        'Hemodynamic parameters stabilized with improved cardiac output',
        'Tissue perfusion adequate as evidenced by warm extremities and mental clarity',
        'Urine output maintained >0.5 mL/kg/hr with stable kidney function',
        'Patient and family demonstrate reduced anxiety with supportive care'
      ]
    }
  },
  {
    id: 'congenital-heart-disease',
    specialtyId: 'cardiac-care',
    title: 'Congenital Heart Disease',
    description: 'Nursing care plan for congenital heart conditions',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Decreased cardiac output related to structural heart defects',
        'Activity intolerance related to decreased oxygen delivery and fatigue',
        'Risk for infection related to compromised cardiac function and potential for endocarditis',
        'Delayed growth and development related to chronic hypoxemia and increased metabolic demands'
      ],
      goals: [
        'Patient will maintain optimal cardiac output within limitations of heart defect',
        'Patient will demonstrate activity tolerance appropriate for developmental level',
        'Patient will remain free from infection, particularly endocarditis',
        'Patient will achieve developmental milestones appropriate for age and condition'
      ],
      interventions: [
        'Monitor vital signs and oxygen saturation every 4 hours and PRN',
        'Assess for signs of congestive heart failure: edema, weight gain, respiratory distress',
        'Plan activities to allow for rest periods and avoid overexertion',
        'Monitor growth parameters: weight, height, head circumference',
        'Educate family on signs of infection and when to seek medical care',
        'Coordinate with pediatric cardiology for ongoing management',
        'Provide developmentally appropriate activities within cardiac limitations',
        'Administer prescribed medications: diuretics, ACE inhibitors, digoxin'
      ],
      evaluation: [
        'Patient maintains stable cardiac status without signs of failure',
        'Patient participates in age-appropriate activities without excessive fatigue',
        'No infections occurred; family demonstrates infection prevention knowledge',
        'Patient shows appropriate growth and development for condition'
      ]
    }
  },
  {
    id: 'decreased-cardiac-output',
    specialtyId: 'cardiac-care',
    title: 'Decreased Cardiac Output & Cardiac Support',
    description: 'Care plan for decreased cardiac output and hemodynamic support',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Decreased cardiac output related to altered contractility, preload, or afterload',
        'Activity intolerance related to inadequate cardiac output and tissue perfusion',
        'Risk for cardiogenic shock related to severe decrease in cardiac function',
        'Anxiety related to decreased functional capacity and lifestyle limitations'
      ],
      goals: [
        'Patient will demonstrate improved cardiac output as evidenced by stable vital signs',
        'Patient will tolerate progressive activity without excessive fatigue or dyspnea',
        'Patient will remain hemodynamically stable without signs of shock',
        'Patient will verbalize understanding of condition and demonstrate effective coping'
      ],
      interventions: [
        'Monitor hemodynamic parameters: blood pressure, heart rate, rhythm continuously',
        'Assess for signs of decreased cardiac output: cool extremities, decreased urine output',
        'Administer prescribed medications: inotropes, vasodilators, diuretics',
        'Monitor daily weights and intake/output to assess fluid balance',
        'Position patient in semi-Fowler\'s position to reduce cardiac workload',
        'Plan activities with rest periods to prevent excessive cardiac demand',
        'Monitor laboratory values: BUN, creatinine, electrolytes, BNP',
        'Provide patient education on energy conservation techniques'
      ],
      evaluation: [
        'Cardiac output improved with blood pressure and heart rate within normal limits',
        'Patient demonstrates improved exercise tolerance with stable vital signs',
        'Patient remains hemodynamically stable without signs of deterioration',
        'Patient verbalizes understanding and demonstrates positive coping strategies'
      ]
    }
  },
  {
    id: 'heart-failure',
    specialtyId: 'cardiac-care',
    title: 'Heart Failure',
    description: 'Evidence-based nursing care plan for heart failure management',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Decreased cardiac output related to altered contractility and preload',
        'Excess fluid volume related to compromised regulatory mechanisms',
        'Activity intolerance related to decreased cardiac output and fatigue',
        'Anxiety related to life-threatening illness and lifestyle changes'
      ],
      goals: [
        'Patient will maintain adequate cardiac output as evidenced by stable vital signs',
        'Patient will achieve fluid balance with absence of edema and clear lung sounds',
        'Patient will tolerate increased activity levels without excessive fatigue',
        'Patient will demonstrate reduced anxiety and effective coping strategies'
      ],
      interventions: [
        'Monitor vital signs, cardiac rhythm, and hemodynamic parameters every 4 hours',
        'Assess for signs of fluid retention: weight gain, edema, jugular vein distention',
        'Administer prescribed medications: ACE inhibitors, diuretics, beta-blockers',
        'Monitor daily weights at same time with same scale',
        'Restrict sodium intake as prescribed (usually 2-3g daily)',
        'Monitor intake and output every 8 hours',
        'Encourage gradual increase in activity as tolerated',
        'Provide patient education on medication compliance and lifestyle modifications'
      ],
      evaluation: [
        'Cardiac output improved with stable blood pressure and heart rate',
        'Fluid balance achieved with absence of peripheral edema and clear lungs',
        'Patient tolerates activities of daily living without excessive shortness of breath',
        'Patient verbalizes understanding of condition and treatment plan'
      ]
    }
  },
  {
    id: 'hypertension',
    specialtyId: 'cardiac-care',
    title: 'Hypertension',
    description: 'Nursing care plan for hypertension management',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Risk for decreased cardiac output related to increased afterload',
        'Ineffective health maintenance related to lack of knowledge about condition',
        'Risk for stroke related to uncontrolled blood pressure',
        'Non-compliance with therapeutic regimen related to asymptomatic nature'
      ],
      goals: [
        'Patient will maintain blood pressure within target range (<140/90 mmHg)',
        'Patient will demonstrate knowledge of hypertension management',
        'Patient will remain free from cardiovascular complications',
        'Patient will adhere to prescribed medication and lifestyle modifications'
      ],
      interventions: [
        'Monitor blood pressure using proper technique every 4 hours',
        'Administer antihypertensive medications as prescribed',
        'Educate patient on DASH diet and sodium restriction (2g daily)',
        'Encourage regular physical activity as tolerated and prescribed',
        'Monitor for signs of target organ damage (headache, vision changes)',
        'Assess medication compliance and potential side effects',
        'Provide stress management techniques and lifestyle counseling',
        'Schedule regular follow-up appointments for monitoring'
      ],
      evaluation: [
        'Blood pressure consistently within target range',
        'Patient verbalizes understanding of hypertension and its management',
        'No signs of cardiovascular complications present',
        'Patient demonstrates adherence to medication and lifestyle modifications'
      ]
    }
  },
  {
    id: 'hypovolemic-shock',
    specialtyId: 'cardiac-care',
    title: 'Hypovolemic Shock',
    description: 'Emergency care plan for hypovolemic shock',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Deficient fluid volume related to hemorrhage, dehydration, or fluid losses',
        'Decreased cardiac output related to reduced venous return and preload',
        'Risk for organ dysfunction related to inadequate tissue perfusion',
        'Anxiety related to life-threatening condition and emergency interventions'
      ],
      goals: [
        'Patient will achieve adequate fluid volume and hemodynamic stability',
        'Patient will maintain adequate cardiac output and tissue perfusion',
        'Patient will maintain organ function without signs of failure',
        'Patient will demonstrate reduced anxiety with supportive care'
      ],
      interventions: [
        'Monitor vital signs continuously and assess for signs of shock',
        'Establish large-bore IV access and administer fluid resuscitation as ordered',
        'Monitor hemoglobin, hematocrit, and electrolyte levels',
        'Assess urine output hourly and maintain >0.5 mL/kg/hr',
        'Monitor for signs of bleeding and control source if identified',
        'Administer blood products as prescribed and monitor for reactions',
        'Position patient in Trendelenburg or supine position as appropriate',
        'Provide emotional support and explain procedures to patient and family'
      ],
      evaluation: [
        'Patient achieved fluid balance with stable blood pressure and heart rate',
        'Cardiac output improved with adequate tissue perfusion',
        'Organ function maintained with normal urine output and mental status',
        'Patient and family verbalize reduced anxiety and understanding of treatment'
      ]
    }
  },
  {
    id: 'impaired-tissue-perfusion',
    specialtyId: 'cardiac-care',
    title: 'Impaired Tissue Perfusion & Ischemia',
    description: 'Care plan for tissue perfusion and ischemia management',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Ineffective tissue perfusion related to decreased cardiac output or vascular compromise',
        'Risk for injury related to decreased sensation and circulation',
        'Acute pain related to tissue ischemia and hypoxia',
        'Risk for infection related to compromised tissue integrity'
      ],
      goals: [
        'Patient will demonstrate improved tissue perfusion to all extremities',
        'Patient will remain free from injury related to circulation impairment',
        'Patient will report pain relief and improved comfort',
        'Patient will maintain tissue integrity without signs of infection'
      ],
      interventions: [
        'Assess peripheral circulation: pulses, color, temperature, capillary refill',
        'Monitor for signs of tissue ischemia: pallor, cyanosis, coolness, numbness',
        'Position extremities to promote circulation and avoid pressure points',
        'Administer prescribed medications to improve circulation',
        'Encourage gentle range of motion exercises as tolerated',
        'Protect affected areas from injury and temperature extremes',
        'Monitor for signs of tissue breakdown or infection',
        'Educate patient on proper foot care and circulation enhancement techniques'
      ],
      evaluation: [
        'Peripheral circulation improved with palpable pulses and normal color',
        'No injuries occurred related to decreased circulation',
        'Patient reports decreased pain and improved comfort levels',
        'Tissue integrity maintained without signs of breakdown or infection'
      ]
    }
  },
  {
    id: 'myocardial-infarction',
    specialtyId: 'cardiac-care',
    title: 'Myocardial Infarction',
    description: 'Comprehensive nursing care plan for heart attack patients',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Acute pain related to myocardial tissue ischemia and necrosis',
        'Decreased cardiac output related to loss of myocardial contractility',
        'Activity intolerance related to decreased cardiac reserve and fear of exertion',
        'Anxiety related to fear of death and lifestyle changes'
      ],
      goals: [
        'Patient will report chest pain relief within 30 minutes of intervention',
        'Patient will maintain adequate cardiac output and hemodynamic stability',
        'Patient will participate in progressive cardiac rehabilitation as tolerated',
        'Patient will demonstrate reduced anxiety and understanding of condition'
      ],
      interventions: [
        'Monitor cardiac rhythm continuously and assess for arrhythmias',
        'Administer prescribed medications: thrombolytics, antiplatelet agents, beta-blockers',
        'Assess chest pain using 0-10 scale and administer nitroglycerin as ordered',
        'Monitor vital signs every 15 minutes during acute phase',
        'Encourage bed rest for first 12-24 hours then progressive activity',
        'Monitor for complications: cardiogenic shock, heart failure, arrhythmias',
        'Provide emotional support and crisis intervention as needed',
        'Initiate cardiac rehabilitation education and lifestyle modification counseling'
      ],
      evaluation: [
        'Patient achieved pain relief with stable cardiac rhythm',
        'Hemodynamic parameters remained stable without signs of complications',
        'Patient tolerated progressive activity without chest pain or dyspnea',
        'Patient verbalized reduced anxiety and commitment to lifestyle changes'
      ]
    }
  },
  {
    id: 'pacemaker-therapy',
    specialtyId: 'cardiac-care',
    title: 'Pacemaker Therapy',
    description: 'Nursing care plan for pacemaker insertion and management',
    category: 'Cardiac Care',
    pgie: {
      problems: [
        'Risk for infection related to implanted device and surgical incision',
        'Risk for decreased cardiac output related to pacemaker malfunction',
        'Deficient knowledge related to pacemaker function and lifestyle modifications',
        'Anxiety related to dependence on electronic device for cardiac function'
      ],
      goals: [
        'Patient will remain free from infection at pacemaker insertion site',
        'Patient will maintain adequate cardiac output with proper pacemaker function',
        'Patient will demonstrate understanding of pacemaker care and precautions',
        'Patient will verbalize reduced anxiety about living with pacemaker'
      ],
      interventions: [
        'Monitor insertion site for signs of infection: redness, swelling, drainage',
        'Assess pacemaker function by monitoring heart rate and rhythm',
        'Restrict arm movement on affected side for 24-48 hours post-insertion',
        'Monitor for signs of pneumothorax: chest pain, dyspnea, decreased breath sounds',
        'Educate patient on pacemaker precautions and electromagnetic interference',
        'Teach patient to monitor pulse daily and when to contact healthcare provider',
        'Provide written instructions on activity restrictions and follow-up care',
        'Discuss emotional concerns and lifestyle adjustments with patient and family'
      ],
      evaluation: [
        'Insertion site healed without signs of infection or complications',
        'Pacemaker functioning properly with appropriate heart rate and rhythm',
        'Patient demonstrates understanding of pacemaker care and safety precautions',
        'Patient expresses confidence and reduced anxiety about living with pacemaker'
      ]
    }
  },

  // Endocrine & Metabolic Care
  {
    id: 'addisons-disease',
    specialtyId: 'endocrine-metabolic',
    title: 'Addison\'s Disease',
    description: 'Nursing care plan for adrenal insufficiency',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for adrenal crisis related to insufficient cortisol production',
        'Activity intolerance related to fatigue and muscle weakness',
        'Risk for electrolyte imbalance related to aldosterone deficiency',
        'Deficient knowledge related to disease management and medication regimen'
      ],
      goals: [
        'Patient will remain free from adrenal crisis during hospitalization',
        'Patient will demonstrate improved activity tolerance within 72 hours',
        'Patient will maintain electrolyte balance within normal limits',
        'Patient will verbalize understanding of disease management and medication schedule'
      ],
      interventions: [
        'Monitor vital signs frequently, especially blood pressure and heart rate',
        'Assess for signs of adrenal crisis (hypotension, shock, altered mental status)',
        'Administer corticosteroid replacement therapy as prescribed',
        'Monitor serum electrolytes, especially sodium and potassium levels',
        'Educate patient about lifelong need for hormone replacement therapy',
        'Teach patient to recognize signs of adrenal insufficiency',
        'Provide information about medical alert identification',
        'Assist with gradual increase in activity level as tolerated'
      ],
      evaluation: [
        'Vital signs remain stable without signs of adrenal crisis',
        'Patient participates in activities without excessive fatigue',
        'Electrolyte levels remain within normal ranges',
        'Patient demonstrates understanding of medication regimen and disease management'
      ]
    }
  },
  {
    id: 'cushings-disease',
    specialtyId: 'endocrine-metabolic',
    title: 'Cushing\'s Disease',
    description: 'Care plan for hypercortisolism management',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for impaired skin integrity related to excess cortisol and fragile skin',
        'Disturbed body image related to physical changes from hypercortisolism',
        'Risk for infection related to immunosuppression from excess cortisol',
        'Risk for injury related to muscle weakness and osteoporosis'
      ],
      goals: [
        'Patient will maintain skin integrity without breakdown during hospitalization',
        'Patient will verbalize acceptance of body changes and coping strategies',
        'Patient will remain free from infection during treatment period',
        'Patient will not sustain injury related to weakness or falls'
      ],
      interventions: [
        'Assess skin daily for breakdown, bruising, or slow-healing wounds',
        'Use gentle handling and positioning techniques to prevent skin trauma',
        'Monitor for signs of infection (fever, elevated WBC, wound changes)',
        'Implement fall prevention measures due to muscle weakness',
        'Provide emotional support and counseling for body image concerns',
        'Monitor blood glucose levels as cortisol affects glucose metabolism',
        'Educate patient about disease process and treatment options',
        'Coordinate care with endocrinologist for cortisol level management'
      ],
      evaluation: [
        'Skin remains intact without new breakdown or injury',
        'Patient demonstrates positive coping mechanisms for body image changes',
        'No signs of infection present throughout care period',
        'Patient remains injury-free with stable mobility'
      ]
    }
  },
  {
    id: 'diabetes-mellitus',
    specialtyId: 'endocrine-metabolic',
    title: 'Diabetes Mellitus (Type 1, Type 2)',
    description: 'Comprehensive diabetes management nursing care plan',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for unstable blood glucose levels related to inadequate insulin or insulin resistance',
        'Deficient knowledge related to diabetes management and self-care',
        'Risk for infection related to elevated glucose levels',
        'Risk for chronic complications related to poor glycemic control'
      ],
      goals: [
        'Patient will maintain blood glucose levels within target range (80-130 mg/dL pre-meal)',
        'Patient will demonstrate proper diabetes self-management skills',
        'Patient will remain free from acute and chronic complications',
        'Patient will adhere to prescribed treatment regimen and lifestyle modifications'
      ],
      interventions: [
        'Monitor blood glucose levels before meals and at bedtime',
        'Administer insulin or oral hypoglycemic agents as prescribed',
        'Provide diabetes education on diet, exercise, medication, and monitoring',
        'Assess feet daily for signs of injury or infection',
        'Monitor for signs of hypoglycemia and hyperglycemia',
        'Coordinate with dietitian for meal planning and carbohydrate counting',
        'Encourage regular physical activity as prescribed by healthcare provider',
        'Monitor HbA1c levels and other diabetes-related laboratory values'
      ],
      evaluation: [
        'Blood glucose levels consistently within target range',
        'Patient demonstrates competency in diabetes self-care skills',
        'No signs of acute complications (DKA, hypoglycemia) present',
        'Patient verbalizes understanding of diabetes management and prevention strategies'
      ]
    }
  },
  {
    id: 'diabetic-ketoacidosis',
    specialtyId: 'endocrine-metabolic',
    title: 'Diabetic Ketoacidosis (DKA) and Hyperglycemic Hyperosmolar Nonketotic Syndrome (HHNS)',
    description: 'Emergency care plan for diabetic complications',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for fluid volume deficit related to osmotic diuresis and dehydration',
        'Risk for electrolyte imbalance related to insulin deficiency and metabolic acidosis',
        'Altered mental status related to severe hyperglycemia and ketosis',
        'Risk for cerebral edema related to rapid correction of hyperglycemia'
      ],
      goals: [
        'Patient will achieve fluid balance and normal hydration status within 24 hours',
        'Patient will maintain electrolyte levels within normal limits',
        'Patient will demonstrate improved mental status and orientation',
        'Patient will not develop complications from treatment (cerebral edema, hypoglycemia)'
      ],
      interventions: [
        'Monitor blood glucose levels hourly initially, then every 2-4 hours',
        'Administer IV fluids as prescribed (normal saline, then 0.45% saline)',
        'Give continuous IV insulin infusion as prescribed',
        'Monitor serum electrolytes (K+, Na+, Cl-, CO2) every 2-4 hours',
        'Assess neurological status and level of consciousness frequently',
        'Monitor arterial blood gases for acid-base balance',
        'Measure urine output and specific gravity hourly',
        'Educate patient/family about DKA prevention and early recognition'
      ],
      evaluation: [
        'Blood glucose levels gradually decrease to target range (150-250 mg/dL initially)',
        'Electrolyte levels normalize and remain stable',
        'Patient is alert, oriented, and mentally clear',
        'No signs of cerebral edema or other treatment complications'
      ]
    }
  },
  {
    id: 'eating-disorders',
    specialtyId: 'endocrine-metabolic',
    title: 'Eating Disorders: Anorexia & Bulimia Nervosa',
    description: 'Nursing care plan for eating disorders',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Imbalanced nutrition: less than body requirements related to restrictive eating patterns',
        'Disturbed body image related to unrealistic perception of body weight and shape',
        'Risk for electrolyte imbalance related to purging behaviors and malnutrition',
        'Ineffective coping related to underlying psychological issues and food-related anxiety'
      ],
      goals: [
        'Patient will achieve gradual weight gain of 1-2 pounds per week if underweight',
        'Patient will verbalize realistic body image and self-perception',
        'Patient will maintain electrolyte balance within normal limits',
        'Patient will demonstrate healthy coping mechanisms for stress and anxiety'
      ],
      interventions: [
        'Monitor daily weights and nutritional intake with accurate documentation',
        'Provide structured meal support and supervision during eating times',
        'Monitor for signs of purging behaviors and implement bathroom supervision',
        'Assess electrolyte levels and vital signs regularly',
        'Provide therapeutic communication and emotional support',
        'Collaborate with mental health professionals and dietitians',
        'Educate patient and family about healthy nutrition and eating patterns',
        'Implement behavioral interventions and meal planning strategies'
      ],
      evaluation: [
        'Patient demonstrates steady weight gain or weight stabilization',
        'Patient expresses more realistic body image perceptions',
        'Electrolyte levels remain stable and within normal ranges',
        'Patient uses healthy coping strategies and seeks appropriate support'
      ]
    }
  },
  {
    id: 'fluid-volume-deficit',
    specialtyId: 'endocrine-metabolic',
    title: 'Fluid Volume Deficit (Dehydration & Hypovolemia)',
    description: 'Care plan for dehydration and hypovolemia',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Fluid volume deficit related to inadequate fluid intake or excessive fluid loss',
        'Risk for electrolyte imbalance related to dehydration and fluid shifts',
        'Risk for impaired tissue perfusion related to decreased circulating volume',
        'Risk for acute kidney injury related to poor renal perfusion'
      ],
      goals: [
        'Patient will achieve adequate hydration status within 24-48 hours',
        'Patient will maintain electrolyte balance within normal limits',
        'Patient will demonstrate adequate tissue perfusion and stable vital signs',
        'Patient will maintain normal kidney function and urine output'
      ],
      interventions: [
        'Monitor intake and output accurately every hour initially',
        'Assess vital signs frequently, noting orthostatic changes',
        'Administer IV fluids as prescribed based on fluid deficit calculations',
        'Monitor serum electrolytes, BUN, and creatinine levels',
        'Assess skin turgor, mucous membranes, and capillary refill',
        'Weigh patient daily at same time with same scale',
        'Encourage oral fluid intake as tolerated and appropriate',
        'Monitor urine specific gravity and color'
      ],
      evaluation: [
        'Patient demonstrates improved hydration status with normal skin turgor',
        'Electrolyte levels return to and remain within normal ranges',
        'Vital signs are stable without orthostatic changes',
        'Urine output is adequate (>0.5 mL/kg/hr) with normal kidney function'
      ]
    }
  },
  {
    id: 'fluid-volume-excess',
    specialtyId: 'endocrine-metabolic',
    title: 'Fluid Volume Excess (Hypervolemia)',
    description: 'Care plan for fluid overload and hypervolemia',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Excess fluid volume related to compromised regulatory mechanisms or excess intake',
        'Impaired gas exchange related to pulmonary edema and fluid accumulation',
        'Risk for impaired skin integrity related to edema and poor circulation',
        'Activity intolerance related to dyspnea and fluid retention'
      ],
      goals: [
        'Patient will achieve fluid balance with resolution of edema within 72 hours',
        'Patient will maintain adequate oxygenation and clear lung sounds',
        'Patient will maintain skin integrity without breakdown',
        'Patient will demonstrate improved activity tolerance and reduced dyspnea'
      ],
      interventions: [
        'Monitor daily weights at same time using same scale',
        'Maintain strict intake and output monitoring',
        'Administer diuretics as prescribed and monitor response',
        'Assess lung sounds and respiratory status every 4 hours',
        'Monitor for peripheral and sacral edema every shift',
        'Restrict fluid and sodium intake as prescribed',
        'Position patient with head of bed elevated to ease breathing',
        'Monitor electrolyte levels, especially potassium with diuretic use'
      ],
      evaluation: [
        'Patient demonstrates weight loss and reduction in edema',
        'Lung sounds are clear with improved oxygenation',
        'Skin remains intact without pressure areas or breakdown',
        'Patient reports decreased dyspnea and improved exercise tolerance'
      ]
    }
  },
  {
    id: 'gestational-diabetes',
    specialtyId: 'endocrine-metabolic',
    title: 'Gestational Diabetes Mellitus',
    description: 'Diabetes management during pregnancy',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for unstable blood glucose levels related to pregnancy-induced insulin resistance',
        'Risk for fetal injury related to maternal hyperglycemia',
        'Deficient knowledge related to gestational diabetes management',
        'Anxiety related to potential complications for mother and baby'
      ],
      goals: [
        'Patient will maintain blood glucose levels within target range for pregnancy',
        'Patient will demonstrate no signs of maternal or fetal complications',
        'Patient will verbalize understanding of diabetes management during pregnancy',
        'Patient will express reduced anxiety about pregnancy outcomes'
      ],
      interventions: [
        'Monitor blood glucose levels as prescribed (fasting and postprandial)',
        'Teach patient proper blood glucose monitoring techniques',
        'Provide education on appropriate diet and carbohydrate counting',
        'Monitor fetal growth and well-being through regular assessments',
        'Administer insulin if prescribed and teach injection technique',
        'Assess for signs of pregnancy complications (preeclampsia, polyhydramnios)',
        'Coordinate care with obstetrician and endocrinologist',
        'Provide emotional support and stress management techniques'
      ],
      evaluation: [
        'Blood glucose levels consistently within recommended pregnancy ranges',
        'Fetal growth and development proceed normally without complications',
        'Patient demonstrates competency in diabetes self-management skills',
        'Patient verbalizes confidence in managing diabetes during pregnancy'
      ]
    }
  },
  {
    id: 'hyperthyroidism',
    specialtyId: 'endocrine-metabolic',
    title: 'Hyperthyroidism',
    description: 'Nursing care plan for overactive thyroid',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for hyperthermia related to increased metabolic rate',
        'Activity intolerance related to fatigue and muscle weakness',
        'Imbalanced nutrition: less than body requirements related to increased metabolism',
        'Anxiety related to excessive thyroid hormone levels'
      ],
      goals: [
        'Patient will maintain normal body temperature within acceptable range',
        'Patient will demonstrate improved activity tolerance without excessive fatigue',
        'Patient will maintain adequate nutritional status and stable weight',
        'Patient will report decreased anxiety and improved emotional stability'
      ],
      interventions: [
        'Monitor vital signs frequently, especially temperature and heart rate',
        'Provide cool environment and lightweight clothing to prevent overheating',
        'Administer antithyroid medications as prescribed',
        'Monitor for signs of thyroid storm (hyperthermia, tachycardia, altered mental status)',
        'Encourage high-calorie, high-protein diet to meet increased metabolic needs',
        'Provide calm, quiet environment to reduce stimulation',
        'Monitor weight daily and assess nutritional intake',
        'Educate patient about medication compliance and follow-up care'
      ],
      evaluation: [
        'Body temperature remains within normal limits',
        'Patient participates in activities without excessive fatigue or weakness',
        'Patient maintains or gains weight with adequate nutritional intake',
        'Patient demonstrates reduced anxiety and improved sleep patterns'
      ]
    }
  },
  {
    id: 'hypothyroidism',
    specialtyId: 'endocrine-metabolic',
    title: 'Hypothyroidism',
    description: 'Care plan for underactive thyroid management',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Activity intolerance related to fatigue and decreased metabolic rate',
        'Risk for hypothermia related to decreased heat production',
        'Constipation related to decreased gastrointestinal motility',
        'Risk for depression related to altered neurotransmitter function'
      ],
      goals: [
        'Patient will demonstrate improved energy levels and activity tolerance',
        'Patient will maintain normal body temperature',
        'Patient will establish regular bowel elimination patterns',
        'Patient will report improved mood and mental clarity'
      ],
      interventions: [
        'Administer thyroid hormone replacement therapy as prescribed',
        'Monitor vital signs and assess for signs of hypothermia',
        'Encourage gradual increase in activity level as tolerated',
        'Provide warm environment and extra blankets as needed',
        'Assess bowel elimination patterns and encourage fiber intake',
        'Monitor TSH levels and other thyroid function tests',
        'Educate patient about lifelong need for thyroid hormone replacement',
        'Assess mental status and mood changes'
      ],
      evaluation: [
        'Patient reports increased energy and improved activity participation',
        'Body temperature remains within normal limits',
        'Patient has regular bowel movements without constipation',
        'Patient demonstrates improved mood and cognitive function'
      ]
    }
  },
  {
    id: 'imbalanced-nutrition',
    specialtyId: 'endocrine-metabolic',
    title: 'Imbalanced Nutrition (Malnutrition)',
    description: 'Nursing care plan for malnutrition and nutritional deficits',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Imbalanced nutrition: less than body requirements related to inadequate intake or absorption',
        'Risk for infection related to compromised immune function from malnutrition',
        'Risk for impaired skin integrity related to poor nutritional status',
        'Activity intolerance related to decreased energy from inadequate nutrition'
      ],
      goals: [
        'Patient will demonstrate progressive weight gain toward ideal body weight',
        'Patient will remain free from infection during hospitalization',
        'Patient will maintain skin integrity without breakdown',
        'Patient will show improved energy levels and activity participation'
      ],
      interventions: [
        'Assess nutritional status using validated screening tools',
        'Monitor daily caloric intake and document food preferences',
        'Weigh patient daily at same time using same scale',
        'Collaborate with dietitian for comprehensive nutrition assessment',
        'Provide nutritional supplements as prescribed',
        'Monitor laboratory values (albumin, prealbumin, transferrin)',
        'Educate patient and family about proper nutrition and meal planning',
        'Assess for factors contributing to poor nutritional intake'
      ],
      evaluation: [
        'Patient demonstrates steady weight gain of 1-2 pounds per week',
        'No signs of infection or compromised immune function present',
        'Skin remains intact with improved color and elasticity',
        'Patient reports increased energy and improved activity tolerance'
      ]
    }
  },
  {
    id: 'obesity-overweight',
    specialtyId: 'endocrine-metabolic',
    title: 'Obesity & Overweight',
    description: 'Care plan for weight management and obesity',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Imbalanced nutrition: more than body requirements related to excessive caloric intake',
        'Activity intolerance related to excessive weight and deconditioning',
        'Risk for complications related to obesity (diabetes, hypertension, sleep apnea)',
        'Chronic low self-esteem related to body image and social stigma'
      ],
      goals: [
        'Patient will achieve gradual weight loss of 1-2 pounds per week',
        'Patient will demonstrate improved activity tolerance and endurance',
        'Patient will maintain normal blood pressure and glucose levels',
        'Patient will verbalize improved self-esteem and body image'
      ],
      interventions: [
        'Assess current eating patterns and identify triggers for overeating',
        'Collaborate with dietitian for individualized meal planning',
        'Encourage gradual increase in physical activity as tolerated',
        'Monitor vital signs and assess for obesity-related complications',
        'Provide education on portion control and healthy food choices',
        'Support patient in setting realistic, achievable weight loss goals',
        'Monitor laboratory values (lipids, glucose, liver function)',
        'Refer to support groups or counseling services as appropriate'
      ],
      evaluation: [
        'Patient demonstrates consistent, gradual weight loss toward target',
        'Patient participates in regular physical activity without excessive fatigue',
        'Blood pressure and glucose levels remain within normal limits',
        'Patient expresses improved confidence and self-acceptance'
      ]
    }
  },
  {
    id: 'unstable-blood-glucose',
    specialtyId: 'endocrine-metabolic',
    title: 'Unstable Blood Glucose Levels (Hyperglycemia & Hypoglycemia)',
    description: 'Care plan for blood glucose fluctuations',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for unstable blood glucose levels related to medication, diet, or illness',
        'Risk for injury related to hypoglycemic episodes',
        'Deficient knowledge related to blood glucose monitoring and management',
        'Risk for acute complications related to severe hyperglycemia or hypoglycemia'
      ],
      goals: [
        'Patient will maintain blood glucose levels within target range',
        'Patient will remain free from injury during glucose fluctuations',
        'Patient will demonstrate competency in blood glucose self-monitoring',
        'Patient will not experience severe hypoglycemic or hyperglycemic episodes'
      ],
      interventions: [
        'Monitor blood glucose levels as prescribed using point-of-care testing',
        'Assess for signs and symptoms of hyperglycemia and hypoglycemia',
        'Administer insulin or glucose as prescribed based on blood glucose levels',
        'Teach patient proper blood glucose monitoring techniques',
        'Educate patient on recognition and treatment of glucose emergencies',
        'Monitor food intake and timing in relation to medications',
        'Coordinate with diabetes educator and endocrinologist',
        'Keep emergency glucose sources readily available'
      ],
      evaluation: [
        'Blood glucose levels consistently within prescribed target range',
        'Patient recognizes symptoms and takes appropriate action for glucose changes',
        'Patient demonstrates accurate blood glucose monitoring technique',
        'No episodes of severe hypoglycemia or hyperglycemia occur'
      ]
    }
  },

  // Acid-Base Imbalances
  {
    id: 'metabolic-acidosis',
    specialtyId: 'endocrine-metabolic',
    title: 'Metabolic Acidosis',
    description: 'Nursing care plan for metabolic acidosis management',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for decreased cardiac output related to acidosis and electrolyte imbalances',
        'Impaired gas exchange related to compensatory hyperventilation',
        'Risk for injury related to altered mental status from acidosis',
        'Risk for electrolyte imbalance related to acid-base disorder'
      ],
      goals: [
        'Patient will maintain adequate cardiac output and stable vital signs',
        'Patient will demonstrate effective breathing patterns and oxygenation',
        'Patient will remain alert and oriented without neurological deficits',
        'Patient will achieve acid-base and electrolyte balance within normal limits'
      ],
      interventions: [
        'Monitor arterial blood gas values and pH levels frequently',
        'Assess respiratory pattern and effort for compensatory changes',
        'Monitor cardiac rhythm and assess for dysrhythmias',
        'Administer sodium bicarbonate as prescribed if pH severely low',
        'Monitor serum electrolytes, especially potassium and calcium',
        'Assess neurological status and level of consciousness',
        'Treat underlying cause of metabolic acidosis as appropriate',
        'Monitor urine output and kidney function'
      ],
      evaluation: [
        'Cardiac output remains adequate with stable blood pressure and pulse',
        'Respiratory compensation is effective with adequate oxygenation',
        'Patient maintains normal mental status and orientation',
        'Arterial blood gas values return to normal ranges'
      ]
    }
  },
  {
    id: 'metabolic-alkalosis',
    specialtyId: 'endocrine-metabolic',
    title: 'Metabolic Alkalosis',
    description: 'Nursing care plan for metabolic alkalosis management',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for decreased cardiac output related to electrolyte imbalances and alkalosis',
        'Risk for seizures related to hypocalcemia and alkalosis',
        'Ineffective breathing pattern related to compensatory hypoventilation',
        'Risk for tetany related to decreased ionized calcium from alkalosis'
      ],
      goals: [
        'Patient will maintain adequate cardiac output and normal heart rhythm',
        'Patient will remain free from seizures during treatment',
        'Patient will maintain effective breathing patterns and adequate oxygenation',
        'Patient will not develop signs of tetany or muscle spasms'
      ],
      interventions: [
        'Monitor arterial blood gas values and serum electrolytes frequently',
        'Assess for signs of hypocalcemia (Chvostek\'s and Trousseau\'s signs)',
        'Monitor cardiac rhythm for dysrhythmias related to electrolyte changes',
        'Administer prescribed medications to correct underlying cause',
        'Assess respiratory rate and pattern for compensation changes',
        'Monitor for signs of tetany or muscle spasms',
        'Replace electrolytes (potassium, chloride) as prescribed',
        'Assess neurological status for changes in consciousness'
      ],
      evaluation: [
        'Cardiac rhythm remains normal with stable blood pressure',
        'No seizure activity or neurological complications occur',
        'Respiratory pattern remains effective with adequate gas exchange',
        'Patient shows no signs of tetany or muscle irritability'
      ]
    }
  },
  {
    id: 'respiratory-acidosis',
    specialtyId: 'endocrine-metabolic',
    title: 'Respiratory Acidosis',
    description: 'Nursing care plan for respiratory acidosis management',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to inadequate ventilation and CO2 retention',
        'Ineffective breathing pattern related to respiratory depression or obstruction',
        'Risk for decreased cardiac output related to hypercapnia and acidosis',
        'Risk for altered mental status related to CO2 narcosis'
      ],
      goals: [
        'Patient will demonstrate improved gas exchange with normal CO2 levels',
        'Patient will maintain effective breathing patterns and adequate ventilation',
        'Patient will maintain stable cardiac output and vital signs',
        'Patient will remain alert and oriented with normal mental status'
      ],
      interventions: [
        'Monitor arterial blood gas values and end-tidal CO2 levels',
        'Assess respiratory rate, pattern, and effort frequently',
        'Position patient to optimize breathing (semi-Fowler\'s or high-Fowler\'s)',
        'Administer oxygen therapy as prescribed while monitoring for CO2 retention',
        'Encourage deep breathing exercises and use of incentive spirometer',
        'Monitor for signs of respiratory distress or failure',
        'Assess neurological status for signs of CO2 narcosis',
        'Prepare for mechanical ventilation if conservative measures fail'
      ],
      evaluation: [
        'Arterial blood gas values show improvement in CO2 levels and pH',
        'Patient demonstrates effective spontaneous breathing patterns',
        'Vital signs remain stable with adequate cardiac output',
        'Patient maintains normal mental status and orientation'
      ]
    }
  },
  {
    id: 'respiratory-alkalosis',
    specialtyId: 'endocrine-metabolic',
    title: 'Respiratory Alkalosis',
    description: 'Nursing care plan for respiratory alkalosis management',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to hyperventilation and anxiety',
        'Risk for tetany related to hypocalcemia from alkalosis',
        'Anxiety related to respiratory distress and fear of suffocation',
        'Risk for injury related to dizziness and lightheadedness'
      ],
      goals: [
        'Patient will demonstrate normal breathing patterns and respiratory rate',
        'Patient will remain free from tetany and muscle spasms',
        'Patient will report reduced anxiety and feeling of control',
        'Patient will not sustain injury from dizziness or fainting'
      ],
      interventions: [
        'Monitor arterial blood gas values and respiratory rate frequently',
        'Teach and encourage slow, controlled breathing techniques',
        'Assess for signs of tetany (Chvostek\'s and Trousseau\'s signs)',
        'Provide calm, reassuring environment to reduce anxiety',
        'Use rebreathing techniques (paper bag) if appropriate and safe',
        'Monitor for signs of dizziness or lightheadedness',
        'Address underlying cause of hyperventilation (pain, anxiety, fever)',
        'Educate patient about breathing control and relaxation techniques'
      ],
      evaluation: [
        'Respiratory rate and pattern return to normal baseline',
        'No signs of tetany or muscle irritability present',
        'Patient demonstrates effective anxiety management techniques',
        'Patient remains safe without injury from symptoms'
      ]
    }
  },

  // Electrolyte Imbalances
  {
    id: 'calcium-imbalances',
    specialtyId: 'endocrine-metabolic',
    title: 'Calcium (Ca) Imbalances: Hypercalcemia and Hypocalcemia',
    description: 'Nursing care plan for calcium imbalances',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for injury related to altered mental status or tetany from calcium imbalance',
        'Risk for dysrhythmias related to electrolyte effects on cardiac conduction',
        'Risk for seizures related to hypocalcemia and neuromuscular irritability',
        'Risk for kidney stones related to hypercalcemia and increased calcium excretion'
      ],
      goals: [
        'Patient will remain free from injury during calcium imbalance correction',
        'Patient will maintain normal cardiac rhythm without dysrhythmias',
        'Patient will not experience seizures or tetany during treatment',
        'Patient will maintain normal kidney function without stone formation'
      ],
      interventions: [
        'Monitor serum calcium levels (total and ionized) frequently',
        'Assess for signs of hypocalcemia (Chvostek\'s and Trousseau\'s signs)',
        'Monitor cardiac rhythm continuously for dysrhythmias',
        'Administer calcium supplements or calcitonin as prescribed',
        'Assess for signs of hypercalcemia (confusion, kidney stones, bone pain)',
        'Monitor for seizure activity and implement seizure precautions',
        'Encourage adequate fluid intake to prevent kidney stones',
        'Educate patient about dietary sources of calcium and vitamin D'
      ],
      evaluation: [
        'Patient remains safe without injury from calcium imbalance effects',
        'Cardiac rhythm remains normal and stable',
        'No seizures or signs of tetany occur during treatment',
        'Kidney function remains normal with adequate urine output'
      ]
    }
  },
  {
    id: 'magnesium-imbalances',
    specialtyId: 'endocrine-metabolic',
    title: 'Magnesium (Mg) Imbalances: Hypermagnesemia and Hypomagnesemia',
    description: 'Nursing care plan for magnesium imbalances',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for cardiac dysrhythmias related to magnesium imbalance effects on heart',
        'Risk for respiratory depression related to hypermagnesemia',
        'Risk for seizures related to hypomagnesemia and neuromuscular irritability',
        'Risk for muscle weakness related to electrolyte effects on neuromuscular function'
      ],
      goals: [
        'Patient will maintain normal cardiac rhythm without dysrhythmias',
        'Patient will demonstrate effective breathing patterns and adequate oxygenation',
        'Patient will remain free from seizures during magnesium correction',
        'Patient will maintain normal muscle strength and function'
      ],
      interventions: [
        'Monitor serum magnesium levels and assess for clinical signs of imbalance',
        'Assess cardiac rhythm continuously for dysrhythmias',
        'Monitor respiratory rate and effort, especially with hypermagnesemia',
        'Administer magnesium supplements or discontinue sources as prescribed',
        'Assess deep tendon reflexes to monitor neuromuscular function',
        'Monitor for signs of hypomagnesemia (tetany, positive Chvostek\'s sign)',
        'Check for concurrent electrolyte imbalances (calcium, potassium)',
        'Keep calcium gluconate available as antidote for magnesium toxicity'
      ],
      evaluation: [
        'Cardiac rhythm remains stable without dangerous dysrhythmias',
        'Respiratory function remains adequate with normal rate and effort',
        'No seizure activity or neuromuscular irritability occurs',
        'Patient demonstrates normal muscle strength and reflexes'
      ]
    }
  },
  {
    id: 'potassium-imbalances',
    specialtyId: 'endocrine-metabolic',
    title: 'Potassium (K) Imbalances: Hyperkalemia and Hypokalemia',
    description: 'Nursing care plan for potassium imbalances',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for life-threatening dysrhythmias related to potassium imbalance',
        'Risk for muscle weakness and paralysis related to hypokalemia',
        'Risk for cardiac arrest related to severe hyperkalemia',
        'Risk for digitalis toxicity related to hypokalemia in patients on digoxin'
      ],
      goals: [
        'Patient will maintain normal cardiac rhythm without life-threatening dysrhythmias',
        'Patient will demonstrate normal muscle strength and function',
        'Patient will not experience cardiac arrest or severe complications',
        'Patient will maintain therapeutic drug levels without toxicity signs'
      ],
      interventions: [
        'Monitor serum potassium levels and cardiac rhythm continuously',
        'Assess for signs of hyperkalemia (muscle weakness, peaked T waves)',
        'Monitor for signs of hypokalemia (muscle cramps, flat T waves, U waves)',
        'Administer potassium supplements or restrict intake as prescribed',
        'Use emergency interventions for severe hyperkalemia (insulin, glucose, kayexalate)',
        'Monitor muscle strength and assess for paralysis',
        'Check digoxin levels if patient is on cardiac glycosides',
        'Educate patient about dietary potassium sources and restrictions'
      ],
      evaluation: [
        'Cardiac rhythm remains normal with no dangerous dysrhythmias',
        'Patient maintains normal muscle strength without weakness or paralysis',
        'No cardiac emergencies or life-threatening complications occur',
        'Drug levels remain therapeutic with no signs of toxicity'
      ]
    }
  },
  {
    id: 'sodium-imbalances',
    specialtyId: 'endocrine-metabolic',
    title: 'Sodium (Na) Imbalances: Hypernatremia and Hyponatremia',
    description: 'Nursing care plan for sodium imbalances',
    category: 'Endocrine & Metabolic Care',
    pgie: {
      problems: [
        'Risk for seizures related to hyponatremia and cerebral edema',
        'Risk for altered mental status related to sodium imbalance effects on brain',
        'Risk for fluid volume imbalance related to sodium and water shifts',
        'Risk for cerebral injury related to rapid correction of sodium levels'
      ],
      goals: [
        'Patient will remain free from seizures during sodium correction',
        'Patient will maintain normal mental status and orientation',
        'Patient will achieve appropriate fluid balance without complications',
        'Patient will not develop complications from overly rapid sodium correction'
      ],
      interventions: [
        'Monitor serum sodium levels and osmolality frequently',
        'Assess neurological status and mental status changes',
        'Implement seizure precautions for patients with hyponatremia',
        'Monitor intake and output and assess for edema or dehydration',
        'Administer hypertonic or hypotonic saline as prescribed',
        'Correct sodium levels gradually to prevent cerebral injury',
        'Monitor for signs of increased intracranial pressure',
        'Restrict or encourage fluid intake based on type of imbalance'
      ],
      evaluation: [
        'No seizure activity occurs during treatment period',
        'Patient remains alert, oriented, and mentally clear',
        'Fluid balance is achieved without edema or dehydration',
        'Sodium levels correct gradually without neurological complications'
      ]
    }
  },

  // Gastrointestinal Care
  {
    id: 'appendectomy-gi',
    specialtyId: 'gastrointestinal-care',
    title: 'Appendectomy',
    description: 'Gastrointestinal care for appendectomy patients',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue trauma',
        'Risk for infection related to surgical procedure and compromised skin integrity',
        'Risk for paralytic ileus related to anesthesia and surgical manipulation',
        'Deficient knowledge related to postoperative care and recovery process'
      ],
      goals: [
        'Patient will report pain level of 3 or less on 0-10 pain scale within 24 hours',
        'Patient will remain free from signs of infection during hospitalization',
        'Patient will demonstrate return of normal bowel function within 48-72 hours',
        'Patient will verbalize understanding of postoperative care instructions'
      ],
      interventions: [
        'Assess pain level using validated pain scale every 4 hours',
        'Administer analgesics as prescribed and monitor effectiveness',
        'Monitor surgical site for signs of infection (redness, swelling, drainage)',
        'Assess bowel sounds and abdominal distension every 4 hours',
        'Encourage early ambulation as tolerated to promote bowel function',
        'Maintain NPO status until bowel sounds return, then advance diet gradually',
        'Provide wound care and dressing changes per protocol',
        'Educate patient about signs of complications to report'
      ],
      evaluation: [
        'Patient reports adequate pain control with minimal discomfort',
        'Surgical site heals without signs of infection or complications',
        'Normal bowel sounds present with passage of flatus and stool',
        'Patient demonstrates understanding of home care instructions'
      ]
    }
  },
  {
    id: 'bowel-incontinence',
    specialtyId: 'gastrointestinal-care',
    title: 'Bowel Incontinence (Fecal Incontinence)',
    description: 'Nursing care plan for fecal incontinence management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Bowel incontinence related to muscle weakness or neurological impairment',
        'Risk for impaired skin integrity related to frequent contact with stool',
        'Social isolation related to embarrassment and fear of accidents',
        'Deficient knowledge related to bowel training and management techniques'
      ],
      goals: [
        'Patient will demonstrate improved bowel control and reduced incontinence episodes',
        'Patient will maintain skin integrity without breakdown or irritation',
        'Patient will participate in social activities without fear or embarrassment',
        'Patient will verbalize understanding of bowel management strategies'
      ],
      interventions: [
        'Assess bowel patterns and identify triggers for incontinence episodes',
        'Implement scheduled toileting program and bowel training regimen',
        'Provide thorough perineal hygiene after each episode',
        'Apply barrier creams or protective ointments to prevent skin breakdown',
        'Encourage dietary modifications to promote formed stools',
        'Teach pelvic floor exercises and biofeedback techniques',
        'Provide emotional support and counseling about condition',
        'Educate about absorbent products and clothing protection'
      ],
      evaluation: [
        'Patient shows reduction in frequency and severity of incontinence episodes',
        'Skin remains intact without redness, excoriation, or breakdown',
        'Patient expresses increased confidence in social situations',
        'Patient demonstrates effective use of bowel management techniques'
      ]
    }
  },
  {
    id: 'cholecystectomy-gi',
    specialtyId: 'gastrointestinal-care',
    title: 'Cholecystectomy',
    description: 'Gastrointestinal care for gallbladder surgery',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and CO2 gas retention from laparoscopy',
        'Risk for infection related to surgical procedure and invasive devices',
        'Imbalanced nutrition related to dietary fat intolerance post-surgery',
        'Deficient knowledge related to dietary modifications and postoperative care'
      ],
      goals: [
        'Patient will report pain level of 3 or less on 0-10 pain scale',
        'Patient will remain free from infection with normal healing',
        'Patient will demonstrate tolerance of low-fat diet without nausea or diarrhea',
        'Patient will verbalize understanding of dietary restrictions and activity limitations'
      ],
      interventions: [
        'Assess pain level and characteristics, including shoulder pain from CO2',
        'Administer prescribed analgesics and monitor for effectiveness',
        'Monitor surgical sites for signs of infection or bleeding',
        'Position patient to facilitate CO2 absorption and reduce discomfort',
        'Advance diet gradually from clear liquids to low-fat solid foods',
        'Monitor for signs of dumping syndrome or fat malabsorption',
        'Educate patient about permanent need for low-fat diet',
        'Provide wound care instructions and signs of complications to report'
      ],
      evaluation: [
        'Patient achieves adequate pain control with manageable discomfort',
        'Surgical sites heal without complications or signs of infection',
        'Patient tolerates low-fat diet with normal digestion and absorption',
        'Patient demonstrates understanding of dietary and activity restrictions'
      ]
    }
  },
  {
    id: 'constipation',
    specialtyId: 'gastrointestinal-care',
    title: 'Constipation',
    description: 'Nursing care plan for constipation management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Constipation related to decreased gastrointestinal motility or inadequate fiber intake',
        'Abdominal pain related to fecal impaction and increased colonic pressure',
        'Risk for fecal impaction related to prolonged constipation',
        'Deficient knowledge related to dietary and lifestyle factors affecting bowel function'
      ],
      goals: [
        'Patient will have regular bowel movements every 1-3 days',
        'Patient will report relief from abdominal pain and discomfort',
        'Patient will not develop fecal impaction or complications',
        'Patient will verbalize understanding of measures to prevent constipation'
      ],
      interventions: [
        'Assess bowel elimination patterns and identify contributing factors',
        'Encourage increased fluid intake (8-10 glasses of water daily)',
        'Promote high-fiber diet with fruits, vegetables, and whole grains',
        'Encourage regular physical activity and mobility as tolerated',
        'Administer prescribed laxatives or stool softeners as needed',
        'Provide privacy and adequate time for bowel elimination',
        'Monitor for signs of fecal impaction (abdominal distension, overflow incontinence)',
        'Educate about establishing regular bowel routine and toileting schedule'
      ],
      evaluation: [
        'Patient demonstrates regular bowel elimination pattern without straining',
        'Abdominal pain and discomfort are relieved or significantly reduced',
        'No signs of fecal impaction or complications develop',
        'Patient implements dietary and lifestyle changes to prevent constipation'
      ]
    }
  },
  {
    id: 'diarrhea',
    specialtyId: 'gastrointestinal-care',
    title: 'Diarrhea Nursing Care Plan and Management',
    description: 'Care plan for diarrhea and fluid balance',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Diarrhea related to infection, inflammation, or dietary factors',
        'Risk for fluid volume deficit related to excessive fluid loss through stools',
        'Risk for impaired skin integrity related to frequent loose stools',
        'Risk for electrolyte imbalance related to gastrointestinal losses'
      ],
      goals: [
        'Patient will have decreased frequency and improved consistency of stools',
        'Patient will maintain adequate hydration status and fluid balance',
        'Patient will maintain skin integrity in perianal area',
        'Patient will maintain electrolyte levels within normal limits'
      ],
      interventions: [
        'Monitor frequency, amount, and characteristics of stools',
        'Assess hydration status by monitoring vital signs and skin turgor',
        'Encourage oral rehydration with clear fluids and electrolyte solutions',
        'Provide thorough perineal care after each bowel movement',
        'Apply barrier creams or protective ointments to prevent skin breakdown',
        'Monitor serum electrolytes, especially sodium and potassium',
        'Identify and eliminate potential dietary triggers or irritants',
        'Administer antidiarrheal medications as prescribed'
      ],
      evaluation: [
        'Stool frequency decreases with improved consistency and formed character',
        'Patient demonstrates adequate hydration with normal vital signs',
        'Perianal skin remains intact without irritation or breakdown',
        'Electrolyte levels return to and remain within normal ranges'
      ]
    }
  },
  {
    id: 'cholecystitis-cholelithiasis',
    specialtyId: 'gastrointestinal-care',
    title: 'Cholecystitis and Cholelithiasis',
    description: 'Nursing care plan for gallbladder inflammation and stones',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to gallbladder inflammation and biliary colic',
        'Imbalanced nutrition related to fat intolerance and nausea',
        'Risk for infection related to inflammation and potential perforation',
        'Deficient knowledge related to dietary modifications and disease management'
      ],
      goals: [
        'Patient will report pain relief to tolerable levels within 2 hours of intervention',
        'Patient will demonstrate improved nutritional intake with fat-restricted diet',
        'Patient will remain free from complications such as infection or perforation',
        'Patient will verbalize understanding of dietary restrictions and follow-up care'
      ],
      interventions: [
        'Assess pain characteristics and provide analgesics as prescribed',
        'Monitor vital signs for signs of complications (fever, tachycardia)',
        'Maintain NPO status during acute episodes as ordered',
        'Position patient in semi-Fowler\'s position for comfort',
        'Advance to low-fat, low-cholesterol diet as tolerated',
        'Monitor for signs of jaundice and changes in urine/stool color',
        'Educate patient about avoiding fatty foods and large meals',
        'Prepare patient for diagnostic tests and potential surgical intervention'
      ],
      evaluation: [
        'Patient reports pain reduction to acceptable levels',
        'Patient tolerates low-fat diet without nausea or discomfort',
        'No signs of infection, jaundice, or complications develop',
        'Patient demonstrates understanding of dietary modifications and treatment plan'
      ]
    }
  },
  {
    id: 'gastroenteritis',
    specialtyId: 'gastrointestinal-care',
    title: 'Gastroenteritis',
    description: 'Nursing care plan for gastroenteritis management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Diarrhea related to gastrointestinal inflammation and infection',
        'Risk for fluid volume deficit related to vomiting and diarrhea',
        'Nausea related to gastrointestinal irritation and inflammation',
        'Risk for transmission of infection related to infectious gastroenteritis'
      ],
      goals: [
        'Patient will have reduced frequency of diarrheal stools within 24-48 hours',
        'Patient will maintain adequate hydration and electrolyte balance',
        'Patient will experience relief from nausea and vomiting',
        'Patient will not transmit infection to others through proper hygiene measures'
      ],
      interventions: [
        'Monitor fluid intake and output, including stool frequency and characteristics',
        'Encourage oral rehydration with clear fluids and electrolyte solutions',
        'Administer IV fluids as prescribed if oral intake inadequate',
        'Provide small, frequent meals of bland, easily digestible foods',
        'Administer antiemetics and antidiarrheal medications as prescribed',
        'Implement contact precautions if infectious cause suspected',
        'Educate about proper hand hygiene and food safety measures',
        'Monitor for signs of dehydration and electrolyte imbalances'
      ],
      evaluation: [
        'Stool frequency decreases with improved consistency',
        'Patient demonstrates adequate hydration with normal vital signs',
        'Nausea and vomiting episodes decrease or resolve',
        'No secondary cases of infection occur among contacts'
      ]
    }
  },
  {
    id: 'gerd',
    specialtyId: 'gastrointestinal-care',
    title: 'Gastroesophageal Reflux Disease (GERD)',
    description: 'GERD management nursing care plan',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to esophageal irritation from gastric acid reflux',
        'Imbalanced nutrition related to dietary restrictions and eating discomfort',
        'Risk for aspiration related to regurgitation and reflux',
        'Deficient knowledge related to lifestyle modifications and medication management'
      ],
      goals: [
        'Patient will report reduced heartburn and chest pain within 48 hours',
        'Patient will maintain adequate nutritional intake with modified diet',
        'Patient will remain free from aspiration episodes',
        'Patient will verbalize understanding of GERD management strategies'
      ],
      interventions: [
        'Assess pain characteristics and administer prescribed antacids or PPIs',
        'Elevate head of bed 6-8 inches to reduce nighttime reflux',
        'Encourage small, frequent meals and avoid eating before bedtime',
        'Identify and help patient avoid trigger foods (spicy, fatty, acidic)',
        'Monitor for signs of aspiration (cough, wheezing, fever)',
        'Educate about weight loss if overweight to reduce abdominal pressure',
        'Teach stress reduction techniques as stress can worsen symptoms',
        'Coordinate with healthcare provider for medication effectiveness evaluation'
      ],
      evaluation: [
        'Patient reports significant reduction in heartburn frequency and severity',
        'Patient tolerates modified diet with adequate caloric intake',
        'No episodes of aspiration or respiratory complications occur',
        'Patient demonstrates understanding and compliance with treatment regimen'
      ]
    }
  },
  {
    id: 'hemorrhoids-gi',
    specialtyId: 'gastrointestinal-care',
    title: 'Hemorrhoids',
    description: 'Gastrointestinal care for hemorrhoids',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to inflamed hemorrhoidal tissue and thrombosis',
        'Risk for bleeding related to fragile hemorrhoidal vessels',
        'Constipation related to fear of pain during defecation',
        'Deficient knowledge related to prevention and management of hemorrhoids'
      ],
      goals: [
        'Patient will report reduced pain and discomfort during bowel movements',
        'Patient will not experience significant bleeding episodes',
        'Patient will have regular, soft bowel movements without straining',
        'Patient will verbalize understanding of hemorrhoid prevention measures'
      ],
      interventions: [
        'Apply topical anesthetics or hemorrhoidal preparations as prescribed',
        'Encourage warm sitz baths 3-4 times daily for 10-15 minutes',
        'Provide stool softeners and high-fiber diet to prevent constipation',
        'Educate about proper toileting position and avoiding prolonged sitting',
        'Monitor for signs of bleeding and assess hemoglobin levels if indicated',
        'Apply ice packs for acute pain and inflammation',
        'Teach proper anal hygiene with gentle cleansing after bowel movements',
        'Encourage increased fluid intake and regular exercise'
      ],
      evaluation: [
        'Patient reports decreased pain and improved comfort with defecation',
        'No significant bleeding episodes occur',
        'Patient demonstrates regular bowel elimination without straining',
        'Patient implements preventive measures and lifestyle modifications'
      ]
    }
  },
  {
    id: 'hepatitis',
    specialtyId: 'gastrointestinal-care',
    title: 'Hepatitis',
    description: 'Nursing care plan for hepatitis management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Risk for impaired liver function related to hepatic inflammation and damage',
        'Fatigue related to decreased energy metabolism and liver dysfunction',
        'Imbalanced nutrition related to nausea, anorexia, and dietary restrictions',
        'Risk for transmission of infection related to infectious hepatitis types'
      ],
      goals: [
        'Patient will demonstrate stable or improving liver function tests',
        'Patient will report increased energy levels and reduced fatigue',
        'Patient will maintain adequate nutritional status despite dietary restrictions',
        'Patient will not transmit infection to others through proper precautions'
      ],
      interventions: [
        'Monitor liver function tests (ALT, AST, bilirubin, albumin) regularly',
        'Encourage rest periods and avoid strenuous activities',
        'Provide high-calorie, low-fat diet in small, frequent meals',
        'Eliminate hepatotoxic substances (alcohol, certain medications)',
        'Implement appropriate isolation precautions based on hepatitis type',
        'Monitor for signs of liver failure (encephalopathy, bleeding, ascites)',
        'Educate patient and family about disease transmission and prevention',
        'Administer prescribed antiviral medications and monitor for effectiveness'
      ],
      evaluation: [
        'Liver function tests show stabilization or improvement over time',
        'Patient demonstrates improved energy and ability to perform ADLs',
        'Patient maintains weight and adequate nutritional intake',
        'No secondary cases of hepatitis occur among close contacts'
      ]
    }
  },
  {
    id: 'ileostomy-colostomy-gi',
    specialtyId: 'gastrointestinal-care',
    title: 'Ileostomy & Colostomy',
    description: 'Gastrointestinal ostomy care and management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Risk for impaired skin integrity related to ostomy output and appliance irritation',
        'Disturbed body image related to presence of ostomy and changes in elimination',
        'Deficient knowledge related to ostomy care and self-management',
        'Risk for fluid and electrolyte imbalance related to high-output ostomy'
      ],
      goals: [
        'Patient will maintain skin integrity around ostomy site without breakdown',
        'Patient will verbalize acceptance of body changes and ostomy',
        'Patient will demonstrate competent ostomy care and appliance management',
        'Patient will maintain normal fluid and electrolyte balance'
      ],
      interventions: [
        'Assess peristomal skin for irritation, breakdown, or infection daily',
        'Teach proper ostomy appliance application and skin protection measures',
        'Empty ostomy pouch when 1/3 to 1/2 full to prevent leakage',
        'Measure and record ostomy output to monitor fluid balance',
        'Provide emotional support and counseling about body image concerns',
        'Connect patient with ostomy support groups and resources',
        'Educate about dietary modifications and foods that affect output',
        'Monitor for signs of dehydration and electrolyte imbalances'
      ],
      evaluation: [
        'Peristomal skin remains healthy without irritation or breakdown',
        'Patient expresses positive adaptation to ostomy and body changes',
        'Patient demonstrates independent ostomy care with confidence',
        'Fluid and electrolyte levels remain within normal limits'
      ]
    }
  },
  {
    id: 'inflammatory-bowel-disease',
    specialtyId: 'gastrointestinal-care',
    title: 'Inflammatory Bowel Disease (IBD)',
    description: 'Nursing care plan for IBD management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Chronic pain related to intestinal inflammation and cramping',
        'Diarrhea related to inflammatory process and decreased absorption',
        'Imbalanced nutrition related to malabsorption and dietary restrictions',
        'Risk for complications related to disease progression (perforation, obstruction)'
      ],
      goals: [
        'Patient will report reduced abdominal pain and cramping episodes',
        'Patient will have decreased frequency of diarrheal stools',
        'Patient will maintain adequate nutritional status and weight',
        'Patient will remain free from acute complications requiring emergency intervention'
      ],
      interventions: [
        'Assess pain patterns and administer prescribed anti-inflammatory medications',
        'Monitor stool frequency, consistency, and presence of blood or mucus',
        'Collaborate with dietitian for individualized nutrition plan',
        'Encourage stress management techniques as stress can trigger flares',
        'Monitor for signs of complications (severe pain, fever, distension)',
        'Administer immunosuppressive medications as prescribed and monitor for side effects',
        'Educate about disease management and medication compliance',
        'Provide emotional support for chronic disease adaptation'
      ],
      evaluation: [
        'Patient reports improved pain control with fewer cramping episodes',
        'Stool frequency decreases with improved consistency',
        'Patient maintains stable weight with adequate nutritional intake',
        'No acute complications or emergency situations develop'
      ]
    }
  },
  {
    id: 'intussusception',
    specialtyId: 'gastrointestinal-care',
    title: 'Intussusception',
    description: 'Nursing care plan for intussusception',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to bowel obstruction and telescoping of intestine',
        'Risk for fluid volume deficit related to vomiting and decreased oral intake',
        'Risk for bowel perforation related to prolonged obstruction',
        'Anxiety related to severe pain and emergency nature of condition'
      ],
      goals: [
        'Patient will achieve pain relief through successful reduction of intussusception',
        'Patient will maintain adequate hydration and electrolyte balance',
        'Patient will not develop bowel perforation or other complications',
        'Patient and family will demonstrate reduced anxiety with understanding of treatment'
      ],
      interventions: [
        'Assess pain characteristics and provide analgesics as prescribed',
        'Monitor vital signs for signs of shock or perforation',
        'Maintain NPO status and insert nasogastric tube if ordered',
        'Prepare patient for diagnostic imaging (ultrasound, CT scan)',
        'Monitor for classic triad: abdominal pain, vomiting, bloody stools',
        'Prepare for therapeutic interventions (barium enema, surgery)',
        'Provide emotional support and age-appropriate explanations',
        'Monitor for signs of successful reduction (pain relief, normal bowel sounds)'
      ],
      evaluation: [
        'Patient experiences pain relief following successful reduction',
        'Fluid and electrolyte balance is maintained or restored',
        'No signs of perforation or peritonitis develop',
        'Patient and family express understanding and reduced anxiety about condition'
      ]
    }
  },
  {
    id: 'liver-cirrhosis',
    specialtyId: 'gastrointestinal-care',
    title: 'Liver Cirrhosis',
    description: 'Nursing care plan for liver cirrhosis management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Risk for bleeding related to decreased clotting factors and portal hypertension',
        'Excess fluid volume related to decreased albumin and portal hypertension',
        'Risk for hepatic encephalopathy related to inability to detoxify ammonia',
        'Imbalanced nutrition related to poor appetite and malabsorption'
      ],
      goals: [
        'Patient will remain free from bleeding episodes',
        'Patient will achieve fluid balance with reduced ascites and edema',
        'Patient will maintain clear mental status without signs of encephalopathy',
        'Patient will maintain adequate nutritional status and protein intake'
      ],
      interventions: [
        'Monitor for signs of bleeding (hematemesis, melena, ecchymoses)',
        'Check coagulation studies (PT/INR, platelets) regularly',
        'Monitor daily weights and abdominal girth for ascites',
        'Restrict sodium intake and monitor fluid balance carefully',
        'Assess mental status and monitor ammonia levels',
        'Administer lactulose as prescribed to reduce ammonia levels',
        'Provide high-calorie, moderate-protein diet as tolerated',
        'Avoid hepatotoxic substances including alcohol and certain medications'
      ],
      evaluation: [
        'No bleeding episodes occur and coagulation studies stabilize',
        'Fluid retention decreases with reduced ascites and peripheral edema',
        'Patient remains alert and oriented without confusion',
        'Patient maintains adequate caloric and protein intake'
      ]
    }
  },
  {
    id: 'nausea-vomiting',
    specialtyId: 'gastrointestinal-care',
    title: 'Nausea & Vomiting',
    description: 'Nursing care plan for nausea and vomiting management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Nausea related to gastrointestinal irritation or medication side effects',
        'Risk for fluid volume deficit related to excessive vomiting',
        'Imbalanced nutrition related to inability to retain food and fluids',
        'Risk for aspiration related to vomiting episodes'
      ],
      goals: [
        'Patient will report reduced nausea intensity and frequency',
        'Patient will maintain adequate hydration and electrolyte balance',
        'Patient will tolerate small amounts of food and fluids without vomiting',
        'Patient will remain free from aspiration during vomiting episodes'
      ],
      interventions: [
        'Assess triggers for nausea and vomiting (medications, foods, odors)',
        'Administer antiemetic medications as prescribed before meals',
        'Provide small, frequent, bland meals and avoid strong odors',
        'Monitor fluid intake and output and assess for dehydration',
        'Position patient upright or side-lying to prevent aspiration',
        'Encourage clear liquids initially, then advance diet as tolerated',
        'Monitor electrolyte levels and administer IV fluids if needed',
        'Provide oral hygiene after vomiting episodes'
      ],
      evaluation: [
        'Patient reports significant reduction in nausea episodes',
        'Fluid balance is maintained with adequate hydration status',
        'Patient tolerates gradual advancement of diet without vomiting',
        'No aspiration episodes occur'
      ]
    }
  },
  {
    id: 'pancreatitis',
    specialtyId: 'gastrointestinal-care',
    title: 'Pancreatitis',
    description: 'Nursing care plan for pancreatitis management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to pancreatic inflammation and enzyme autodigestion',
        'Risk for fluid volume deficit related to vomiting and third-spacing',
        'Imbalanced nutrition related to pancreatic insufficiency and dietary restrictions',
        'Risk for complications related to severe pancreatitis (necrosis, pseudocysts)'
      ],
      goals: [
        'Patient will achieve adequate pain control to tolerable levels',
        'Patient will maintain adequate hydration and hemodynamic stability',
        'Patient will maintain nutritional status through appropriate feeding methods',
        'Patient will not develop life-threatening complications'
      ],
      interventions: [
        'Assess pain using appropriate scale and administer analgesics as prescribed',
        'Maintain NPO status during acute phase to rest the pancreas',
        'Monitor vital signs and fluid balance closely for shock or dehydration',
        'Position patient in knee-chest or side-lying position for comfort',
        'Monitor laboratory values (amylase, lipase, glucose, calcium)',
        'Administer IV fluids and electrolyte replacement as prescribed',
        'Gradually advance diet from clear liquids to low-fat meals as tolerated',
        'Monitor for complications (respiratory distress, hypocalcemia, hyperglycemia)'
      ],
      evaluation: [
        'Patient reports pain reduction to acceptable levels',
        'Hemodynamic stability is maintained with adequate fluid balance',
        'Patient tolerates gradual nutrition advancement without complications',
        'No signs of severe complications or organ failure develop'
      ]
    }
  },
  {
    id: 'peritonitis',
    specialtyId: 'gastrointestinal-care',
    title: 'Peritonitis',
    description: 'Nursing care plan for peritonitis management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Risk for septic shock related to peritoneal infection and systemic inflammation',
        'Acute pain related to peritoneal inflammation and abdominal distension',
        'Risk for fluid volume deficit related to third-spacing and inflammatory response',
        'Ineffective breathing pattern related to abdominal distension and pain'
      ],
      goals: [
        'Patient will remain hemodynamically stable without signs of septic shock',
        'Patient will achieve adequate pain control with appropriate interventions',
        'Patient will maintain adequate fluid balance and perfusion',
        'Patient will demonstrate effective breathing patterns and adequate oxygenation'
      ],
      interventions: [
        'Monitor vital signs frequently for signs of sepsis or shock',
        'Assess pain level and administer analgesics as prescribed',
        'Maintain strict aseptic technique during procedures and wound care',
        'Administer broad-spectrum antibiotics as prescribed',
        'Monitor fluid balance and administer IV fluids for resuscitation',
        'Position patient in semi-Fowler\'s position to facilitate breathing',
        'Insert nasogastric tube if ordered to decompress abdomen',
        'Prepare patient for potential surgical intervention'
      ],
      evaluation: [
        'Vital signs remain stable without signs of septic shock',
        'Patient reports manageable pain levels with interventions',
        'Fluid balance is maintained with adequate urine output',
        'Respiratory status remains stable with effective breathing patterns'
      ]
    }
  },
  {
    id: 'peptic-ulcer',
    specialtyId: 'gastrointestinal-care',
    title: 'Peptic Ulcer Disease',
    description: 'Care plan for peptic ulcer management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to gastric or duodenal ulceration and acid irritation',
        'Risk for bleeding related to ulcer erosion into blood vessels',
        'Imbalanced nutrition related to food intolerance and dietary restrictions',
        'Deficient knowledge related to H. pylori treatment and lifestyle modifications'
      ],
      goals: [
        'Patient will report significant reduction in epigastric pain',
        'Patient will remain free from gastrointestinal bleeding',
        'Patient will maintain adequate nutritional intake with modified diet',
        'Patient will verbalize understanding of treatment regimen and prevention measures'
      ],
      interventions: [
        'Assess pain characteristics and administer prescribed antacids or PPIs',
        'Monitor for signs of bleeding (hematemesis, melena, dropping hemoglobin)',
        'Encourage small, frequent meals and avoid irritating foods',
        'Eliminate alcohol, NSAIDs, and smoking which worsen ulcers',
        'Administer H. pylori eradication therapy if indicated',
        'Test stool for occult blood and monitor hemoglobin levels',
        'Educate about stress management techniques',
        'Schedule follow-up endoscopy to assess healing'
      ],
      evaluation: [
        'Patient reports significant improvement in pain symptoms',
        'No evidence of gastrointestinal bleeding occurs',
        'Patient tolerates diet modifications with adequate caloric intake',
        'Patient demonstrates understanding of medication regimen and lifestyle changes'
      ]
    }
  },
  {
    id: 'subtotal-gastrectomy-gi',
    specialtyId: 'gastrointestinal-care',
    title: 'Subtotal Gastrectomy',
    description: 'Gastrointestinal care for stomach surgery',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue trauma',
        'Risk for dumping syndrome related to altered gastric anatomy',
        'Imbalanced nutrition related to reduced stomach capacity and malabsorption',
        'Risk for vitamin B12 deficiency related to loss of intrinsic factor'
      ],
      goals: [
        'Patient will report pain level of 3 or less on 0-10 pain scale',
        'Patient will not experience dumping syndrome symptoms',
        'Patient will maintain adequate nutritional status with modified eating patterns',
        'Patient will not develop vitamin deficiencies through appropriate supplementation'
      ],
      interventions: [
        'Assess pain regularly and administer analgesics as prescribed',
        'Educate about eating small, frequent meals to prevent dumping syndrome',
        'Advise avoiding simple sugars and drinking fluids between meals',
        'Monitor weight and nutritional status closely',
        'Encourage high-protein, low-carbohydrate diet in small portions',
        'Administer vitamin B12 injections as prescribed',
        'Monitor for signs of dumping syndrome (weakness, sweating, diarrhea)',
        'Provide wound care and monitor surgical site for healing'
      ],
      evaluation: [
        'Patient achieves adequate pain control with manageable discomfort',
        'No episodes of dumping syndrome occur',
        'Patient maintains stable weight with adequate protein intake',
        'Vitamin levels remain within normal limits with supplementation'
      ]
    }
  },
  {
    id: 'umbilical-inguinal-hernia',
    specialtyId: 'gastrointestinal-care',
    title: 'Umbilical and Inguinal Hernia',
    description: 'Nursing care plan for hernia management',
    category: 'Gastrointestinal Care',
    pgie: {
      problems: [
        'Acute pain related to hernia protrusion and tissue stretching',
        'Risk for incarceration related to bowel entrapment in hernia sac',
        'Activity intolerance related to discomfort and fear of hernia enlargement',
        'Deficient knowledge related to hernia management and surgical options'
      ],
      goals: [
        'Patient will report manageable pain levels during daily activities',
        'Patient will not develop hernia incarceration or strangulation',
        'Patient will maintain normal activity levels with appropriate precautions',
        'Patient will verbalize understanding of hernia management and treatment options'
      ],
      interventions: [
        'Assess pain characteristics and provide analgesics as needed',
        'Monitor hernia for changes in size, color, or ability to reduce',
        'Educate about signs of incarceration (severe pain, nausea, vomiting)',
        'Encourage use of abdominal binder or truss if recommended',
        'Advise avoiding heavy lifting and straining during bowel movements',
        'Teach gentle manual reduction techniques if appropriate',
        'Prepare for surgical consultation and hernia repair if indicated',
        'Provide postoperative care instructions if surgery is performed'
      ],
      evaluation: [
        'Patient reports acceptable pain levels with current management',
        'No signs of hernia complications develop',
        'Patient resumes normal activities with appropriate modifications',
        'Patient demonstrates understanding of hernia care and follows recommendations'
      ]
    }
  },

  // Mental Health & Psychiatric Care
  {
    id: 'anxiety-disorders',
    specialtyId: 'mental-health-psychiatric',
    title: 'Anxiety and Panic Disorders',
    description: 'Nursing care plan for anxiety management',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Anxiety related to irrational fears and perceived threats',
        'Ineffective coping related to overwhelming feelings and inadequate support',
        'Social isolation related to fear of embarrassment or panic attacks',
        'Risk for self-harm related to severe anxiety and desperation'
      ],
      goals: [
        'Patient will demonstrate decreased anxiety and effective anxiety management techniques',
        'Patient will develop and utilize healthy coping strategies',
        'Patient will engage in social activities and relationships',
        'Patient will remain safe and free from self-harm behaviors'
      ],
      interventions: [
        'Assess anxiety level using standardized anxiety scales (GAD-7, Beck Anxiety)',
        'Teach relaxation techniques: deep breathing, progressive muscle relaxation',
        'Administer prescribed anxiolytic medications and monitor effectiveness',
        'Provide a calm, structured environment with minimal stimuli',
        'Encourage expression of feelings and validate patient experiences',
        'Educate about anxiety disorder and normalize symptoms',
        'Coordinate with mental health professionals for therapy (CBT, exposure therapy)',
        'Monitor for panic attack symptoms and provide immediate support'
      ],
      evaluation: [
        'Patient reports decreased anxiety levels and increased sense of control',
        'Patient demonstrates use of effective coping strategies during stressful situations',
        'Patient participates in social activities with reduced avoidance behaviors',
        'No self-harm incidents occur and patient expresses hope for recovery'
      ]
    }
  },
  {
    id: 'bipolar-disorders',
    specialtyId: 'mental-health-psychiatric',
    title: 'Bipolar Disorders',
    description: 'Nursing care plan for bipolar disorder management',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Risk for injury related to impaired judgment during manic episodes',
        'Disturbed sleep pattern related to hyperactivity or depression',
        'Ineffective medication adherence related to side effects or lack of insight',
        'Interrupted family processes related to unpredictable mood changes'
      ],
      goals: [
        'Patient will remain safe and free from injury during mood episodes',
        'Patient will maintain adequate sleep patterns (6-8 hours nightly)',
        'Patient will demonstrate consistent medication adherence',
        'Patient will maintain stable family and social relationships'
      ],
      interventions: [
        'Monitor for signs of mania: elevated mood, decreased sleep, grandiosity, impulsivity',
        'Assess for depression symptoms: hopelessness, fatigue, suicidal ideation',
        'Administer mood stabilizers as prescribed and monitor therapeutic levels',
        'Provide structured environment with consistent routines',
        'Monitor sleep patterns and implement sleep hygiene measures',
        'Educate patient and family about bipolar disorder and trigger recognition',
        'Facilitate family therapy and support group participation',
        'Coordinate with psychiatrist for medication adjustments and monitoring'
      ],
      evaluation: [
        'Patient maintains safety without injury during mood episodes',
        'Sleep patterns improved with 6-8 hours of restorative sleep',
        'Patient takes medications consistently with therapeutic blood levels maintained',
        'Family relationships stabilized with improved communication and understanding'
      ]
    }
  },
  {
    id: 'major-depression',
    specialtyId: 'mental-health-psychiatric',
    title: 'Major Depression',
    description: 'Care plan for depression and mood disorders',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Hopelessness related to altered mood and cognitive distortions',
        'Risk for suicide related to depression and feelings of worthlessness',
        'Self-care deficit related to fatigue and lack of motivation',
        'Social isolation related to depressed mood and low energy'
      ],
      goals: [
        'Patient will express realistic hope and positive thoughts about the future',
        'Patient will remain safe and contract for safety',
        'Patient will perform activities of daily living independently',
        'Patient will engage in meaningful social relationships and activities'
      ],
      interventions: [
        'Assess for suicidal ideation using Columbia Suicide Severity Rating Scale',
        'Implement suicide precautions if indicated: remove harmful objects, 1:1 observation',
        'Administer antidepressant medications as prescribed and monitor side effects',
        'Encourage participation in daily activities and self-care routines',
        'Provide cognitive behavioral therapy techniques to challenge negative thoughts',
        'Facilitate group therapy participation and social interaction',
        'Monitor appetite, sleep patterns, and energy levels',
        'Educate patient and family about depression and treatment options'
      ],
      evaluation: [
        'Patient expresses hope for the future and identifies reasons for living',
        'No suicide attempts or self-harm behaviors occur',
        'Patient independently performs hygiene, grooming, and other ADLs',
        'Patient participates in social activities and expresses enjoyment'
      ]
    }
  },
  {
    id: 'schizophrenia',
    specialtyId: 'mental-health-psychiatric',
    title: 'Schizophrenia',
    description: 'Care plan for schizophrenia and psychotic disorders',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Disturbed thought processes related to delusions and hallucinations',
        'Impaired social interaction related to withdrawal and paranoia',
        'Risk for violence related to command hallucinations or paranoid delusions',
        'Ineffective medication adherence related to side effects and lack of insight'
      ],
      goals: [
        'Patient will demonstrate reality-based thinking and decreased psychotic symptoms',
        'Patient will engage in appropriate social interactions',
        'Patient will remain safe and not harm self or others',
        'Patient will comply with prescribed medication regimen'
      ],
      interventions: [
        'Assess for hallucinations, delusions, and disorganized thinking',
        'Administer antipsychotic medications as prescribed and monitor for side effects',
        'Use therapeutic communication: avoid arguing with delusions, focus on feelings',
        'Provide structured environment with clear expectations and routines',
        'Monitor for extrapyramidal side effects and tardive dyskinesia',
        'Encourage participation in reality-oriented activities',
        'Assess for violence risk factors and implement safety measures as needed',
        'Coordinate with multidisciplinary team for comprehensive rehabilitation'
      ],
      evaluation: [
        'Patient shows decreased frequency and intensity of psychotic symptoms',
        'Patient initiates and maintains appropriate conversations with others',
        'No violent incidents occur and patient expresses control over behavior',
        'Patient takes medications consistently with minimal side effects'
      ]
    }
  },

  // Neurological Care
  {
    id: 'alzheimers-disease',
    specialtyId: 'neurological-care',
    title: 'Alzheimer\'s Disease',
    description: 'Comprehensive care plan for Alzheimer\'s patients',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Chronic confusion related to neuronal degeneration and loss',
        'Risk for injury related to wandering and impaired judgment',
        'Self-care deficit related to cognitive impairment',
        'Caregiver role strain related to progressive nature of disease'
      ],
      goals: [
        'Patient will remain safe and free from injury in current environment',
        'Patient will maintain optimal level of cognitive function for as long as possible',
        'Patient will maintain independence in ADLs to maximum potential',
        'Caregiver will demonstrate effective coping strategies and utilize support resources'
      ],
      interventions: [
        'Provide consistent, structured environment and routine',
        'Use validation therapy and avoid arguing with confused statements',
        'Implement safety measures: ID bracelet, door alarms, remove hazards',
        'Encourage participation in familiar activities and social interaction',
        'Provide clear, simple instructions using short sentences',
        'Monitor nutrition and hydration status regularly',
        'Educate family on disease progression and available resources',
        'Coordinate with interdisciplinary team for comprehensive care'
      ],
      evaluation: [
        'Patient remains safe with no injuries from wandering or confusion',
        'Cognitive function maintained at optimal level with minimal decline',
        'Patient participates in self-care activities to maximum ability',
        'Family demonstrates understanding and utilizes appropriate support services'
      ]
    }
  },
  {
    id: 'stroke',
    specialtyId: 'neurological-care',
    title: 'Cerebrovascular Accident (Stroke)',
    description: 'Evidence-based nursing care plan for stroke patients',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to hemiparesis or hemiplegia',
        'Impaired verbal communication related to dysarthria or aphasia',
        'Risk for aspiration related to impaired swallowing reflex',
        'Self-care deficit related to neuromuscular impairment'
      ],
      goals: [
        'Patient will demonstrate improved mobility and motor function within limitations',
        'Patient will establish effective communication methods',
        'Patient will maintain clear airway and show no signs of aspiration',
        'Patient will achieve maximum independence in activities of daily living'
      ],
      interventions: [
        'Perform neurological assessments every 4 hours using Glasgow Coma Scale',
        'Position patient to prevent complications: turn every 2 hours, elevate HOB 30°',
        'Implement fall prevention measures and assist with mobility as needed',
        'Conduct swallowing assessment before oral intake; NPO if high aspiration risk',
        'Encourage use of unaffected extremities and adaptive equipment',
        'Provide speech therapy consultation for communication impairments',
        'Monitor for signs of increased intracranial pressure',
        'Educate patient and family on stroke prevention and rehabilitation'
      ],
      evaluation: [
        'Patient shows improvement in motor strength and coordination',
        'Communication methods established with patient able to express needs',
        'No episodes of aspiration; swallowing function improved or compensated',
        'Patient demonstrates increased independence in self-care activities'
      ]
    }
  },
  {
    id: 'parkinsons-disease',
    specialtyId: 'neurological-care',
    title: 'Parkinson\'s Disease',
    description: 'Care plan for Parkinson\'s disease management',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to bradykinesia, rigidity, and tremor',
        'Risk for falls related to postural instability and gait disturbances',
        'Impaired verbal communication related to monotone speech and decreased facial expression',
        'Imbalanced nutrition related to difficulty swallowing and decreased appetite'
      ],
      goals: [
        'Patient will maintain optimal mobility and independence in ADLs',
        'Patient will remain free from falls and injury',
        'Patient will communicate effectively using adaptive techniques',
        'Patient will maintain adequate nutritional status and weight'
      ],
      interventions: [
        'Administer antiparkinsonian medications on time to maintain therapeutic levels',
        'Encourage regular exercise and physical therapy for muscle flexibility',
        'Implement fall prevention strategies: remove hazards, use assistive devices',
        'Provide ample time for activities and avoid rushing patient',
        'Encourage speech therapy consultation for communication difficulties',
        'Monitor swallowing ability and modify diet texture as needed',
        'Educate patient and family about disease progression and medication management',
        'Provide emotional support and connect with Parkinson\'s support groups'
      ],
      evaluation: [
        'Patient demonstrates improved mobility with optimal medication timing',
        'No falls or injuries occur during care period',
        'Patient communicates needs effectively with family and healthcare team',
        'Nutritional status maintained with stable weight and adequate intake'
      ]
    }
  },
  {
    id: 'seizure-disorder',
    specialtyId: 'neurological-care',
    title: 'Seizure Disorder',
    description: 'Nursing care plan for seizure management',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Risk for injury related to seizure activity and loss of consciousness',
        'Risk for ineffective airway clearance related to altered consciousness during seizures',
        'Anxiety related to unpredictability of seizure occurrence',
        'Deficient knowledge related to seizure management and safety precautions'
      ],
      goals: [
        'Patient will remain free from injury during seizure episodes',
        'Patient will maintain clear airway and adequate oxygenation',
        'Patient will demonstrate reduced anxiety and effective coping strategies',
        'Patient will verbalize understanding of seizure disorder and self-management'
      ],
      interventions: [
        'Administer anticonvulsant medications as prescribed and monitor blood levels',
        'Implement seizure precautions: padded bed rails, suction at bedside',
        'During seizure: protect from injury, do not restrain, turn to side',
        'Monitor airway patency and provide oxygen as needed post-seizure',
        'Document seizure activity: onset, duration, type, postictal phase',
        'Educate patient on seizure triggers and lifestyle modifications',
        'Provide emotional support and counseling regarding lifestyle impact',
        'Coordinate with neurology for medication adjustments and follow-up'
      ],
      evaluation: [
        'No injuries sustained during seizure episodes',
        'Airway remained clear with adequate oxygenation maintained',
        'Patient reports decreased anxiety and improved confidence in self-management',
        'Patient demonstrates understanding of medication compliance and safety measures'
      ]
    }
  },

  // Respiratory Care
  {
    id: 'asthma',
    specialtyId: 'respiratory-care',
    title: 'Asthma',
    description: 'Evidence-based nursing care plan for asthma management',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to bronchospasm and increased mucus production',
        'Impaired gas exchange related to ventilation-perfusion imbalance',
        'Activity intolerance related to fatigue and shortness of breath',
        'Anxiety related to feeling of suffocation and fear of attack'
      ],
      goals: [
        'Patient will maintain patent airway with effective breathing patterns',
        'Patient will demonstrate improved gas exchange with oxygen saturation >95%',
        'Patient will tolerate increased activity levels without respiratory distress',
        'Patient will verbalize reduced anxiety and confidence in self-management'
      ],
      interventions: [
        'Monitor respiratory status: rate, depth, use of accessory muscles, breath sounds',
        'Administer bronchodilators and corticosteroids as prescribed',
        'Position patient in high-Fowler\'s position to facilitate breathing',
        'Encourage slow, deep breathing and pursed-lip breathing techniques',
        'Identify and help patient avoid known triggers (allergens, irritants)',
        'Provide patient education on proper inhaler technique and peak flow monitoring',
        'Develop asthma action plan with patient and family',
        'Monitor oxygen saturation and administer oxygen therapy as needed'
      ],
      evaluation: [
        'Airway clearance improved with decreased wheezing and clear breath sounds',
        'Gas exchange adequate with oxygen saturation maintained >95%',
        'Patient able to perform activities without significant dyspnea',
        'Patient demonstrates proper self-management techniques and reduced anxiety'
      ]
    }
  },
  {
    id: 'copd',
    specialtyId: 'respiratory-care',
    title: 'Chronic Obstructive Pulmonary Disease (COPD)',
    description: 'Long-term care plan for COPD management',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to shortness of breath and anxiety',
        'Impaired gas exchange related to alveolar-capillary membrane changes',
        'Activity intolerance related to fatigue and insufficient oxygenation',
        'Ineffective airway clearance related to bronchospasm and retained secretions'
      ],
      goals: [
        'Patient will demonstrate improved breathing patterns and reduced dyspnea',
        'Patient will maintain oxygen saturation within acceptable parameters',
        'Patient will increase activity tolerance and maintain independence',
        'Patient will effectively clear airway secretions'
      ],
      interventions: [
        'Monitor respiratory status including rate, depth, and oxygen saturation',
        'Administer oxygen therapy as prescribed, typically 1-3 L/min to maintain SpO2 88-92%',
        'Encourage pursed-lip breathing and diaphragmatic breathing exercises',
        'Position patient in tripod or high-Fowler\'s position for optimal breathing',
        'Administer bronchodilators and corticosteroids as prescribed',
        'Encourage fluid intake to thin secretions unless contraindicated',
        'Provide pulmonary rehabilitation education and smoking cessation counseling',
        'Monitor for signs of respiratory infection and exacerbation'
      ],
      evaluation: [
        'Breathing pattern improved with decreased use of accessory muscles',
        'Oxygen saturation maintained within target range without distress',
        'Patient able to perform ADLs with manageable shortness of breath',
        'Effective cough with improved ability to expectorate secretions'
      ]
    }
  },
  {
    id: 'lung-cancer',
    specialtyId: 'respiratory-care',
    title: 'Lung Cancer',
    description: 'Nursing care plan for lung cancer patients',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to tumor obstruction and decreased lung capacity',
        'Acute pain related to tumor invasion and treatment side effects',
        'Anxiety related to diagnosis and prognosis of life-threatening illness',
        'Imbalanced nutrition related to decreased appetite and treatment effects'
      ],
      goals: [
        'Patient will maintain adequate respiratory function and comfort',
        'Patient will report pain control at acceptable level (≤4/10)',
        'Patient will demonstrate effective coping mechanisms and reduced anxiety',
        'Patient will maintain adequate nutritional status and stable weight'
      ],
      interventions: [
        'Monitor respiratory status: rate, depth, oxygen saturation, breath sounds',
        'Administer oxygen therapy and bronchodilators as prescribed',
        'Position patient to optimize breathing: elevated head of bed, leaning forward',
        'Assess pain using 0-10 scale and administer analgesics as prescribed',
        'Provide emotional support and encourage expression of concerns',
        'Coordinate with oncology team for chemotherapy and radiation therapy',
        'Monitor nutritional intake and collaborate with dietitian',
        'Educate patient and family about treatment options and palliative care'
      ],
      evaluation: [
        'Respiratory function optimized with manageable dyspnea',
        'Pain adequately controlled allowing for comfort and rest',
        'Patient expresses reduced anxiety and demonstrates effective coping',
        'Nutritional goals met with stable or improved weight'
      ]
    }
  },
  {
    id: 'pneumonia',
    specialtyId: 'respiratory-care',
    title: 'Pneumonia',
    description: 'Comprehensive nursing care plan for pneumonia patients',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to alveolar-capillary membrane changes',
        'Ineffective airway clearance due to increased secretions',
        'Activity intolerance related to decreased oxygenation',
        'Risk for fluid volume deficit due to fever and tachypnea'
      ],
      goals: [
        'Patient will maintain oxygen saturation ≥ 95% on room air within 48 hours',
        'Patient will demonstrate effective airway clearance as evidenced by normal breath sounds and productive cough',
        'Patient will perform activities of daily living (ADLs) with minimal fatigue',
        'Patient will maintain adequate hydration as evidenced by stable vital signs and urine output > 0.5 mL/kg/hr'
      ],
      interventions: [
        'Monitor respiratory rate, depth, effort, and oxygen saturation at least every 4 hours',
        'Administer supplemental oxygen as prescribed and titrate to maintain SpO₂ ≥ 95%',
        'Encourage coughing, deep breathing exercises, and use of incentive spirometry every 2 hours while awake',
        'Perform chest physiotherapy and suction as needed to mobilize secretions',
        'Position patient in semi-Fowler\'s or high-Fowler\'s position to promote lung expansion',
        'Assess and document sputum characteristics (color, amount, consistency)',
        'Administer prescribed antibiotics and antipyretics as scheduled',
        'Encourage fluid intake up to 2–3 liters per day, unless contraindicated',
        'Monitor input and output, and assess for signs of dehydration'
      ],
      evaluation: [
        'Oxygen saturation maintained at ≥ 95% on room air',
        'Breath sounds clear or improved; productive cough present with decreased sputum',
        'Patient able to complete ADLs without undue fatigue',
        'Adequate fluid balance maintained; vital signs stable; urine output within normal limits'
      ]
    }
  },
  {
    id: 'pulmonary-embolism',
    specialtyId: 'respiratory-care',
    title: 'Pulmonary Embolism',
    description: 'Emergency care plan for pulmonary embolism',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to ventilation-perfusion mismatch',
        'Acute pain related to pulmonary infarction and pleural irritation',
        'Risk for bleeding related to anticoagulation therapy',
        'Anxiety related to sudden onset of dyspnea and fear of death'
      ],
      goals: [
        'Patient will maintain adequate oxygenation with SpO2 ≥ 95%',
        'Patient will report chest pain reduced to tolerable levels',
        'Patient will not experience bleeding complications from anticoagulation',
        'Patient will demonstrate reduced anxiety and understanding of condition'
      ],
      interventions: [
        'Monitor respiratory status continuously including oxygen saturation',
        'Administer oxygen therapy as prescribed to maintain adequate oxygenation',
        'Position patient in high-Fowler\'s position to facilitate breathing',
        'Administer prescribed anticoagulants and monitor coagulation studies',
        'Assess for signs of bleeding: bruising, hematuria, melena, epistaxis',
        'Monitor vital signs for signs of hemodynamic compromise',
        'Provide emotional support and explain procedures to reduce anxiety',
        'Encourage bed rest during acute phase to prevent clot dislodgement'
      ],
      evaluation: [
        'Gas exchange improved with oxygen saturation within normal limits',
        'Chest pain manageable and does not interfere with breathing',
        'No signs of bleeding complications from anticoagulation therapy',
        'Patient expresses reduced anxiety and understanding of treatment plan'
      ]
    }
  },

  // Surgery & Perioperative Care
  {
    id: 'amputation',
    specialtyId: 'surgery-perioperative',
    title: 'Amputation',
    description: 'Nursing care plan for amputation patients',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical amputation and phantom limb sensation',
        'Impaired mobility related to loss of limb and need for adaptive equipment',
        'Disturbed body image related to loss of body part and functional changes',
        'Risk for infection related to surgical incision and potential for delayed healing'
      ],
      goals: [
        'Patient will report manageable pain levels and effective phantom limb management',
        'Patient will demonstrate safe mobility with assistive devices',
        'Patient will verbalize acceptance of body changes and maintain positive self-image',
        'Patient will maintain clean, well-healing surgical site without infection'
      ],
      interventions: [
        'Assess pain level including phantom limb pain using 0-10 scale every 4 hours',
        'Administer prescribed pain medications and evaluate effectiveness',
        'Monitor surgical site for signs of infection, drainage, or poor healing',
        'Assist with positioning and stump care according to surgeon guidelines',
        'Coordinate with physical therapy for mobility training and prosthetic fitting',
        'Provide emotional support and counseling for body image concerns',
        'Educate patient on stump care, phantom limb phenomena, and prosthetic care',
        'Encourage participation in support groups or peer counseling'
      ],
      evaluation: [
        'Patient reports adequate pain control and decreased phantom limb discomfort',
        'Patient demonstrates safe transfers and mobility with appropriate assistive devices',
        'Patient expresses acceptance of amputation and positive outlook for rehabilitation',
        'Surgical site healed without complications or signs of infection'
      ]
    }
  },
  {
    id: 'appendectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Appendectomy',
    description: 'Pre and post-operative care for appendectomy patients',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue trauma',
        'Risk for infection related to ruptured appendix or surgical wound',
        'Nausea and vomiting related to anesthesia and bowel manipulation',
        'Activity intolerance related to post-operative pain and fatigue'
      ],
      goals: [
        'Patient will report pain level of 3 or less on 0-10 scale',
        'Patient will remain free from post-operative infections',
        'Patient will tolerate oral intake without nausea or vomiting',
        'Patient will progressively increase activity level as tolerated'
      ],
      interventions: [
        'Assess pain level every 4 hours and administer analgesics as prescribed',
        'Monitor incision site for signs of infection: redness, swelling, drainage',
        'Monitor vital signs every 4 hours for signs of complications',
        'Assess bowel sounds and encourage early ambulation to prevent ileus',
        'Administer anti-emetics as prescribed and advance diet as tolerated',
        'Encourage deep breathing and coughing exercises to prevent pneumonia',
        'Provide incision care education and signs of infection to report',
        'Assist with progressive mobilization from bed to chair to walking'
      ],
      evaluation: [
        'Patient achieved adequate pain control with minimal discomfort',
        'Incision healed without signs of infection or complications',
        'Patient tolerated regular diet without nausea or vomiting',
        'Patient ambulated independently with normal activity tolerance'
      ]
    }
  },
  {
    id: 'cholecystectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Cholecystectomy',
    description: 'Gallbladder surgery nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and pneumoperitoneum from laparoscopy',
        'Risk for infection related to surgical wound and potential bile leakage',
        'Nausea related to anesthesia and manipulation of abdominal organs',
        'Impaired skin integrity related to surgical incisions'
      ],
      goals: [
        'Patient will report manageable pain levels (≤3/10) throughout recovery',
        'Patient will remain free from post-operative infections',
        'Patient will tolerate oral intake without persistent nausea',
        'Patient will achieve complete wound healing without complications'
      ],
      interventions: [
        'Assess pain level every 4 hours, especially right shoulder pain from CO2 gas',
        'Administer prescribed analgesics and evaluate effectiveness',
        'Monitor surgical sites for signs of infection or bile leakage',
        'Encourage early ambulation to help eliminate residual CO2 gas',
        'Monitor for signs of bile duct injury: jaundice, abdominal pain, fever',
        'Provide anti-emetics as needed and advance diet gradually',
        'Educate patient on low-fat diet modifications post-surgery',
        'Teach incision care and signs of complications to report'
      ],
      evaluation: [
        'Patient achieved adequate pain relief with decreasing discomfort',
        'No signs of infection or bile leakage at surgical sites',
        'Patient tolerated regular diet with minimal digestive issues',
        'Incisions healed completely without dehiscence or infection'
      ]
    }
  },
  {
    id: 'fracture',
    specialtyId: 'surgery-perioperative',
    title: 'Fracture',
    description: 'Nursing care plan for fracture management',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to tissue trauma and bone disruption',
        'Impaired physical mobility related to pain and immobilization',
        'Risk for infection related to break in skin integrity',
        'Risk for neurovascular compromise related to swelling and immobilization'
      ],
      goals: [
        'Patient will report pain level of 3 or less on 0-10 scale within 24 hours',
        'Patient will maintain or improve mobility within restrictions by discharge',
        'Patient will remain free from signs of infection throughout healing',
        'Patient will maintain adequate circulation and sensation distal to fracture'
      ],
      interventions: [
        'Assess pain level using 0-10 scale every 4 hours and PRN',
        'Administer prescribed pain medications and evaluate effectiveness',
        'Maintain proper alignment and immobilization of fractured extremity',
        'Monitor neurovascular status every 2 hours (circulation, sensation, movement)',
        'Assess for signs of infection at fracture site daily',
        'Encourage mobility within prescribed restrictions',
        'Provide patient education on cast care and signs of complications'
      ],
      evaluation: [
        'Pain adequately controlled with reported levels of 3 or less',
        'Patient demonstrates safe mobility techniques within restrictions',
        'No signs of infection present at fracture site',
        'Neurovascular status remains intact distal to fracture'
      ]
    }
  },
  {
    id: 'hemorrhoids',
    specialtyId: 'surgery-perioperative',
    title: 'Hemorrhoids',
    description: 'Care plan for hemorrhoid treatment and management',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to swollen, inflamed hemorrhoidal tissue',
        'Risk for infection related to surgical excision site',
        'Constipation related to fear of pain with defecation',
        'Deficient knowledge related to post-operative care and prevention'
      ],
      goals: [
        'Patient will report decreased pain and improved comfort with sitting',
        'Patient will remain free from post-operative infection',
        'Patient will have regular, soft bowel movements without straining',
        'Patient will demonstrate understanding of hemorrhoid prevention strategies'
      ],
      interventions: [
        'Assess pain level and provide prescribed analgesics and topical treatments',
        'Apply ice packs for first 24 hours, then warm sitz baths 3-4 times daily',
        'Monitor surgical site for signs of infection, bleeding, or poor healing',
        'Encourage high-fiber diet and adequate fluid intake (8-10 glasses daily)',
        'Administer stool softeners and laxatives as prescribed',
        'Educate patient on proper toileting habits and avoiding straining',
        'Provide donut cushion for sitting comfort and positioning instructions',
        'Teach signs of complications to report: excessive bleeding, fever, severe pain'
      ],
      evaluation: [
        'Patient achieved pain relief and comfortable sitting position',
        'Surgical site healed without signs of infection or complications',
        'Patient had regular bowel movements without straining or pain',
        'Patient verbalizes understanding of lifestyle modifications for prevention'
      ]
    }
  },
  {
    id: 'hysterectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Hysterectomy',
    description: 'Surgical nursing care plan for hysterectomy',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue trauma',
        'Risk for infection related to surgical wound and urinary tract',
        'Disturbed body image related to loss of reproductive organs',
        'Risk for urinary retention related to proximity of surgery to bladder'
      ],
      goals: [
        'Patient will report adequate pain control with minimal discomfort',
        'Patient will remain free from post-operative infections',
        'Patient will demonstrate positive adjustment to body changes',
        'Patient will resume normal urinary elimination patterns'
      ],
      interventions: [
        'Assess pain level every 4 hours and administer analgesics as prescribed',
        'Monitor abdominal incision for signs of infection or dehiscence',
        'Encourage early ambulation to prevent complications and promote healing',
        'Monitor urinary output and assess for signs of retention or infection',
        'Provide emotional support and counseling regarding body image concerns',
        'Educate patient on activity restrictions and lifting limitations',
        'Monitor for vaginal bleeding and provide perineal care instructions',
        'Discuss hormonal changes and menopausal symptoms if applicable'
      ],
      evaluation: [
        'Patient achieved satisfactory pain relief throughout recovery period',
        'No signs of wound infection or urinary tract complications',
        'Patient expressed acceptance of procedure and positive body image',
        'Patient voided adequately without retention or incontinence issues'
      ]
    }
  },
  {
    id: 'ileostomy-colostomy',
    specialtyId: 'surgery-perioperative',
    title: 'Ileostomy & Colostomy',
    description: 'Nursing care plan for ostomy creation and management',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Disturbed body image related to presence of ostomy and altered bowel elimination',
        'Risk for impaired skin integrity related to exposure to stool and ostomy appliances',
        'Deficient knowledge related to ostomy care and management',
        'Risk for infection related to surgical site and contamination from stool'
      ],
      goals: [
        'Patient will demonstrate acceptance of ostomy and positive body image',
        'Patient will maintain intact skin around ostomy site',
        'Patient will demonstrate competency in ostomy care and appliance management',
        'Patient will remain free from infections related to ostomy care'
      ],
      interventions: [
        'Assess patient\'s emotional response to ostomy and provide psychological support',
        'Monitor stoma color, size, and output every 4 hours initially',
        'Maintain proper fit of ostomy appliance and change when 1/3 full',
        'Protect peristomal skin with barrier products and proper cleansing',
        'Educate patient on ostomy care: emptying, changing appliance, skin care',
        'Coordinate with enterostomal therapist for specialized care and education',
        'Monitor for complications: prolapse, retraction, obstruction, infection',
        'Connect patient with ostomy support groups and community resources'
      ],
      evaluation: [
        'Patient expressed acceptance of ostomy and maintained positive self-image',
        'Peristomal skin remained intact without irritation or breakdown',
        'Patient demonstrated independent ostomy care with confidence',
        'No signs of infection or complications at ostomy site'
      ]
    }
  },
  {
    id: 'laminectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Laminectomy (Disc Surgery)',
    description: 'Spinal surgery nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and nerve inflammation',
        'Risk for injury related to spinal instability and neurological deficits',
        'Impaired mobility related to surgical restrictions and pain',
        'Risk for infection related to surgical site and proximity to spinal canal'
      ],
      goals: [
        'Patient will report decreased pain and improved neurological symptoms',
        'Patient will maintain neurological function without complications',
        'Patient will demonstrate safe mobility within prescribed restrictions',
        'Patient will maintain surgical site healing without infection'
      ],
      interventions: [
        'Assess neurological status every 4 hours: motor, sensory, reflexes',
        'Monitor for signs of cerebrospinal fluid leak: clear drainage, headache',
        'Maintain proper spinal alignment with log-rolling techniques',
        'Assess pain level and administer analgesics as prescribed',
        'Monitor surgical incision for signs of infection or drainage',
        'Encourage early ambulation as ordered with assistance initially',
        'Educate patient on proper body mechanics and lifting restrictions',
        'Coordinate with physical therapy for safe mobility training'
      ],
      evaluation: [
        'Patient experienced significant pain relief and improved neurological function',
        'No neurological complications or deficits developed post-operatively',
        'Patient ambulated safely with understanding of activity restrictions',
        'Surgical incision healed completely without signs of infection'
      ]
    }
  },
  {
    id: 'mastectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Mastectomy',
    description: 'Breast surgery nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue removal',
        'Disturbed body image related to loss of breast and changes in appearance',
        'Risk for lymphedema related to lymph node removal',
        'Risk for infection related to surgical wound and drainage tubes'
      ],
      goals: [
        'Patient will achieve adequate pain control with minimal discomfort',
        'Patient will demonstrate positive adaptation to body image changes',
        'Patient will maintain normal arm function without lymphedema development',
        'Patient will maintain clean, healing surgical site without infection'
      ],
      interventions: [
        'Assess pain level every 4 hours and provide multimodal pain management',
        'Monitor surgical site and drainage tubes for output, color, and patency',
        'Position affected arm elevated on pillows to promote drainage',
        'Encourage early range of motion exercises as prescribed by surgeon',
        'Provide emotional support and body image counseling',
        'Educate on lymphedema prevention: avoid blood pressure checks on affected side',
        'Connect with breast cancer support groups and reconstruction resources',
        'Teach arm exercises and precautions to prevent complications'
      ],
      evaluation: [
        'Patient achieved satisfactory pain relief throughout recovery',
        'Patient expressed positive coping with body changes and support needs',
        'Patient maintained full arm mobility without signs of lymphedema',
        'Surgical site healed without complications or signs of infection'
      ]
    }
  },
  {
    id: 'subtotal-gastrectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Subtotal Gastrectomy',
    description: 'Stomach surgery nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue manipulation',
        'Risk for nutritional deficit related to reduced stomach capacity',
        'Risk for dumping syndrome related to rapid gastric emptying',
        'Risk for anastomotic leak related to surgical connection sites'
      ],
      goals: [
        'Patient will achieve adequate pain control throughout recovery',
        'Patient will maintain nutritional status with appropriate dietary modifications',
        'Patient will adapt to dietary changes without dumping syndrome symptoms',
        'Patient will heal without anastomotic complications'
      ],
      interventions: [
        'Assess pain level every 4 hours and provide prescribed analgesics',
        'Monitor nasogastric tube output and maintain patency as ordered',
        'Assess for signs of anastomotic leak: abdominal pain, fever, drainage',
        'Begin clear liquids as ordered and advance diet gradually',
        'Educate patient on small, frequent meals and dietary modifications',
        'Monitor for dumping syndrome: nausea, cramping, diarrhea after eating',
        'Coordinate with dietitian for nutritional counseling and meal planning',
        'Monitor weight and laboratory values for nutritional status'
      ],
      evaluation: [
        'Patient achieved satisfactory pain relief with progressive improvement',
        'Patient maintained stable weight with adequate nutritional intake',
        'Patient adapted to dietary changes without significant dumping symptoms',
        'Patient healed without anastomotic leak or other surgical complications'
      ]
    }
  },
  {
    id: 'surgery-perioperative-client',
    specialtyId: 'surgery-perioperative',
    title: 'Surgery (Perioperative Client)',
    description: 'General perioperative nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Anxiety related to surgical procedure and fear of unknown outcomes',
        'Risk for surgical site infection related to invasive procedure',
        'Acute pain related to tissue trauma from surgical intervention',
        'Risk for perioperative positioning injury related to prolonged immobility'
      ],
      goals: [
        'Patient will demonstrate reduced anxiety and understanding of perioperative process',
        'Patient will remain free from surgical site infections',
        'Patient will achieve adequate post-operative pain control',
        'Patient will maintain tissue integrity without positioning injuries'
      ],
      interventions: [
        'Provide pre-operative education about surgical procedure and expectations',
        'Implement pre-operative skin preparation and antibiotic prophylaxis as ordered',
        'Monitor vital signs and assess for complications throughout perioperative period',
        'Ensure proper positioning and padding during surgery to prevent injury',
        'Assess pain level post-operatively and provide multimodal pain management',
        'Monitor surgical site for signs of infection or delayed healing',
        'Encourage early mobilization and deep breathing exercises',
        'Provide discharge education on incision care and activity restrictions'
      ],
      evaluation: [
        'Patient verbalized reduced anxiety and understanding of surgical experience',
        'No surgical site infections developed during recovery period',
        'Patient achieved satisfactory pain control with appropriate interventions',
        'No positioning injuries or pressure ulcers developed during surgery'
      ]
    }
  },
  {
    id: 'thyroidectomy',
    specialtyId: 'surgery-perioperative',
    title: 'Thyroidectomy',
    description: 'Thyroid surgery nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Risk for ineffective airway clearance related to tracheal edema and neck swelling',
        'Risk for injury related to inadvertent parathyroid or recurrent laryngeal nerve damage',
        'Acute pain related to surgical incision in neck area',
        'Risk for thyroid storm or hypothyroidism related to thyroid hormone imbalance'
      ],
      goals: [
        'Patient will maintain patent airway without respiratory distress',
        'Patient will maintain normal calcium levels and voice function',
        'Patient will achieve adequate pain control with minimal neck discomfort',
        'Patient will maintain stable thyroid hormone levels without complications'
      ],
      interventions: [
        'Monitor respiratory status closely: rate, effort, oxygen saturation',
        'Assess for signs of tracheal compression: stridor, difficulty swallowing',
        'Monitor serum calcium levels and assess for signs of hypocalcemia',
        'Evaluate voice quality and swallowing ability every 4 hours',
        'Position patient with head of bed elevated 30-45 degrees',
        'Apply ice collar to reduce swelling and provide comfort',
        'Monitor thyroid hormone levels and administer replacement therapy as ordered',
        'Educate patient on signs of thyroid hormone imbalance and medication management'
      ],
      evaluation: [
        'Patient maintained clear airway without signs of obstruction',
        'Patient maintained normal calcium levels and intact voice function',
        'Patient achieved satisfactory pain relief with comfortable neck positioning',
        'Patient maintained stable thyroid function with appropriate hormone replacement'
      ]
    }
  },
  {
    id: 'total-joint-replacement',
    specialtyId: 'surgery-perioperative',
    title: 'Total Joint (Knee, Hip) Replacement',
    description: 'Joint replacement surgery nursing care plan',
    category: 'Surgery & Perioperative Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and joint manipulation',
        'Risk for dislocation related to improper positioning or movement',
        'Risk for infection related to prosthetic implant and surgical site',
        'Impaired mobility related to surgical restrictions and pain'
      ],
      goals: [
        'Patient will achieve adequate pain control to participate in rehabilitation',
        'Patient will maintain proper joint positioning without dislocation',
        'Patient will remain free from prosthetic joint infection',
        'Patient will demonstrate progressive improvement in mobility and function'
      ],
      interventions: [
        'Assess pain level every 4 hours and provide multimodal pain management',
        'Maintain proper positioning with hip abduction pillow (for hip replacement)',
        'Monitor for signs of dislocation: severe pain, leg shortening, rotation',
        'Assess surgical site for signs of infection or delayed healing',
        'Encourage early mobilization with physical therapy as ordered',
        'Monitor for signs of fat embolism: confusion, petechial rash, hypoxemia',
        'Educate patient on joint precautions and activity restrictions',
        'Coordinate with occupational therapy for adaptive equipment training'
      ],
      evaluation: [
        'Patient achieved satisfactory pain relief enabling participation in therapy',
        'Patient maintained proper joint alignment without dislocation episodes',
        'No signs of infection developed at surgical site or around prosthesis',
        'Patient demonstrated improved mobility and independence with assistive devices'
      ]
    }
  },

  // Hematologic & Lymphatic Care
  {
    id: 'anaphylactic-shock',
    specialtyId: 'hematologic-lymphatic',
    title: 'Anaphylactic Shock',
    description: 'Emergency care plan for anaphylactic reactions',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to laryngeal edema and bronchospasm',
        'Decreased cardiac output related to vasodilation and capillary leak',
        'Risk for ineffective tissue perfusion related to distributive shock',
        'Anxiety related to life-threatening allergic reaction and respiratory distress'
      ],
      goals: [
        'Patient will maintain patent airway and adequate oxygenation',
        'Patient will maintain adequate cardiac output and blood pressure',
        'Patient will achieve effective tissue perfusion to vital organs',
        'Patient will demonstrate reduced anxiety with effective coping'
      ],
      interventions: [
        'Assess airway patency and prepare for emergency intubation if needed',
        'Administer epinephrine immediately per protocol (IM or IV)',
        'Monitor vital signs continuously: blood pressure, heart rate, oxygen saturation',
        'Establish large-bore IV access and administer isotonic fluids rapidly',
        'Administer prescribed corticosteroids and H1/H2 antihistamines',
        'Monitor for biphasic reaction and continue observation for 4-6 hours',
        'Remove or discontinue exposure to allergen if identified',
        'Educate patient about allergen avoidance and emergency action plan'
      ],
      evaluation: [
        'Airway remains patent with oxygen saturation >95% on room air',
        'Blood pressure stabilized within normal range for patient',
        'Signs of adequate perfusion: normal mental status, urine output >30ml/hr',
        'Patient expresses understanding of condition and prevention strategies'
      ]
    }
  },
  {
    id: 'anemia',
    specialtyId: 'hematologic-lymphatic',
    title: 'Anemia',
    description: 'Nursing care plan for anemia management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Fatigue related to decreased oxygen-carrying capacity of blood',
        'Activity intolerance related to tissue hypoxia and weakness',
        'Imbalanced nutrition related to inadequate intake of iron, B12, or folate',
        'Risk for infection related to decreased immune function in some types of anemia'
      ],
      goals: [
        'Patient will report increased energy levels and decreased fatigue',
        'Patient will demonstrate improved activity tolerance without excessive dyspnea',
        'Patient will maintain adequate nutritional intake to support RBC production',
        'Patient will remain free from infection'
      ],
      interventions: [
        'Monitor hemoglobin and hematocrit levels and report critical values',
        'Assess for signs of anemia: pallor, fatigue, dyspnea, tachycardia',
        'Administer prescribed iron supplements, B12, or folate as ordered',
        'Encourage iron-rich foods: lean meats, dark leafy greens, fortified cereals',
        'Monitor vital signs and oxygen saturation regularly',
        'Encourage rest periods between activities to prevent excessive fatigue',
        'Educate patient on taking iron supplements with vitamin C to enhance absorption',
        'Monitor for side effects of iron therapy: constipation, GI upset'
      ],
      evaluation: [
        'Patient reports decreased fatigue and improved energy levels',
        'Activity tolerance improved with stable vital signs during exertion',
        'Laboratory values show improvement in hemoglobin and hematocrit',
        'Patient demonstrates understanding of dietary and medication management'
      ]
    }
  },
  {
    id: 'aortic-aneurysm',
    specialtyId: 'hematologic-lymphatic',
    title: 'Aortic Aneurysm',
    description: 'Nursing care plan for aortic aneurysm management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for bleeding related to aneurysm rupture and hemorrhage',
        'Acute pain related to aneurysm expansion and pressure on surrounding structures',
        'Risk for ineffective tissue perfusion related to compromised blood flow',
        'Anxiety related to life-threatening condition and surgical intervention'
      ],
      goals: [
        'Patient will not experience aneurysm rupture during stabilization period',
        'Patient will report pain controlled at acceptable levels',
        'Patient will maintain adequate tissue perfusion to vital organs',
        'Patient will demonstrate effective coping with condition and treatment plan'
      ],
      interventions: [
        'Monitor vital signs frequently for signs of rupture: hypotension, tachycardia',
        'Assess for signs of expansion: severe back/abdominal pain, pulsatile mass',
        'Maintain blood pressure control with prescribed antihypertensive medications',
        'Position patient to avoid increased intra-abdominal pressure',
        'Prepare for emergency surgery if rupture occurs: type and crossmatch blood',
        'Monitor peripheral pulses and assess for signs of compromised circulation',
        'Educate about activity restrictions and signs/symptoms to report',
        'Provide emotional support and coordinate with surgical team'
      ],
      evaluation: [
        'Patient remains hemodynamically stable without signs of rupture',
        'Pain managed effectively allowing for comfort and rest',
        'Peripheral pulses remain strong with adequate tissue perfusion',
        'Patient verbalizes understanding of condition and demonstrates reduced anxiety'
      ]
    }
  },
  {
    id: 'bleeding-risk-hemophilia',
    specialtyId: 'hematologic-lymphatic',
    title: 'Bleeding Risk & Hemophilia',
    description: 'Care plan for bleeding disorders and hemophilia',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for bleeding related to deficient clotting factors and prolonged bleeding time',
        'Risk for injury related to bleeding tendency and need for activity modifications',
        'Acute pain related to bleeding episodes and joint hemorrhage',
        'Deficient knowledge related to bleeding disorder management and prevention'
      ],
      goals: [
        'Patient will not experience major bleeding episodes or hemorrhage',
        'Patient will avoid injuries and trauma that could cause bleeding',
        'Patient will report pain controlled during bleeding episodes',
        'Patient will demonstrate understanding of condition management and safety measures'
      ],
      interventions: [
        'Monitor for signs of bleeding: petechiae, ecchymoses, prolonged bleeding',
        'Administer factor replacement therapy as prescribed during bleeding episodes',
        'Apply pressure to bleeding sites and elevate affected extremities',
        'Avoid invasive procedures when possible; use smallest gauge needles necessary',
        'Educate about activity modifications: avoid contact sports, use protective gear',
        'Monitor joint range of motion and assess for signs of hemarthrosis',
        'Coordinate with hematology specialist for factor level monitoring',
        'Educate family about emergency management and when to seek immediate care'
      ],
      evaluation: [
        'No major bleeding episodes occur during care period',
        'Patient demonstrates safe practices and avoids injury-causing activities',
        'Pain from bleeding episodes managed effectively with interventions',
        'Patient and family verbalize understanding of bleeding precautions and management'
      ]
    }
  },
  {
    id: 'deep-vein-thrombosis',
    specialtyId: 'hematologic-lymphatic',
    title: 'Deep Vein Thrombosis',
    description: 'Nursing care plan for DVT prevention and management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for pulmonary embolism related to dislodged blood clot',
        'Acute pain related to inflammation and swelling in affected extremity',
        'Impaired tissue perfusion related to venous obstruction and clot formation',
        'Risk for bleeding related to anticoagulation therapy'
      ],
      goals: [
        'Patient will not develop pulmonary embolism or clot extension',
        'Patient will report pain controlled at comfortable levels',
        'Patient will maintain adequate circulation to affected extremity',
        'Patient will not experience major bleeding complications from anticoagulation'
      ],
      interventions: [
        'Monitor for signs of pulmonary embolism: chest pain, shortness of breath, tachypnea',
        'Assess affected extremity for pain, swelling, warmth, and color changes',
        'Administer prescribed anticoagulants: heparin, warfarin, or direct oral anticoagulants',
        'Monitor coagulation studies: PTT, INR, and adjust medications accordingly',
        'Elevate affected extremity above heart level to reduce swelling',
        'Apply graduated compression stockings as prescribed',
        'Encourage early ambulation unless contraindicated',
        'Educate about bleeding precautions and signs requiring immediate medical attention'
      ],
      evaluation: [
        'No signs of pulmonary embolism or clot progression develop',
        'Pain and swelling in affected extremity significantly reduced',
        'Circulation to extremity restored with normal color and temperature',
        'Coagulation levels maintained within therapeutic range without bleeding'
      ]
    }
  },
  {
    id: 'disseminated-intravascular-coagulation',
    specialtyId: 'hematologic-lymphatic',
    title: 'Disseminated Intravascular Coagulation',
    description: 'Emergency care plan for DIC management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for bleeding related to consumption of clotting factors and platelets',
        'Risk for ineffective tissue perfusion related to microclot formation',
        'Risk for shock related to blood loss and circulatory failure',
        'Anxiety related to critical condition and life-threatening complications'
      ],
      goals: [
        'Patient will achieve hemostasis and cessation of abnormal bleeding',
        'Patient will maintain adequate tissue perfusion to vital organs',
        'Patient will maintain stable hemodynamic status without shock',
        'Patient will demonstrate reduced anxiety with effective support'
      ],
      interventions: [
        'Monitor for bleeding from multiple sites: IV sites, mucous membranes, GI tract',
        'Assess laboratory values: platelets, fibrinogen, PT/PTT, D-dimer, FDP',
        'Administer blood products as ordered: platelets, fresh frozen plasma, cryoprecipitate',
        'Monitor vital signs continuously and assess for signs of shock',
        'Treat underlying cause: infection, obstetric complications, malignancy',
        'Avoid invasive procedures unless absolutely necessary',
        'Monitor neurological status for signs of intracranial bleeding',
        'Provide emotional support to patient and family during critical care'
      ],
      evaluation: [
        'Bleeding controlled with stable coagulation parameters',
        'Adequate tissue perfusion maintained with normal organ function',
        'Hemodynamic stability achieved without signs of shock',
        'Patient and family demonstrate understanding and effective coping'
      ]
    }
  },
  {
    id: 'hemophilia',
    specialtyId: 'hematologic-lymphatic',
    title: 'Hemophilia',
    description: 'Nursing care plan for hemophilia management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for bleeding related to deficient clotting factors VIII or IX',
        'Acute pain related to joint hemorrhage and hemarthrosis',
        'Risk for injury related to prolonged bleeding tendency',
        'Deficient knowledge related to bleeding disorder management and prevention'
      ],
      goals: [
        'Patient will not experience life-threatening bleeding episodes',
        'Patient will report controlled pain during bleeding episodes',
        'Patient will avoid trauma and injuries that precipitate bleeding',
        'Patient will demonstrate understanding of condition management and safety measures'
      ],
      interventions: [
        'Monitor for signs of bleeding: prolonged bleeding, joint swelling, hematuria',
        'Administer factor VIII or IX concentrates as prescribed for bleeding episodes',
        'Apply R.I.C.E. protocol for joint bleeding: Rest, Ice, Compression, Elevation',
        'Avoid medications that affect platelet function: aspirin, NSAIDs',
        'Educate about safe physical activities and avoiding contact sports',
        'Monitor joint range of motion and assess for chronic arthropathy',
        'Coordinate with hematology specialist for prophylactic factor replacement',
        'Educate family about signs of bleeding requiring immediate medical attention'
      ],
      evaluation: [
        'No major bleeding complications occur during care period',
        'Joint pain from hemarthrosis managed effectively with treatment',
        'Patient demonstrates safe practices and injury prevention measures',
        'Patient and family verbalize understanding of bleeding precautions and emergency care'
      ]
    }
  },
  {
    id: 'kawasaki-disease',
    specialtyId: 'hematologic-lymphatic',
    title: 'Kawasaki Disease',
    description: 'Nursing care plan for Kawasaki disease in pediatric patients',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Hyperthermia related to inflammatory process and vasculitis',
        'Risk for decreased cardiac output related to coronary artery aneurysms',
        'Acute pain related to inflammation of skin, mucous membranes, and joints',
        'Risk for impaired skin integrity related to characteristic rash and peeling'
      ],
      goals: [
        'Patient will maintain normal body temperature within 24-48 hours',
        'Patient will maintain adequate cardiac output without coronary complications',
        'Patient will report pain controlled at comfortable levels',
        'Patient will maintain skin integrity without secondary infections'
      ],
      interventions: [
        'Monitor temperature every 2-4 hours and administer aspirin as prescribed',
        'Administer intravenous immunoglobulin (IVIG) within 10 days of fever onset',
        'Monitor for cardiac complications: echocardiogram, EKG changes',
        'Assess for classic signs: strawberry tongue, conjunctival injection, rash',
        'Provide comfort measures for irritability: dim lights, quiet environment',
        'Apply gentle moisturizers to prevent skin cracking and promote healing',
        'Monitor for signs of coronary artery involvement: chest pain, arrhythmias',
        'Educate family about long-term cardiac follow-up and activity restrictions'
      ],
      evaluation: [
        'Temperature returns to normal and remains stable for 48 hours',
        'Cardiac function remains stable with no evidence of coronary artery changes',
        'Child demonstrates improved comfort and decreased irritability',
        'Skin heals without secondary infection or severe peeling complications'
      ]
    }
  },
  {
    id: 'leukemia',
    specialtyId: 'hematologic-lymphatic',
    title: 'Leukemia',
    description: 'Nursing care plan for leukemia management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for infection related to immunosuppression and neutropenia',
        'Risk for bleeding related to thrombocytopenia and bone marrow suppression',
        'Fatigue related to anemia and effects of chemotherapy treatment',
        'Imbalanced nutrition related to chemotherapy side effects and poor appetite'
      ],
      goals: [
        'Patient will remain free from opportunistic infections',
        'Patient will not experience major bleeding episodes',
        'Patient will demonstrate improved energy levels and activity tolerance',
        'Patient will maintain adequate nutritional status during treatment'
      ],
      interventions: [
        'Monitor white blood cell count and absolute neutrophil count daily',
        'Implement neutropenic precautions: private room, hand hygiene, limited visitors',
        'Monitor platelet count and assess for bleeding: petechiae, bruising, bleeding gums',
        'Administer chemotherapy as prescribed and monitor for side effects',
        'Monitor vital signs and assess for signs of infection every 4 hours',
        'Encourage high-calorie, high-protein diet and nutritional supplements',
        'Coordinate with oncology team for supportive care: blood products, growth factors',
        'Provide emotional support and connect with cancer support resources'
      ],
      evaluation: [
        'No infections develop; patient maintains stable white blood cell counts',
        'Platelet levels remain adequate or bleeding episodes effectively managed',
        'Patient reports improved energy and participates in daily activities',
        'Nutritional status stable with minimal weight loss during treatment'
      ]
    }
  },
  {
    id: 'lymphoma',
    specialtyId: 'hematologic-lymphatic',
    title: 'Lymphoma',
    description: 'Nursing care plan for lymphoma management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for infection related to compromised immune system and treatment effects',
        'Fatigue related to disease process and chemotherapy treatments',
        'Imbalanced nutrition related to decreased appetite and treatment side effects',
        'Anxiety related to cancer diagnosis and uncertain prognosis'
      ],
      goals: [
        'Patient will remain free from infections during treatment period',
        'Patient will demonstrate improved energy levels and functional capacity',
        'Patient will maintain adequate nutritional status and stable weight',
        'Patient will demonstrate effective coping mechanisms and reduced anxiety'
      ],
      interventions: [
        'Monitor laboratory values: CBC with differential, liver and kidney function',
        'Implement infection prevention measures: hand hygiene, avoiding crowds',
        'Administer chemotherapy and targeted therapy as prescribed',
        'Monitor for treatment side effects: nausea, vomiting, mucositis, neuropathy',
        'Assess lymph node size and monitor for signs of tumor lysis syndrome',
        'Encourage adequate nutrition and hydration during treatment',
        'Provide emotional support and connect with oncology social services',
        'Educate about disease process, treatment plan, and side effect management'
      ],
      evaluation: [
        'No opportunistic infections occur during treatment',
        'Patient reports improved energy and ability to perform daily activities',
        'Nutritional status maintained with minimal weight loss',
        'Patient verbalizes understanding of treatment and demonstrates effective coping'
      ]
    }
  },
  {
    id: 'sepsis-septicemia',
    specialtyId: 'hematologic-lymphatic',
    title: 'Sepsis and Septicemia',
    description: 'Emergency care plan for sepsis management',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Risk for septic shock related to overwhelming systemic infection',
        'Hyperthermia or hypothermia related to infectious process and immune response',
        'Risk for organ dysfunction related to inadequate tissue perfusion',
        'Risk for DIC related to inflammatory cascade and coagulation abnormalities'
      ],
      goals: [
        'Patient will maintain hemodynamic stability without progression to septic shock',
        'Patient will achieve normal body temperature regulation',
        'Patient will maintain adequate organ function without multiple organ failure',
        'Patient will maintain normal coagulation without bleeding complications'
      ],
      interventions: [
        'Monitor vital signs continuously and assess for signs of shock',
        'Obtain blood cultures before antibiotic administration',
        'Administer broad-spectrum antibiotics within 1 hour of recognition',
        'Implement early goal-directed therapy with fluid resuscitation',
        'Monitor lactate levels and assess tissue perfusion markers',
        'Administer vasopressors if fluid resuscitation inadequate',
        'Monitor laboratory values: CBC, BUN, creatinine, liver function, coagulation',
        'Assess for source of infection and coordinate with infectious disease team'
      ],
      evaluation: [
        'Patient maintains stable blood pressure and cardiac output',
        'Temperature normalizes and inflammatory markers decrease',
        'Organ function preserved with normal urine output and mental status',
        'Coagulation studies remain normal without signs of DIC'
      ]
    }
  },
  {
    id: 'sickle-cell-crisis',
    specialtyId: 'hematologic-lymphatic',
    title: 'Sickle Cell Anemia Crisis',
    description: 'Emergency care plan for sickle cell crisis',
    category: 'Hematologic & Lymphatic Care',
    pgie: {
      problems: [
        'Acute pain related to vaso-occlusive crisis and tissue ischemia',
        'Risk for ineffective tissue perfusion related to sickling and vessel occlusion',
        'Risk for infection related to functional asplenia and immunocompromise',
        'Deficient fluid volume related to increased fluid requirements and poor intake'
      ],
      goals: [
        'Patient will report pain controlled at levels allowing for comfort and rest',
        'Patient will maintain adequate tissue perfusion without organ damage',
        'Patient will remain free from bacterial infections and complications',
        'Patient will achieve adequate hydration and fluid balance'
      ],
      interventions: [
        'Assess pain level using 0-10 scale and administer prescribed analgesics',
        'Provide adequate hydration with IV fluids to prevent further sickling',
        'Administer oxygen therapy to improve oxygen saturation >95%',
        'Monitor for complications: acute chest syndrome, stroke, splenic sequestration',
        'Maintain body temperature and avoid cold exposure to prevent vasoconstriction',
        'Monitor vital signs and neurological status for signs of complications',
        'Educate about crisis triggers: dehydration, infection, stress, cold exposure',
        'Coordinate with hematology team for comprehensive sickle cell management'
      ],
      evaluation: [
        'Pain crisis resolves with patient reporting pain levels ≤4/10',
        'No evidence of stroke, acute chest syndrome, or organ damage',
        'Patient remains afebrile without signs of bacterial infection',
        'Adequate hydration achieved with improved urine output and skin turgor'
      ]
    }
  },

  // Infectious Diseases Care
  {
    id: 'aids-hiv',
    specialtyId: 'infectious-diseases',
    title: 'Acquired Immunodeficiency Syndrome (AIDS) (HIV Positive)',
    description: 'Nursing care plan for HIV/AIDS management',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Risk for infection related to compromised immune system and low CD4 count',
        'Imbalanced nutrition related to chronic disease, medications, and opportunistic infections',
        'Fatigue related to chronic illness and medications',
        'Social isolation related to stigma and fear of disease transmission'
      ],
      goals: [
        'Patient will remain free from opportunistic infections and complications',
        'Patient will maintain adequate nutritional status and stable weight',
        'Patient will demonstrate improved energy levels and activity tolerance',
        'Patient will maintain social connections and receive appropriate support'
      ],
      interventions: [
        'Monitor CD4 count, viral load, and laboratory values regularly',
        'Administer highly active antiretroviral therapy (HAART) as prescribed',
        'Implement strict infection control measures and educate about prevention',
        'Assess nutritional status and coordinate with dietitian for meal planning',
        'Monitor for opportunistic infections: pneumonia, fungal infections, TB',
        'Provide emotional support and connect with HIV support groups',
        'Educate about safe sex practices and transmission prevention',
        'Coordinate with infectious disease specialist and case management services'
      ],
      evaluation: [
        'No opportunistic infections develop during care period',
        'Nutritional status stable with appropriate weight maintenance',
        'Patient reports improved energy and ability to perform daily activities',
        'Patient maintains social relationships and receives adequate support'
      ]
    }
  },
  {
    id: 'acute-rheumatic-fever',
    specialtyId: 'infectious-diseases',
    title: 'Acute Rheumatic Fever',
    description: 'Nursing care plan for rheumatic fever management',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Acute pain related to joint inflammation and arthritis',
        'Activity intolerance related to carditis and joint inflammation',
        'Risk for decreased cardiac output related to rheumatic carditis',
        'Hyperthermia related to inflammatory process and infection'
      ],
      goals: [
        'Patient will report pain controlled at comfortable levels for mobility',
        'Patient will gradually increase activity tolerance without cardiac compromise',
        'Patient will maintain adequate cardiac output and stable vital signs',
        'Patient will maintain normal body temperature'
      ],
      interventions: [
        'Administer prescribed anti-inflammatory medications (aspirin, NSAIDs) as ordered',
        'Monitor for signs of carditis: murmurs, chest pain, shortness of breath',
        'Implement bed rest during acute phase to reduce cardiac workload',
        'Apply heat or cold to affected joints for pain relief',
        'Monitor temperature and administer antipyretics as prescribed',
        'Assess joint pain, swelling, and range of motion regularly',
        'Educate about importance of antibiotic prophylaxis for future dental/surgical procedures',
        'Coordinate with cardiology for long-term cardiac monitoring'
      ],
      evaluation: [
        'Joint pain and inflammation significantly reduced with improved mobility',
        'Patient tolerates progressive activity without signs of cardiac distress',
        'Cardiac function remains stable without evidence of permanent damage',
        'Temperature returns to normal range and remains stable'
      ]
    }
  },
  {
    id: 'dengue-hemorrhagic-fever',
    specialtyId: 'infectious-diseases',
    title: 'Dengue Hemorrhagic Fever',
    description: 'Nursing care plan for dengue fever management',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Risk for hypovolemic shock related to plasma leakage and bleeding',
        'Hyperthermia related to viral infection and inflammatory response',
        'Risk for bleeding related to thrombocytopenia and capillary fragility',
        'Acute pain related to headache, muscle aches, and abdominal pain'
      ],
      goals: [
        'Patient will not develop dengue shock syndrome or severe complications',
        'Patient will maintain normal body temperature',
        'Patient will not experience significant bleeding episodes',
        'Patient will report pain controlled at manageable levels'
      ],
      interventions: [
        'Monitor vital signs every 2-4 hours for signs of shock: hypotension, tachycardia',
        'Assess for plasma leakage: pleural effusion, ascites, hemoconcentration',
        'Monitor platelet count and assess for bleeding: petechiae, bruising, bleeding gums',
        'Maintain strict fluid balance monitoring with intake and output',
        'Administer IV fluids carefully to prevent fluid overload',
        'Avoid aspirin and NSAIDs due to bleeding risk',
        'Implement bleeding precautions: soft toothbrush, gentle handling',
        'Monitor for warning signs: severe abdominal pain, persistent vomiting, rapid pulse'
      ],
      evaluation: [
        'Patient maintains hemodynamic stability without progression to shock',
        'Temperature controlled with supportive measures',
        'No significant bleeding complications develop',
        'Pain managed effectively with safe analgesics (acetaminophen)'
      ]
    }
  },
  {
    id: 'herpes-zoster',
    specialtyId: 'infectious-diseases',
    title: 'Herpes Zoster (Shingles)',
    description: 'Nursing care plan for shingles management',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Acute pain related to viral inflammation of nerve pathways',
        'Risk for transmission of infection related to active viral shedding',
        'Impaired skin integrity related to vesicular lesions',
        'Risk for postherpetic neuralgia related to nerve damage'
      ],
      goals: [
        'Patient will report pain controlled at tolerable levels for daily functioning',
        'Patient will not transmit infection to susceptible individuals',
        'Patient will demonstrate healing of skin lesions without secondary infection',
        'Patient will not develop postherpetic neuralgia complications'
      ],
      interventions: [
        'Administer prescribed antiviral medications within 72 hours of onset',
        'Assess pain level using 0-10 scale and provide appropriate pain management',
        'Implement contact precautions until lesions crust over completely',
        'Educate about avoiding contact with pregnant women and immunocompromised individuals',
        'Apply cool, moist compresses to lesions for comfort',
        'Monitor for complications: bacterial superinfection, eye involvement',
        'Provide emotional support as pain and appearance may cause distress',
        'Educate about potential for postherpetic neuralgia and early intervention'
      ],
      evaluation: [
        'Pain significantly reduced allowing for improved quality of life',
        'No transmission of infection to family members or healthcare workers',
        'Lesions heal without signs of secondary bacterial infection',
        'Patient receives early intervention to prevent chronic pain complications'
      ]
    }
  },
  {
    id: 'influenza-infectious',
    specialtyId: 'infectious-diseases',
    title: 'Influenza (Flu)',
    description: 'Nursing care plan for influenza management',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Hyperthermia related to viral infection and inflammatory response',
        'Risk for transmission of infection related to respiratory droplets',
        'Fatigue related to viral illness and body\'s immune response',
        'Risk for complications related to secondary bacterial infections'
      ],
      goals: [
        'Patient will maintain normal body temperature',
        'Patient will not transmit infection to others',
        'Patient will demonstrate improved energy levels and activity tolerance',
        'Patient will not develop secondary complications such as pneumonia'
      ],
      interventions: [
        'Monitor temperature every 4 hours and administer antipyretics as prescribed',
        'Implement droplet precautions and educate about respiratory hygiene',
        'Encourage adequate rest and gradual return to activities',
        'Administer antiviral medications if prescribed within 48 hours of onset',
        'Monitor for complications: shortness of breath, persistent fever, dehydration',
        'Encourage adequate fluid intake to prevent dehydration',
        'Provide comfort measures: humidified air, throat lozenges, warm saltwater gargles',
        'Educate about completing antiviral course and when to seek medical attention'
      ],
      evaluation: [
        'Temperature returns to normal range and remains stable',
        'No secondary transmission to family members or contacts',
        'Patient reports improved energy and ability to perform daily activities',
        'No signs of secondary bacterial infections or complications develop'
      ]
    }
  },
  {
    id: 'pulmonary-tuberculosis-infectious',
    specialtyId: 'infectious-diseases',
    title: 'Pulmonary Tuberculosis',
    description: 'Nursing care plan for tuberculosis management',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Risk for infection transmission related to airborne pathogen spread',
        'Ineffective airway clearance related to increased pulmonary secretions',
        'Imbalanced nutrition related to decreased appetite and weight loss',
        'Deficient knowledge related to disease transmission and medication compliance'
      ],
      goals: [
        'Patient will not transmit infection to others during treatment',
        'Patient will maintain clear airway with effective cough and expectoration',
        'Patient will maintain adequate nutritional status and stable weight',
        'Patient will demonstrate understanding of disease management and medication compliance'
      ],
      interventions: [
        'Implement airborne isolation precautions with negative pressure room',
        'Ensure patient wears mask when outside isolation room',
        'Administer anti-tuberculosis medications as prescribed (RIPE therapy)',
        'Monitor for medication side effects and drug interactions',
        'Encourage high-calorie, high-protein diet and nutritional supplements',
        'Provide education on medication compliance and directly observed therapy (DOT)',
        'Monitor sputum cultures for response to treatment and infectivity',
        'Coordinate with public health for contact tracing and follow-up'
      ],
      evaluation: [
        'No secondary infections or transmission events documented',
        'Sputum production decreased with improved airway clearance',
        'Nutritional status stable with appropriate weight gain',
        'Patient demonstrates understanding and compliance with medication regimen'
      ]
    }
  },
  {
    id: 'risk-for-infection',
    specialtyId: 'infectious-diseases',
    title: 'Risk for Infection & Infection Control',
    description: 'Nursing care plan for infection prevention and control',
    category: 'Infectious Diseases Care',
    pgie: {
      problems: [
        'Risk for infection related to compromised immune system or invasive procedures',
        'Deficient knowledge related to infection prevention measures',
        'Risk for transmission of infection related to inadequate precautions',
        'Risk for healthcare-associated infections related to hospitalization'
      ],
      goals: [
        'Patient will remain free from infections during care period',
        'Patient will demonstrate understanding of infection prevention strategies',
        'Patient will not transmit infections to others',
        'Patient will not acquire healthcare-associated infections'
      ],
      interventions: [
        'Implement appropriate isolation precautions based on infection type',
        'Perform hand hygiene before and after all patient contact',
        'Use personal protective equipment (PPE) appropriately',
        'Monitor for signs of infection: fever, elevated WBC, wound changes',
        'Educate patient and family about infection prevention measures',
        'Maintain aseptic technique for invasive procedures',
        'Monitor and rotate invasive device sites per protocol',
        'Coordinate with infection control team for outbreak prevention'
      ],
      evaluation: [
        'No healthcare-associated infections develop during hospitalization',
        'Patient and family demonstrate proper hand hygiene and precautions',
        'No transmission of infection to other patients or staff',
        'Infection prevention protocols followed consistently by all staff'
      ]
    }
  },

  // Integumentary Care
  {
    id: 'burn-injury',
    specialtyId: 'integumentary-care',
    title: 'Burn Injury',
    description: 'Nursing care plan for burn injury management',
    category: 'Integumentary Care',
    pgie: {
      problems: [
        'Impaired skin integrity related to thermal injury and tissue destruction',
        'Acute pain related to exposed nerve endings and tissue damage',
        'Risk for infection related to loss of skin barrier and open wounds',
        'Deficient fluid volume related to increased capillary permeability and fluid loss'
      ],
      goals: [
        'Patient will demonstrate progressive healing of burn wounds without complications',
        'Patient will report pain controlled at acceptable levels for comfort and mobility',
        'Patient will remain free from wound infections and sepsis',
        'Patient will maintain adequate fluid balance and hemodynamic stability'
      ],
      interventions: [
        'Assess burn depth, size, and location using rule of nines and burn diagrams',
        'Maintain sterile technique during wound care and dressing changes',
        'Administer prescribed pain medications before procedures and as needed',
        'Monitor fluid intake and output, daily weights, and electrolyte levels',
        'Apply prescribed topical antimicrobials and dressings per protocol',
        'Position extremities to prevent contractures and maintain function',
        'Provide high-calorie, high-protein nutrition to support healing',
        'Monitor for signs of infection: fever, increased wound drainage, odor'
      ],
      evaluation: [
        'Burn wounds show signs of healing with granulation tissue and epithelialization',
        'Pain managed effectively allowing for participation in care and mobility',
        'No signs of wound infection or systemic sepsis develop',
        'Fluid balance maintained with stable vital signs and adequate urine output'
      ]
    }
  },
  {
    id: 'dermatitis',
    specialtyId: 'integumentary-care',
    title: 'Dermatitis',
    description: 'Nursing care plan for dermatitis management',
    category: 'Integumentary Care',
    pgie: {
      problems: [
        'Impaired skin integrity related to inflammatory skin condition and scratching',
        'Acute pain related to skin irritation, burning, and itching sensations',
        'Risk for infection related to broken skin from scratching',
        'Disturbed body image related to visible skin lesions and appearance changes'
      ],
      goals: [
        'Patient will demonstrate healing of skin lesions without secondary infection',
        'Patient will report reduced pain and itching with improved comfort',
        'Patient will not develop secondary bacterial infections from scratching',
        'Patient will express improved body image and self-confidence'
      ],
      interventions: [
        'Assess skin condition: location, appearance, drainage, and response to treatment',
        'Apply prescribed topical medications: corticosteroids, moisturizers, barriers',
        'Educate about avoiding known irritants and allergens',
        'Recommend gentle skin care products and lukewarm water for cleansing',
        'Encourage short fingernails and cotton gloves to minimize scratching damage',
        'Provide cool compresses and oatmeal baths for itch relief',
        'Monitor for signs of secondary infection: increased redness, purulent drainage',
        'Educate about chronic nature of condition and long-term management strategies'
      ],
      evaluation: [
        'Skin lesions show improvement with reduced inflammation and healing',
        'Patient reports decreased itching and improved comfort levels',
        'No signs of secondary bacterial infection develop',
        'Patient demonstrates improved coping with appearance and condition management'
      ]
    }
  },
  {
    id: 'herpes-zoster-integumentary',
    specialtyId: 'integumentary-care',
    title: 'Herpes Zoster (Shingles)',
    description: 'Skin care plan for shingles management',
    category: 'Integumentary Care',
    pgie: {
      problems: [
        'Acute pain related to viral inflammation of nerve pathways',
        'Impaired skin integrity related to vesicular lesions and potential secondary infection',
        'Risk for transmission of infection related to active viral shedding',
        'Activity intolerance related to severe pain and discomfort'
      ],
      goals: [
        'Patient will report pain controlled at tolerable levels for daily functioning',
        'Patient will demonstrate healing of skin lesions without secondary infection',
        'Patient will not transmit infection to susceptible individuals',
        'Patient will maintain optimal activity level within pain limitations'
      ],
      interventions: [
        'Assess pain level using 0-10 scale and administer prescribed antivirals and analgesics',
        'Apply cool, moist compresses to lesions for pain relief and prevent crusting',
        'Keep lesions clean and dry, avoid breaking vesicles to prevent secondary infection',
        'Implement contact precautions until lesions crust over completely',
        'Educate about avoiding contact with pregnant women and immunocompromised individuals',
        'Monitor for complications: bacterial superinfection, postherpetic neuralgia',
        'Provide emotional support as pain and appearance may cause distress',
        'Coordinate with pain management specialists for severe or persistent pain'
      ],
      evaluation: [
        'Pain significantly reduced allowing for improved quality of life',
        'Lesions heal without signs of secondary bacterial infection',
        'No transmission of infection to family members or healthcare workers',
        'Patient resumes normal activities as pain allows and energy permits'
      ]
    }
  },
  {
    id: 'pressure-ulcer',
    specialtyId: 'integumentary-care',
    title: 'Pressure Ulcer (Bedsores)',
    description: 'Nursing care plan for pressure ulcer prevention and treatment',
    category: 'Integumentary Care',
    pgie: {
      problems: [
        'Impaired skin integrity related to prolonged pressure and shearing forces',
        'Risk for infection related to open wounds and compromised skin barrier',
        'Acute pain related to tissue damage and wound care procedures',
        'Impaired physical mobility related to pain and positioning restrictions'
      ],
      goals: [
        'Patient will demonstrate healing of existing pressure ulcers without complications',
        'Patient will not develop additional pressure ulcers during care',
        'Patient will remain free from wound infections',
        'Patient will maintain optimal mobility within therapeutic restrictions'
      ],
      interventions: [
        'Assess skin integrity every shift using Braden scale for pressure ulcer risk',
        'Implement turning schedule every 2 hours and position changes',
        'Use pressure-relieving devices: specialized mattresses, cushions, heel protectors',
        'Clean wounds with normal saline and apply prescribed dressings',
        'Maintain adequate nutrition with protein and vitamin C for wound healing',
        'Monitor for signs of infection: increased drainage, odor, surrounding erythema',
        'Educate patient and family about pressure ulcer prevention strategies',
        'Coordinate with wound care specialist for complex or non-healing ulcers'
      ],
      evaluation: [
        'Existing pressure ulcers show signs of healing with granulation tissue',
        'No new pressure ulcers develop during hospitalization or care period',
        'Wounds remain clean without signs of infection or systemic complications',
        'Patient tolerates position changes and maintains skin integrity'
      ]
    }
  },
  {
    id: 'wound-care',
    specialtyId: 'integumentary-care',
    title: 'Wound Care and Skin/Tissue Integrity',
    description: 'Comprehensive wound care and skin integrity nursing plan',
    category: 'Integumentary Care',
    pgie: {
      problems: [
        'Impaired skin integrity related to trauma, surgery, or underlying pathology',
        'Risk for infection related to broken skin barrier and wound contamination',
        'Delayed wound healing related to poor nutrition, circulation, or medical conditions',
        'Acute pain related to tissue damage and wound care procedures'
      ],
      goals: [
        'Patient will demonstrate progressive wound healing without complications',
        'Patient will remain free from wound infections and systemic sepsis',
        'Patient will achieve optimal wound healing within expected timeframe',
        'Patient will report manageable pain during wound care procedures'
      ],
      interventions: [
        'Assess wound characteristics: size, depth, drainage, surrounding skin condition',
        'Maintain sterile technique during wound care and dressing changes',
        'Clean wound with appropriate solution and apply prescribed dressings',
        'Monitor for signs of infection: increased drainage, odor, erythema, fever',
        'Ensure adequate nutrition with protein, vitamins, and minerals for healing',
        'Protect periwound skin with barrier creams or protective dressings',
        'Educate patient and family about proper wound care techniques',
        'Document wound progress with photographs and measurements as appropriate'
      ],
      evaluation: [
        'Wound demonstrates progressive healing with decreased size and improved appearance',
        'No signs of local or systemic infection develop',
        'Wound healing proceeds according to expected timeline for wound type',
        'Patient tolerates wound care with acceptable pain levels'
      ]
    }
  },

  // Maternal & Newborn Care
  {
    id: 'abortion',
    specialtyId: 'maternal-newborn',
    title: 'Abortion (Termination of Pregnancy)',
    description: 'Nursing care plan for pregnancy termination',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for bleeding related to uterine procedure and incomplete evacuation',
        'Acute pain related to uterine contractions and cervical dilation',
        'Risk for infection related to invasive procedure and open cervix',
        'Grieving related to pregnancy loss and emotional response'
      ],
      goals: [
        'Patient will not experience excessive bleeding or hemorrhage',
        'Patient will report pain controlled at acceptable level (≤4/10)',
        'Patient will remain free from postprocedural infections',
        'Patient will demonstrate healthy grieving process and emotional support'
      ],
      interventions: [
        'Monitor vital signs and assess for signs of hemorrhage every 15 minutes initially',
        'Assess vaginal bleeding: amount, color, clots, and report excessive bleeding',
        'Administer prescribed analgesics and assess pain relief effectiveness',
        'Monitor temperature and signs of infection every 4 hours',
        'Provide emotional support and validate patient\'s feelings without judgment',
        'Educate about expected bleeding, cramping, and warning signs',
        'Offer contraceptive counseling and family planning information',
        'Coordinate with social services and counseling services as needed'
      ],
      evaluation: [
        'Vaginal bleeding remains within normal limits without signs of hemorrhage',
        'Pain adequately controlled with medications and comfort measures',
        'No signs of infection: normal temperature, no foul discharge',
        'Patient expresses feelings appropriately and utilizes support systems'
      ]
    }
  },
  {
    id: 'cervical-insufficiency',
    specialtyId: 'maternal-newborn',
    title: 'Cervical Insufficiency (Premature Dilation of the Cervix)',
    description: 'Care plan for cervical insufficiency during pregnancy',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for preterm birth related to cervical incompetence',
        'Anxiety related to risk of pregnancy loss and bed rest restrictions',
        'Activity intolerance related to prescribed activity restrictions',
        'Risk for infection related to cervical cerclage procedure'
      ],
      goals: [
        'Patient will maintain pregnancy to term or near term',
        'Patient will demonstrate effective coping with activity restrictions',
        'Patient will maintain appropriate activity level within prescribed limitations',
        'Patient will remain free from infections related to cerclage'
      ],
      interventions: [
        'Monitor for signs of preterm labor: contractions, cervical changes, bleeding',
        'Assess cervical length via ultrasound as prescribed',
        'Educate about activity restrictions and importance of bed rest',
        'Monitor vital signs and fetal heart rate regularly',
        'Assess for signs of infection: fever, unusual discharge, pelvic pressure',
        'Provide emotional support and counseling about pregnancy concerns',
        'Coordinate care with perinatologist for specialized management',
        'Educate about signs requiring immediate medical attention'
      ],
      evaluation: [
        'Pregnancy progresses without preterm labor or delivery',
        'Patient demonstrates understanding and compliance with restrictions',
        'Patient maintains appropriate activity level without complications',
        'No signs of infection at cerclage site or systemically'
      ]
    }
  },
  {
    id: 'cesarean-birth',
    specialtyId: 'maternal-newborn',
    title: 'Cesarean Birth',
    description: 'Nursing care plan for cesarean section delivery',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and uterine contractions',
        'Risk for infection related to surgical wound and invasive procedure',
        'Impaired physical mobility related to surgical procedure and pain',
        'Risk for ineffective breastfeeding related to delayed skin-to-skin contact'
      ],
      goals: [
        'Patient will report pain controlled at acceptable level (≤4/10)',
        'Patient will not develop postoperative infections',
        'Patient will demonstrate progressive improvement in mobility',
        'Patient will establish successful breastfeeding if desired'
      ],
      interventions: [
        'Assess pain level using 0-10 scale and administer analgesics as prescribed',
        'Monitor surgical incision for signs of infection, dehiscence, or bleeding',
        'Encourage early ambulation as ordered to prevent complications',
        'Assist with breastfeeding positioning and provide lactation support',
        'Monitor vital signs and fundal height every 4 hours',
        'Assess lochia for amount, color, and odor',
        'Provide education on incision care and activity restrictions',
        'Monitor for signs of postoperative complications: thrombosis, pneumonia'
      ],
      evaluation: [
        'Pain adequately controlled allowing for comfort and mobility',
        'Surgical incision heals without signs of infection or complications',
        'Patient ambulates safely with progressive improvement in activity',
        'Breastfeeding established successfully with adequate infant nutrition'
      ]
    }
  },
  {
    id: 'cleft-palate-lip',
    specialtyId: 'maternal-newborn',
    title: 'Cleft Palate and Cleft Lip',
    description: 'Nursing care plan for cleft lip and palate',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Ineffective infant feeding pattern related to anatomical defect',
        'Risk for aspiration related to impaired sucking and swallowing',
        'Risk for altered parent-infant attachment related to visible defect',
        'Risk for infection related to surgical repair sites'
      ],
      goals: [
        'Infant will maintain adequate nutrition and weight gain',
        'Infant will not experience aspiration during feeding',
        'Parents will demonstrate bonding and attachment behaviors',
        'Infant will heal from surgical repairs without complications'
      ],
      interventions: [
        'Use specialized feeding equipment: cleft palate nipples, squeezable bottles',
        'Position infant upright during feeding to prevent aspiration',
        'Monitor weight gain and nutritional status regularly',
        'Burp infant frequently during feeding to prevent air swallowing',
        'Provide emotional support and education to parents about condition',
        'Coordinate care with plastic surgery and speech therapy teams',
        'Protect surgical sites from trauma and monitor for healing',
        'Educate parents on feeding techniques and postoperative care'
      ],
      evaluation: [
        'Infant demonstrates adequate weight gain and nutritional intake',
        'No episodes of aspiration or respiratory distress during feeding',
        'Parents show appropriate bonding behaviors and express confidence in care',
        'Surgical sites heal appropriately without signs of infection or dehiscence'
      ]
    }
  },
  {
    id: 'gestational-diabetes-maternal',
    specialtyId: 'maternal-newborn',
    title: 'Gestational Diabetes Mellitus',
    description: 'Maternal care plan for gestational diabetes',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for unstable blood glucose related to insulin resistance in pregnancy',
        'Risk for fetal macrosomia related to maternal hyperglycemia',
        'Deficient knowledge related to diabetes management during pregnancy',
        'Anxiety related to potential complications for mother and baby'
      ],
      goals: [
        'Patient will maintain blood glucose levels within target range',
        'Patient will deliver infant of appropriate size without complications',
        'Patient will demonstrate understanding of diabetes management',
        'Patient will express reduced anxiety about pregnancy outcomes'
      ],
      interventions: [
        'Monitor blood glucose levels as prescribed: fasting and postprandial',
        'Provide education on diabetic diet and carbohydrate counting',
        'Teach self-monitoring of blood glucose and record keeping',
        'Coordinate with dietitian for meal planning and nutrition counseling',
        'Monitor fetal growth via ultrasound and non-stress tests',
        'Assess for signs of diabetic complications: polyuria, polydipsia, fatigue',
        'Administer insulin therapy if prescribed and teach injection technique',
        'Educate about potential complications and when to seek medical care'
      ],
      evaluation: [
        'Blood glucose levels maintained within target ranges throughout pregnancy',
        'Fetal growth appropriate for gestational age without macrosomia',
        'Patient demonstrates competent self-management of diabetes',
        'Patient expresses confidence and reduced anxiety about pregnancy outcome'
      ]
    }
  },
  {
    id: 'hyperbilirubinemia',
    specialtyId: 'maternal-newborn',
    title: 'Hyperbilirubinemia (Jaundice)',
    description: 'Newborn care plan for jaundice management',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for injury related to elevated bilirubin levels and potential kernicterus',
        'Ineffective thermoregulation related to phototherapy treatment',
        'Risk for deficient fluid volume related to increased insensible water loss',
        'Risk for impaired parent-infant attachment related to treatment separation'
      ],
      goals: [
        'Infant will not develop complications from hyperbilirubinemia',
        'Infant will maintain normal body temperature during treatment',
        'Infant will maintain adequate hydration and fluid balance',
        'Parents will maintain bonding and attachment during treatment'
      ],
      interventions: [
        'Monitor serum bilirubin levels and plot on hour-specific nomogram',
        'Initiate phototherapy as prescribed and ensure proper positioning',
        'Protect infant\'s eyes during phototherapy with appropriate eye patches',
        'Monitor temperature every 2-4 hours and adjust incubator as needed',
        'Assess skin condition and turn infant every 2 hours for even exposure',
        'Monitor intake and output and encourage frequent feeding',
        'Facilitate parent visits and encourage participation in care',
        'Educate parents about jaundice, treatment, and home monitoring'
      ],
      evaluation: [
        'Bilirubin levels decrease to safe range without neurological complications',
        'Infant maintains normothermia during phototherapy treatment',
        'Infant maintains adequate hydration with good urine output',
        'Parents demonstrate bonding behaviors and confidence in infant care'
      ]
    }
  },
  {
    id: 'labor-stages',
    specialtyId: 'maternal-newborn',
    title: 'Labor Stages, Induced, Augmented, Dysfunctional, Precipitous Labor',
    description: 'Comprehensive labor and delivery nursing care plan',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Acute pain related to uterine contractions and cervical dilation',
        'Risk for maternal or fetal injury related to labor complications',
        'Anxiety related to labor process and birth outcome',
        'Risk for ineffective coping related to prolonged or precipitous labor'
      ],
      goals: [
        'Patient will experience manageable pain during labor and delivery',
        'Patient and fetus will remain safe throughout labor process',
        'Patient will demonstrate effective coping with labor process',
        'Patient will have positive birth experience with optimal outcomes'
      ],
      interventions: [
        'Monitor fetal heart rate continuously and assess for signs of distress',
        'Assess maternal vital signs, contractions, and cervical progress regularly',
        'Provide comfort measures: position changes, massage, breathing techniques',
        'Administer pain relief as requested: epidural, IV medications',
        'Monitor for complications: hemorrhage, cord prolapse, fetal distress',
        'Provide emotional support and encouragement throughout labor',
        'Facilitate family presence and participation as desired',
        'Prepare for delivery and immediate newborn care'
      ],
      evaluation: [
        'Pain managed effectively with patient satisfaction with comfort measures',
        'Maternal and fetal vital signs remain stable throughout labor',
        'Patient demonstrates effective coping and participation in birth process',
        'Delivery accomplished safely with healthy mother and infant outcomes'
      ]
    }
  },
  {
    id: 'neonatal-sepsis',
    specialtyId: 'maternal-newborn',
    title: 'Neonatal Sepsis',
    description: 'Nursing care plan for newborn sepsis',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for infection related to immature immune system and bacterial exposure',
        'Ineffective thermoregulation related to sepsis and metabolic demands',
        'Risk for impaired gas exchange related to respiratory complications',
        'Risk for deficient fluid volume related to increased metabolic demands'
      ],
      goals: [
        'Infant will not develop systemic infection or septic complications',
        'Infant will maintain normal body temperature',
        'Infant will maintain adequate oxygenation and ventilation',
        'Infant will maintain adequate fluid balance and perfusion'
      ],
      interventions: [
        'Monitor for early signs of sepsis: temperature instability, feeding intolerance',
        'Obtain blood cultures and other specimens as ordered before antibiotic therapy',
        'Administer antibiotics as prescribed and monitor for effectiveness',
        'Monitor vital signs every 2-4 hours and assess for changes',
        'Maintain strict aseptic technique for all procedures and care',
        'Monitor respiratory status and provide oxygen support as needed',
        'Assess feeding tolerance and provide nutritional support',
        'Provide family support and education about neonatal sepsis'
      ],
      evaluation: [
        'Blood cultures negative and infant shows no signs of systemic infection',
        'Infant maintains normothermia with stable temperature regulation',
        'Respiratory status stable with adequate oxygen saturation',
        'Infant maintains good perfusion with adequate urine output and feeding tolerance'
      ]
    }
  },
  {
    id: 'perinatal-loss',
    specialtyId: 'maternal-newborn',
    title: 'Perinatal Loss (Miscarriage, Stillbirth)',
    description: 'Nursing care plan for perinatal loss and grief support',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Grieving related to loss of expected infant and future plans',
        'Risk for complicated grieving related to traumatic nature of loss',
        'Risk for bleeding related to incomplete evacuation or retained products',
        'Spiritual distress related to questioning of beliefs and meaning'
      ],
      goals: [
        'Patient will demonstrate healthy grieving process and expression of feelings',
        'Patient will not develop complicated or prolonged grief reactions',
        'Patient will not experience excessive bleeding or hemorrhage',
        'Patient will find meaning and hope despite the loss experience'
      ],
      interventions: [
        'Provide compassionate presence and validate the reality of the loss',
        'Offer opportunities for the family to see and hold the baby if desired',
        'Create memory items: photos, footprints, locks of hair, blanket',
        'Monitor for excessive bleeding and assess uterine involution',
        'Coordinate with chaplain or spiritual care services as requested',
        'Provide information about support groups and grief counseling',
        'Educate about physical recovery and when to seek medical care',
        'Discuss future pregnancy concerns and family planning when appropriate'
      ],
      evaluation: [
        'Patient expresses feelings appropriately and begins healthy grieving process',
        'Patient shows signs of gradual adaptation to loss without complicated grief',
        'Physical recovery proceeds normally without bleeding complications',
        'Patient expresses some sense of hope and meaning despite the loss'
      ]
    }
  },
  {
    id: 'placental-abruption',
    specialtyId: 'maternal-newborn',
    title: 'Placental Abruption',
    description: 'Emergency care plan for placental abruption',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for maternal hemorrhage related to premature placental separation',
        'Risk for fetal hypoxia related to decreased placental perfusion',
        'Acute pain related to uterine irritability and bleeding',
        'Anxiety related to emergency situation and fetal well-being concerns'
      ],
      goals: [
        'Patient will not experience life-threatening hemorrhage',
        'Fetus will maintain adequate oxygenation and survive without complications',
        'Patient will report pain controlled at acceptable levels',
        'Patient will demonstrate understanding of condition and treatment plan'
      ],
      interventions: [
        'Monitor vital signs every 15 minutes and assess for signs of shock',
        'Assess fundal height and uterine tone continuously',
        'Monitor fetal heart rate continuously for signs of distress',
        'Establish large-bore IV access and prepare for blood transfusion',
        'Assess vaginal bleeding: amount, color, presence of clots',
        'Prepare for emergency cesarean delivery if indicated',
        'Administer prescribed medications: tocolytics, corticosteroids',
        'Provide emotional support and update family on maternal-fetal status'
      ],
      evaluation: [
        'Maternal vital signs remain stable without signs of hemorrhagic shock',
        'Fetal heart rate remains reassuring with adequate oxygenation',
        'Pain managed effectively with maternal comfort maintained',
        'Patient and family demonstrate understanding of emergency interventions needed'
      ]
    }
  },
  {
    id: 'placenta-previa',
    specialtyId: 'maternal-newborn',
    title: 'Placenta Previa',
    description: 'Nursing care plan for placenta previa management',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for maternal hemorrhage related to abnormal placental implantation',
        'Activity intolerance related to prescribed bed rest and activity restrictions',
        'Anxiety related to bleeding episodes and pregnancy complications',
        'Risk for preterm delivery related to bleeding and need for early delivery'
      ],
      goals: [
        'Patient will not experience life-threatening hemorrhage during pregnancy',
        'Patient will comply with activity restrictions to prevent bleeding',
        'Patient will demonstrate effective coping with pregnancy limitations',
        'Patient will carry pregnancy as close to term as safely possible'
      ],
      interventions: [
        'Monitor for bleeding episodes: amount, color, associated symptoms',
        'Maintain strict bed rest or modified activity as prescribed',
        'Avoid vaginal examinations and intercourse completely',
        'Monitor fetal well-being with regular non-stress tests',
        'Assess maternal vital signs every 4 hours for signs of bleeding',
        'Prepare for emergency delivery and blood transfusion if needed',
        'Provide education about warning signs requiring immediate care',
        'Coordinate with perinatologist for specialized management'
      ],
      evaluation: [
        'No significant bleeding episodes occur during pregnancy management',
        'Patient maintains compliance with activity restrictions',
        'Patient expresses understanding and copes effectively with limitations',
        'Pregnancy maintained to optimal gestational age for delivery'
      ]
    }
  },
  {
    id: 'postpartum-hemorrhage',
    specialtyId: 'maternal-newborn',
    title: 'Postpartum Hemorrhage',
    description: 'Emergency care plan for postpartum bleeding',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for hypovolemic shock related to excessive blood loss',
        'Deficient fluid volume related to hemorrhage and inadequate replacement',
        'Risk for infection related to retained placental products or trauma',
        'Anxiety related to life-threatening bleeding episode'
      ],
      goals: [
        'Patient will not develop hypovolemic shock or cardiovascular collapse',
        'Patient will maintain adequate fluid volume and hemodynamic stability',
        'Patient will not develop postpartum infections',
        'Patient will demonstrate understanding of treatment and recovery process'
      ],
      interventions: [
        'Monitor vital signs every 15 minutes and assess for signs of shock',
        'Quantify blood loss accurately using weighing pads and visual estimation',
        'Massage uterine fundus and assess for firmness and position',
        'Administer prescribed uterotonics: oxytocin, methylergonovine, misoprostol',
        'Establish large-bore IV access and prepare for blood transfusion',
        'Monitor laboratory values: CBC, coagulation studies, type and crossmatch',
        'Prepare for surgical interventions if conservative measures fail',
        'Provide emotional support and keep family informed of status'
      ],
      evaluation: [
        'Vital signs stabilize and patient shows no signs of shock',
        'Bleeding controlled and uterine tone improved',
        'Hemoglobin and hematocrit levels stabilize within acceptable ranges',
        'Patient expresses understanding of treatment and recovery expectations'
      ]
    }
  },
  {
    id: 'postpartum-thrombophlebitis',
    specialtyId: 'maternal-newborn',
    title: 'Postpartum Thrombophlebitis',
    description: 'Care plan for postpartum blood clots',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for pulmonary embolism related to deep vein thrombosis',
        'Acute pain related to venous inflammation and thrombosis',
        'Impaired physical mobility related to pain and prescribed bed rest',
        'Risk for bleeding related to anticoagulant therapy'
      ],
      goals: [
        'Patient will not develop pulmonary embolism or other embolic complications',
        'Patient will report pain controlled at acceptable levels',
        'Patient will maintain optimal mobility within therapeutic restrictions',
        'Patient will not experience bleeding complications from anticoagulation'
      ],
      interventions: [
        'Monitor for signs of pulmonary embolism: dyspnea, chest pain, tachycardia',
        'Assess affected extremity for warmth, swelling, pain, and Homan\'s sign',
        'Administer prescribed anticoagulants and monitor PT/INR or aPTT',
        'Encourage bed rest with affected leg elevated above heart level',
        'Apply warm, moist compresses to affected area as ordered',
        'Monitor for signs of bleeding: bruising, hematuria, excessive bleeding',
        'Educate about anticoagulation therapy and monitoring requirements',
        'Gradually increase activity as symptoms improve and ordered by physician'
      ],
      evaluation: [
        'No signs or symptoms of pulmonary embolism develop',
        'Pain and swelling in affected extremity decreased',
        'Patient demonstrates understanding of activity restrictions and gradually increases mobility',
        'No bleeding complications occur during anticoagulation therapy'
      ]
    }
  },
  {
    id: 'prenatal-hemorrhage',
    specialtyId: 'maternal-newborn',
    title: 'Prenatal Hemorrhage',
    description: 'Emergency care plan for prenatal bleeding',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for maternal hypovolemia related to blood loss',
        'Risk for fetal compromise related to decreased placental perfusion',
        'Anxiety related to bleeding and concern for pregnancy outcome',
        'Risk for preterm labor related to uterine irritability from bleeding'
      ],
      goals: [
        'Patient will maintain adequate blood volume and hemodynamic stability',
        'Fetus will maintain adequate oxygenation and normal heart rate patterns',
        'Patient will demonstrate effective coping with pregnancy complications',
        'Patient will not experience preterm labor contractions'
      ],
      interventions: [
        'Monitor vital signs every 15-30 minutes and assess for signs of shock',
        'Assess amount and characteristics of vaginal bleeding continuously',
        'Monitor fetal heart rate continuously for signs of distress',
        'Avoid vaginal examinations until placental location determined',
        'Establish IV access and prepare for fluid resuscitation',
        'Position patient in left lateral position to improve placental perfusion',
        'Coordinate with physician for ultrasound and diagnostic testing',
        'Prepare for emergency delivery if maternal or fetal compromise occurs'
      ],
      evaluation: [
        'Maternal vital signs remain stable without signs of hypovolemia',
        'Fetal heart rate remains reassuring with no signs of distress',
        'Patient verbalizes understanding of condition and expresses manageable anxiety',
        'No uterine contractions or signs of preterm labor develop'
      ]
    }
  },
  {
    id: 'preeclampsia',
    specialtyId: 'maternal-newborn',
    title: 'Preeclampsia and Gestational Hypertension',
    description: 'Nursing care plan for preeclampsia management',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for seizures related to severe hypertension and cerebral edema',
        'Risk for maternal organ damage related to hypertensive crisis',
        'Risk for fetal growth restriction related to decreased placental perfusion',
        'Excess fluid volume related to sodium retention and decreased glomerular filtration'
      ],
      goals: [
        'Patient will not experience seizure activity or eclampsia',
        'Patient will maintain stable blood pressure without organ damage',
        'Fetus will maintain adequate growth and oxygenation',
        'Patient will maintain fluid balance without excessive edema'
      ],
      interventions: [
        'Monitor blood pressure every 4 hours or more frequently if unstable',
        'Assess for signs of severe preeclampsia: headache, visual changes, epigastric pain',
        'Monitor urine protein levels and 24-hour urine collection as ordered',
        'Administer antihypertensive medications as prescribed (labetalol, nifedipine)',
        'Implement seizure precautions: padded bed rails, suction at bedside',
        'Monitor fetal well-being with daily fetal movement counts and NSTs',
        'Assess deep tendon reflexes and clonus every shift',
        'Prepare for delivery planning and coordinate with perinatologist'
      ],
      evaluation: [
        'Blood pressure controlled within safe parameters without seizure activity',
        'No signs of organ damage: normal liver function, kidney function, platelet count',
        'Fetal growth and well-being maintained within normal parameters',
        'Edema controlled and patient maintains appropriate fluid balance'
      ]
    }
  },
  {
    id: 'prenatal-infection',
    specialtyId: 'maternal-newborn',
    title: 'Prenatal Infection',
    description: 'Care plan for infections during pregnancy',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for maternal sepsis related to untreated or severe infection',
        'Risk for fetal infection related to maternal-fetal transmission',
        'Risk for preterm labor related to infection-induced uterine irritability',
        'Hyperthermia related to infectious process and inflammatory response'
      ],
      goals: [
        'Patient will not develop sepsis or systemic complications',
        'Fetus will not be affected by maternal infection',
        'Patient will not experience preterm labor contractions',
        'Patient will maintain normal body temperature'
      ],
      interventions: [
        'Monitor vital signs every 4 hours with attention to temperature and heart rate',
        'Obtain specimens for culture and sensitivity testing as ordered',
        'Administer prescribed antibiotics safe for pregnancy use',
        'Monitor for signs of preterm labor: contractions, cervical changes',
        'Assess fetal well-being with regular fetal heart rate monitoring',
        'Implement infection control measures to prevent transmission',
        'Monitor white blood cell count and other laboratory indicators',
        'Educate about completing antibiotic course and follow-up care'
      ],
      evaluation: [
        'Infection resolves without progression to sepsis or complications',
        'Fetal heart rate remains reassuring with no signs of fetal infection',
        'No uterine contractions or signs of preterm labor develop',
        'Temperature returns to normal range and remains stable'
      ]
    }
  },
  {
    id: 'preterm-labor',
    specialtyId: 'maternal-newborn',
    title: 'Preterm Labor',
    description: 'Nursing care plan for preterm labor management',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for preterm delivery related to cervical changes and uterine contractions',
        'Anxiety related to threat of preterm birth and fetal outcomes',
        'Activity intolerance related to prescribed bed rest and tocolytic therapy',
        'Risk for medication side effects related to tocolytic drugs'
      ],
      goals: [
        'Patient will not deliver before 37 weeks gestation if possible',
        'Patient will demonstrate effective coping with treatment restrictions',
        'Patient will tolerate activity limitations without complications',
        'Patient will not experience severe side effects from tocolytic therapy'
      ],
      interventions: [
        'Monitor uterine contractions continuously for frequency, duration, intensity',
        'Assess cervical changes through sterile vaginal examinations as ordered',
        'Administer prescribed tocolytic medications (terbutaline, nifedipine)',
        'Monitor maternal vital signs and fetal heart rate continuously',
        'Implement bed rest with bathroom privileges or as ordered',
        'Administer corticosteroids for fetal lung maturation as prescribed',
        'Provide emotional support and education about preterm labor',
        'Coordinate care with neonatology team for potential preterm delivery'
      ],
      evaluation: [
        'Uterine contractions decrease or stop with cervical changes halted',
        'Patient verbalizes understanding and shows effective coping strategies',
        'Patient maintains bed rest compliance without developing complications',
        'Tocolytic therapy tolerated without significant adverse effects'
      ]
    }
  },
  {
    id: 'puerperal-postpartum-infections',
    specialtyId: 'maternal-newborn',
    title: 'Puerperal & Postpartum Infections',
    description: 'Care plan for postpartum infections',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for sepsis related to postpartum infection and delayed treatment',
        'Hyperthermia related to infectious process and inflammatory response',
        'Acute pain related to infected tissues and inflammatory process',
        'Risk for impaired breastfeeding related to maternal illness and treatment'
      ],
      goals: [
        'Patient will not develop sepsis or systemic complications',
        'Patient will maintain normal body temperature',
        'Patient will report pain controlled at acceptable levels',
        'Patient will maintain breastfeeding if desired without complications'
      ],
      interventions: [
        'Monitor vital signs every 4 hours with close attention to temperature',
        'Assess lochia for amount, color, odor, and signs of infection',
        'Monitor incision sites for signs of infection: redness, warmth, drainage',
        'Administer prescribed antibiotics and monitor for effectiveness',
        'Encourage adequate fluid intake and nutrition for healing',
        'Implement infection control measures to prevent spread',
        'Assess breastfeeding and pumping if patient unable to nurse directly',
        'Educate about signs of worsening infection requiring immediate care'
      ],
      evaluation: [
        'Infection resolves without progression to sepsis or complications',
        'Temperature returns to normal and remains stable',
        'Pain decreases and becomes manageable with comfort measures',
        'Breastfeeding maintained or milk supply preserved for future nursing'
      ]
    }
  },
  {
    id: 'substance-abuse-pregnancy',
    specialtyId: 'maternal-newborn',
    title: 'Substance (Alcohol and Drug) Abuse in Pregnancy',
    description: 'Care plan for substance abuse during pregnancy',
    category: 'Maternal & Newborn Care',
    pgie: {
      problems: [
        'Risk for fetal alcohol syndrome related to maternal alcohol consumption',
        'Risk for preterm labor related to substance use effects',
        'Imbalanced nutrition related to poor dietary habits and substance use',
        'Ineffective coping related to substance dependence and pregnancy stress'
      ],
      goals: [
        'Patient will deliver infant without fetal alcohol syndrome or drug effects',
        'Patient will maintain pregnancy to term without preterm labor',
        'Patient will maintain adequate nutrition for maternal and fetal health',
        'Patient will develop healthy coping strategies and reduce substance use'
      ],
      interventions: [
        'Conduct non-judgmental assessment of substance use patterns and frequency',
        'Provide education about effects of substances on fetal development',
        'Coordinate with addiction counselors and social services for treatment',
        'Monitor for signs of withdrawal and provide supportive care',
        'Assess nutritional status and coordinate with dietitian for meal planning',
        'Monitor fetal growth and well-being with regular ultrasounds and NSTs',
        'Implement harm reduction strategies if complete abstinence not achieved',
        'Prepare for potential neonatal abstinence syndrome in newborn'
      ],
      evaluation: [
        'Fetal growth and development appropriate with no signs of substance effects',
        'Pregnancy maintained to term with no complications from substance use',
        'Maternal weight gain appropriate and nutritional needs met',
        'Patient demonstrates reduced substance use and improved coping skills'
      ]
    }
  },

  // Additional Mental Health & Psychiatric Care (completing the section)
  {
    id: 'alcohol-withdrawal',
    specialtyId: 'mental-health-psychiatric',
    title: 'Alcohol Withdrawal',
    description: 'Nursing care plan for alcohol withdrawal management',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Risk for injury related to seizures and delirium tremens',
        'Disturbed sensory perception related to withdrawal hallucinations',
        'Ineffective coping related to loss of alcohol as coping mechanism',
        'Risk for fluid volume deficit related to diaphoresis and decreased intake'
      ],
      goals: [
        'Patient will remain safe and free from withdrawal complications',
        'Patient will demonstrate reality-based perceptions',
        'Patient will identify healthy coping strategies to replace alcohol use',
        'Patient will maintain adequate hydration and electrolyte balance'
      ],
      interventions: [
        'Monitor for withdrawal symptoms using CIWA-Ar scale every 4 hours',
        'Administer prescribed benzodiazepines per protocol to prevent seizures',
        'Assess vital signs frequently and monitor for signs of delirium tremens',
        'Provide calm, well-lit environment and orient to reality frequently',
        'Monitor intake and output and encourage fluid replacement',
        'Administer thiamine, folic acid, and multivitamins as prescribed',
        'Provide emotional support and avoid judgmental attitudes',
        'Coordinate with addiction counselor for treatment planning'
      ],
      evaluation: [
        'Patient experiences safe withdrawal without seizures or delirium',
        'Patient oriented to reality without withdrawal hallucinations',
        'Patient identifies triggers and expresses commitment to sobriety',
        'Patient maintains adequate hydration with normal electrolyte levels'
      ]
    }
  },
  {
    id: 'anxiety-fear',
    specialtyId: 'mental-health-psychiatric',
    title: 'Anxiety & Fear',
    description: 'Basic nursing care plan for anxiety and fear management',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Anxiety related to unknown outcomes and perceived threats',
        'Fear related to specific situations or objects',
        'Ineffective coping related to overwhelming emotional responses',
        'Sleep disturbance related to worry and hypervigilance'
      ],
      goals: [
        'Patient will demonstrate reduced anxiety levels in triggering situations',
        'Patient will identify specific fears and develop management strategies',
        'Patient will utilize effective coping mechanisms when stressed',
        'Patient will achieve restful sleep patterns'
      ],
      interventions: [
        'Assess anxiety and fear levels using validated assessment tools',
        'Teach stress reduction techniques: deep breathing, mindfulness, grounding',
        'Help patient identify triggers and early warning signs of anxiety',
        'Provide calm, supportive presence during episodes of high anxiety',
        'Encourage gradual exposure to feared situations when appropriate',
        'Administer anxiolytic medications as prescribed for severe symptoms',
        'Educate about the connection between thoughts, feelings, and behaviors',
        'Promote healthy sleep hygiene and relaxation before bedtime'
      ],
      evaluation: [
        'Patient reports decreased anxiety in previously triggering situations',
        'Patient verbalizes understanding of fears and uses coping strategies',
        'Patient demonstrates effective use of stress management techniques',
        'Patient sleeps 6-8 hours nightly with minimal nighttime awakening'
      ]
    }
  },
  {
    id: 'body-image-disturbance',
    specialtyId: 'mental-health-psychiatric',
    title: 'Body Image Disturbance & Self-Esteem',
    description: 'Care plan for body image and self-esteem issues',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Disturbed body image related to perceived physical defects or changes',
        'Chronic low self-esteem related to negative self-perception',
        'Social isolation related to shame and embarrassment about appearance',
        'Risk for self-harm related to body dissatisfaction and low self-worth'
      ],
      goals: [
        'Patient will express realistic perception of body image',
        'Patient will verbalize positive self-statements and improved self-esteem',
        'Patient will engage in social activities without excessive self-consciousness',
        'Patient will utilize healthy coping strategies when distressed'
      ],
      interventions: [
        'Assess patient\'s perception of body image and self-esteem using standardized tools',
        'Encourage expression of feelings about body image without judgment',
        'Help patient identify and challenge negative self-talk and cognitive distortions',
        'Promote focus on body function rather than appearance',
        'Encourage participation in activities that build self-confidence',
        'Facilitate support group participation with others experiencing similar issues',
        'Provide education about realistic body image and media influences',
        'Monitor for signs of self-harm and implement safety measures as needed'
      ],
      evaluation: [
        'Patient demonstrates more realistic and positive body image perception',
        'Patient makes positive self-statements and shows improved confidence',
        'Patient participates in social activities with reduced self-consciousness',
        'Patient uses healthy coping strategies without resorting to self-harm'
      ]
    }
  },
  {
    id: 'impaired-thought-processes',
    specialtyId: 'mental-health-psychiatric',
    title: 'Impaired Thought Processes & Cognitive Impairment',
    description: 'Care plan for cognitive and thought process disorders',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Impaired thought processes related to cognitive dysfunction or mental illness',
        'Risk for injury related to poor judgment and decision-making',
        'Self-care deficit related to cognitive impairment and confusion',
        'Social isolation related to communication difficulties and behavioral changes'
      ],
      goals: [
        'Patient will demonstrate improved cognitive function within limitations',
        'Patient will remain safe and free from injury',
        'Patient will maintain optimal level of independence in self-care',
        'Patient will engage in meaningful social interactions'
      ],
      interventions: [
        'Assess cognitive function using standardized tools (MMSE, MoCA)',
        'Provide structured environment with consistent routines and clear expectations',
        'Use simple, concrete language and repeat instructions as needed',
        'Implement reality orientation techniques and validate feelings',
        'Ensure safety by removing hazards and providing supervision as needed',
        'Encourage participation in cognitive stimulation activities',
        'Assist with activities of daily living while promoting independence',
        'Facilitate family education about cognitive changes and management strategies'
      ],
      evaluation: [
        'Patient shows stabilization or improvement in cognitive assessment scores',
        'No injuries occur due to impaired judgment or confusion',
        'Patient maintains maximum possible independence in self-care activities',
        'Patient participates in social activities appropriate to cognitive level'
      ]
    }
  },
  {
    id: 'personality-disorders',
    specialtyId: 'mental-health-psychiatric',
    title: 'Personality Disorders',
    description: 'Nursing care plan for personality disorder management',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Ineffective coping related to maladaptive personality patterns',
        'Impaired social interaction related to interpersonal difficulties',
        'Chronic low self-esteem related to identity disturbance',
        'Risk for self-directed violence related to emotional dysregulation'
      ],
      goals: [
        'Patient will develop effective coping strategies for stress management',
        'Patient will demonstrate improved interpersonal relationships',
        'Patient will express improved self-worth and identity stability',
        'Patient will utilize safe alternatives to self-destructive behaviors'
      ],
      interventions: [
        'Establish clear, consistent boundaries and therapeutic limits',
        'Use dialectical behavior therapy (DBT) skills for emotional regulation',
        'Encourage expression of feelings in appropriate ways',
        'Provide consistent staff assignments to build therapeutic relationships',
        'Teach distress tolerance and interpersonal effectiveness skills',
        'Monitor for self-harm behaviors and implement safety contracts',
        'Facilitate group therapy participation for peer feedback and support',
        'Coordinate with mental health team for long-term treatment planning'
      ],
      evaluation: [
        'Patient demonstrates use of healthy coping strategies during stressful situations',
        'Patient maintains stable relationships with decreased conflict',
        'Patient expresses more positive self-regard and stable identity',
        'Patient reports using safe alternatives when feeling urge to self-harm'
      ]
    }
  },
  {
    id: 'sexual-assault',
    specialtyId: 'mental-health-psychiatric',
    title: 'Sexual Assault',
    description: 'Trauma-informed care plan for sexual assault survivors',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Post-trauma syndrome related to sexual assault experience',
        'Powerlessness related to loss of control during traumatic event',
        'Risk for infection related to sexual assault injuries',
        'Disturbed sleep pattern related to nightmares and hypervigilance'
      ],
      goals: [
        'Patient will demonstrate healthy processing of trauma experience',
        'Patient will regain sense of personal control and empowerment',
        'Patient will remain free from sexually transmitted infections',
        'Patient will achieve restful sleep patterns'
      ],
      interventions: [
        'Provide immediate medical evaluation and forensic examination if consented',
        'Offer prophylactic antibiotics and emergency contraception as appropriate',
        'Use trauma-informed care principles: safety, trustworthiness, collaboration',
        'Allow patient to maintain control over decisions and pace of care',
        'Provide crisis counseling and validate patient\'s feelings and responses',
        'Connect with sexual assault nurse examiner (SANE) if available',
        'Facilitate referral to specialized trauma therapy and support services',
        'Educate about normal trauma responses and coping strategies'
      ],
      evaluation: [
        'Patient demonstrates adaptive coping and processing of trauma',
        'Patient makes autonomous decisions and expresses feeling empowered',
        'STI testing negative and no infections develop',
        'Patient reports improved sleep quality with fewer trauma-related nightmares'
      ]
    }
  },
  {
    id: 'substance-dependence',
    specialtyId: 'mental-health-psychiatric',
    title: 'Substance Dependence and Abuse',
    description: 'Nursing care plan for substance abuse treatment',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Ineffective denial related to substance use and its consequences',
        'Ineffective coping related to use of substances to manage stress',
        'Risk for injury related to impaired judgment and motor function',
        'Interrupted family processes related to substance abuse effects'
      ],
      goals: [
        'Patient will acknowledge substance abuse problem and need for treatment',
        'Patient will develop healthy coping strategies to replace substance use',
        'Patient will remain safe and free from substance-related injuries',
        'Patient will rebuild healthy family relationships and support systems'
      ],
      interventions: [
        'Assess substance use history and patterns without being judgmental',
        'Use motivational interviewing techniques to enhance motivation for change',
        'Monitor for withdrawal symptoms and administer medications as prescribed',
        'Provide education about addiction as a disease and recovery process',
        'Facilitate participation in 12-step programs or other support groups',
        'Teach stress management and relapse prevention strategies',
        'Coordinate with addiction counselors and social services',
        'Involve family in treatment planning and education about addiction'
      ],
      evaluation: [
        'Patient verbalizes understanding of addiction and commitment to recovery',
        'Patient demonstrates use of healthy coping skills during stressful situations',
        'No substance-related injuries or accidents occur during treatment',
        'Family relationships show improvement with increased communication and trust'
      ]
    }
  },
  {
    id: 'suicide-behaviors',
    specialtyId: 'mental-health-psychiatric',
    title: 'Suicide Behaviors',
    description: 'Crisis intervention care plan for suicidal ideation',
    category: 'Mental Health & Psychiatric Care',
    pgie: {
      problems: [
        'Risk for suicide related to hopelessness and psychological pain',
        'Hopelessness related to perception that problems are unsolvable',
        'Social isolation related to withdrawal and shame about suicidal thoughts',
        'Ineffective coping related to overwhelming life stressors'
      ],
      goals: [
        'Patient will remain safe and contract for safety',
        'Patient will express hope and identify reasons for living',
        'Patient will engage with support systems and healthcare team',
        'Patient will develop effective coping strategies for managing crises'
      ],
      interventions: [
        'Conduct comprehensive suicide risk assessment using validated tools (C-SSRS)',
        'Implement appropriate level of suicide precautions based on risk level',
        'Remove potentially harmful objects from patient environment',
        'Establish therapeutic relationship and provide constant supervision if needed',
        'Help patient identify protective factors and reasons for living',
        'Teach crisis intervention techniques and safety planning',
        'Facilitate connection with mental health professionals and crisis services',
        'Involve family/support system in safety planning and treatment'
      ],
      evaluation: [
        'Patient contracts for safety and reports no active suicidal ideation',
        'Patient expresses hope for the future and identifies goals to live for',
        'Patient actively engages with treatment team and support persons',
        'Patient demonstrates use of healthy coping strategies during distress'
      ]
    }
  },

  // Musculoskeletal Care
  {
    id: 'amputation-musculoskeletal',
    specialtyId: 'musculoskeletal-care',
    title: 'Amputation',
    description: 'Musculoskeletal care plan for amputation patients',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Acute pain related to surgical amputation and phantom limb sensation',
        'Impaired physical mobility related to loss of limb and altered body mechanics',
        'Disturbed body image related to loss of body part and functional changes',
        'Risk for infection related to surgical site and delayed wound healing'
      ],
      goals: [
        'Patient will report manageable pain levels and effective phantom limb management',
        'Patient will demonstrate safe mobility with prosthetic or assistive devices',
        'Patient will verbalize acceptance of body changes and maintain positive self-image',
        'Patient will achieve complete wound healing without complications'
      ],
      interventions: [
        'Assess pain level including phantom limb pain using 0-10 scale every 4 hours',
        'Administer prescribed analgesics and evaluate effectiveness of pain relief',
        'Monitor surgical site for signs of infection, drainage, or poor healing',
        'Assist with residual limb positioning and wrapping as ordered',
        'Coordinate with physical therapy for mobility training and prosthetic preparation',
        'Provide emotional support and counseling for body image adjustment',
        'Educate on residual limb care, phantom limb phenomena, and prosthetic use',
        'Encourage participation in support groups and peer counseling'
      ],
      evaluation: [
        'Patient reports adequate pain control and decreased phantom limb discomfort',
        'Patient demonstrates safe transfers and mobility with appropriate devices',
        'Patient expresses acceptance of amputation and positive outlook for rehabilitation',
        'Surgical site healed completely without signs of infection or complications'
      ]
    }
  },
  {
    id: 'congenital-hip-dysplasia',
    specialtyId: 'musculoskeletal-care',
    title: 'Congenital Hip Dysplasia',
    description: 'Nursing care plan for hip dysplasia in infants',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Risk for impaired skin integrity related to immobilization devices and positioning',
        'Impaired physical mobility related to hip dysplasia and corrective devices',
        'Risk for delayed growth and development related to immobilization',
        'Parental anxiety related to infant\'s condition and treatment requirements'
      ],
      goals: [
        'Infant will maintain intact skin integrity during treatment period',
        'Infant will achieve proper hip joint alignment and stability',
        'Infant will meet developmental milestones within individual capabilities',
        'Parents will demonstrate confidence in caring for infant with hip dysplasia'
      ],
      interventions: [
        'Inspect skin under and around immobilization devices every 4-8 hours',
        'Ensure proper fit and positioning of Pavlik harness or spica cast',
        'Provide age-appropriate stimulation and developmental activities',
        'Teach parents proper positioning, feeding, and diaper changing techniques',
        'Monitor for circulation, sensation, and movement in affected extremities',
        'Assess pain and comfort level using age-appropriate pain scales',
        'Provide education on signs of complications requiring immediate attention',
        'Coordinate follow-up care with orthopedic specialist and developmental services'
      ],
      evaluation: [
        'Skin remains intact without breakdown or pressure areas',
        'Hip joint alignment improved as evidenced by imaging studies',
        'Infant demonstrates age-appropriate growth and developmental progress',
        'Parents verbalize understanding and demonstrate competent care techniques'
      ]
    }
  },
  {
    id: 'fracture-musculoskeletal',
    specialtyId: 'musculoskeletal-care',
    title: 'Fracture',
    description: 'Musculoskeletal nursing care plan for fractures',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Acute pain related to bone injury, soft tissue damage, and muscle spasm',
        'Impaired physical mobility related to fracture, immobilization, and pain',
        'Risk for infection related to open fracture or surgical intervention',
        'Risk for complications related to immobility (thrombosis, pneumonia)'
      ],
      goals: [
        'Patient will report pain controlled at acceptable level (≤4/10)',
        'Patient will maintain optimal mobility within restrictions of treatment',
        'Patient will not develop infections at fracture or surgical sites',
        'Patient will remain free from complications of immobility'
      ],
      interventions: [
        'Assess pain level using 0-10 scale and administer analgesics as prescribed',
        'Monitor neurovascular status: circulation, sensation, movement every 2 hours',
        'Maintain proper alignment and immobilization of fractured extremity',
        'Assess cast, splint, or traction device for proper fit and function',
        'Encourage active range of motion in unaffected joints',
        'Monitor for signs of infection: fever, drainage, increased pain',
        'Implement measures to prevent complications: leg exercises, deep breathing',
        'Educate on cast care, activity restrictions, and signs of complications'
      ],
      evaluation: [
        'Pain adequately controlled allowing for comfort and participation in care',
        'Patient maintains maximum mobility within treatment limitations',
        'No signs of infection at fracture site or surgical incision',
        'Patient remains free from thrombosis, pneumonia, or other complications'
      ]
    }
  },
  {
    id: 'impaired-physical-mobility',
    specialtyId: 'musculoskeletal-care',
    title: 'Impaired Physical Mobility & Immobility',
    description: 'Care plan for mobility impairment and immobility',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to musculoskeletal impairment or prolonged bed rest',
        'Risk for disuse syndrome related to immobility and muscle atrophy',
        'Risk for impaired skin integrity related to pressure and decreased mobility',
        'Risk for thromboembolism related to venous stasis and immobility'
      ],
      goals: [
        'Patient will maintain or improve current level of mobility and function',
        'Patient will not develop muscle atrophy or joint contractures',
        'Patient will maintain intact skin integrity without pressure ulcers',
        'Patient will not develop blood clots or pulmonary embolism'
      ],
      interventions: [
        'Assess current mobility level and functional capacity regularly',
        'Implement range of motion exercises: active, passive, or assisted',
        'Encourage position changes every 2 hours and use pressure-relieving devices',
        'Apply sequential compression devices or anticoagulants as prescribed',
        'Coordinate with physical therapy for mobility training and exercises',
        'Use assistive devices: walkers, canes, wheelchairs as appropriate',
        'Monitor for signs of complications: skin breakdown, swelling, pain',
        'Educate patient and family on importance of mobility and exercise'
      ],
      evaluation: [
        'Patient demonstrates improved or maintained mobility within limitations',
        'No evidence of muscle atrophy or joint contractures',
        'Skin remains intact without signs of pressure damage',
        'No signs of thromboembolism or circulatory complications'
      ]
    }
  },
  {
    id: 'juvenile-rheumatoid-arthritis',
    specialtyId: 'musculoskeletal-care',
    title: 'Juvenile Rheumatoid Arthritis',
    description: 'Pediatric care plan for juvenile arthritis',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Chronic pain related to joint inflammation and morning stiffness',
        'Impaired physical mobility related to joint pain and inflammation',
        'Risk for delayed growth and development related to chronic illness',
        'Interrupted family processes related to child\'s chronic condition'
      ],
      goals: [
        'Child will report pain controlled at acceptable levels for age',
        'Child will maintain optimal joint mobility and functional ability',
        'Child will achieve age-appropriate growth and developmental milestones',
        'Family will demonstrate effective coping and management strategies'
      ],
      interventions: [
        'Assess pain using age-appropriate pain scales and administer medications',
        'Apply heat therapy to stiff joints and cold therapy during acute flares',
        'Encourage range of motion exercises and physical therapy participation',
        'Monitor for medication side effects and therapeutic effectiveness',
        'Promote normal growth through adequate nutrition and activity',
        'Facilitate school attendance and participation in age-appropriate activities',
        'Provide family education on disease management and medication compliance',
        'Connect family with support groups and community resources'
      ],
      evaluation: [
        'Child reports manageable pain levels and participates in daily activities',
        'Joint mobility maintained with minimal functional limitations',
        'Child demonstrates normal growth patterns and developmental progress',
        'Family shows effective adaptation and management of chronic condition'
      ]
    }
  },
  {
    id: 'laminectomy-musculoskeletal',
    specialtyId: 'musculoskeletal-care',
    title: 'Laminectomy (Disc Surgery)',
    description: 'Musculoskeletal care for spinal surgery',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and spinal manipulation',
        'Impaired physical mobility related to surgical procedure and activity restrictions',
        'Risk for infection related to surgical wound and spinal hardware',
        'Risk for neurological injury related to spinal cord proximity'
      ],
      goals: [
        'Patient will report pain controlled at acceptable level (≤4/10)',
        'Patient will demonstrate improved mobility without neurological deficits',
        'Patient will not develop postoperative infections',
        'Patient will maintain or improve neurological function'
      ],
      interventions: [
        'Assess pain level every 4 hours and administer analgesics as prescribed',
        'Monitor neurological status: motor function, sensation, reflexes',
        'Maintain proper spinal alignment and log-rolling technique for position changes',
        'Monitor surgical site for signs of infection, drainage, or dehiscence',
        'Encourage deep breathing exercises and incentive spirometry use',
        'Assist with early ambulation as ordered by surgeon',
        'Provide education on activity restrictions and proper body mechanics',
        'Coordinate with physical therapy for progressive mobility training'
      ],
      evaluation: [
        'Pain adequately controlled allowing for mobility and self-care',
        'Patient demonstrates improved mobility with stable neurological function',
        'Surgical site healed without signs of infection or complications',
        'Neurological status maintained or improved from preoperative baseline'
      ]
    }
  },
  {
    id: 'osteoarthritis',
    specialtyId: 'musculoskeletal-care',
    title: 'Osteoarthritis',
    description: 'Nursing care plan for osteoarthritis management',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Chronic pain related to joint degeneration and inflammation',
        'Impaired physical mobility related to joint stiffness and pain',
        'Activity intolerance related to pain and decreased range of motion',
        'Self-care deficit related to joint dysfunction and pain'
      ],
      goals: [
        'Patient will report pain managed at acceptable level for daily activities',
        'Patient will maintain or improve joint mobility and function',
        'Patient will demonstrate improved activity tolerance',
        'Patient will perform activities of daily living independently or with minimal assistance'
      ],
      interventions: [
        'Assess pain level and joint function using standardized tools',
        'Administer prescribed analgesics and anti-inflammatory medications',
        'Apply heat therapy for stiffness and cold therapy for acute inflammation',
        'Encourage gentle range of motion and strengthening exercises',
        'Promote weight management to reduce stress on weight-bearing joints',
        'Provide assistive devices and adaptive equipment as needed',
        'Educate on joint protection techniques and energy conservation',
        'Coordinate with physical and occupational therapy for comprehensive care'
      ],
      evaluation: [
        'Patient reports improved pain control and functional ability',
        'Joint mobility maintained or improved with regular exercise',
        'Patient able to perform desired activities with manageable discomfort',
        'Independence in self-care activities achieved or maintained'
      ]
    }
  },
  {
    id: 'osteogenic-sarcoma',
    specialtyId: 'musculoskeletal-care',
    title: 'Osteogenic Sarcoma (Osteosarcoma)',
    description: 'Nursing care plan for bone cancer',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Acute pain related to bone tumor and treatment side effects',
        'Impaired physical mobility related to tumor location and treatment',
        'Risk for infection related to immunosuppression from chemotherapy',
        'Anxiety related to cancer diagnosis and uncertain prognosis'
      ],
      goals: [
        'Patient will report pain controlled at acceptable level (≤4/10)',
        'Patient will maintain optimal mobility and functional capacity',
        'Patient will remain free from infections during treatment',
        'Patient will demonstrate effective coping strategies and reduced anxiety'
      ],
      interventions: [
        'Assess pain using 0-10 scale and administer analgesics as prescribed',
        'Monitor for pathological fractures and implement safety measures',
        'Coordinate with oncology team for chemotherapy and surgical planning',
        'Monitor for signs of infection and implement neutropenic precautions',
        'Provide emotional support and facilitate counseling services',
        'Assess functional status and coordinate with rehabilitation services',
        'Educate patient and family about treatment plan and side effects',
        'Monitor laboratory values and report significant changes'
      ],
      evaluation: [
        'Pain adequately controlled allowing for daily activities and rest',
        'Patient maintains safe mobility without injury or fractures',
        'No signs of infection during chemotherapy treatment period',
        'Patient and family demonstrate effective coping and understanding of treatment'
      ]
    }
  },
  {
    id: 'osteoporosis',
    specialtyId: 'musculoskeletal-care',
    title: 'Osteoporosis',
    description: 'Nursing care plan for osteoporosis management',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Risk for injury (fractures) related to decreased bone density',
        'Chronic pain related to compression fractures and bone changes',
        'Impaired physical mobility related to fear of falling and fractures',
        'Deficient knowledge related to disease management and prevention'
      ],
      goals: [
        'Patient will not experience fractures during care period',
        'Patient will report pain controlled at manageable levels',
        'Patient will maintain safe mobility and independence',
        'Patient will demonstrate understanding of osteoporosis management'
      ],
      interventions: [
        'Assess risk factors and perform fall risk assessment',
        'Implement fall prevention measures: remove hazards, adequate lighting',
        'Encourage weight-bearing exercises and physical activity as tolerated',
        'Administer prescribed calcium, vitamin D, and bone-building medications',
        'Provide education on proper nutrition and calcium-rich foods',
        'Teach proper body mechanics and safe transfer techniques',
        'Monitor for signs of compression fractures: back pain, height loss',
        'Coordinate with physical therapy for strengthening and balance training'
      ],
      evaluation: [
        'No fractures or injuries occur during care period',
        'Patient reports improved pain management and comfort',
        'Patient demonstrates safe mobility techniques and maintains independence',
        'Patient verbalizes understanding of disease management and prevention strategies'
      ]
    }
  },
  {
    id: 'rheumatoid-arthritis',
    specialtyId: 'musculoskeletal-care',
    title: 'Rheumatoid Arthritis',
    description: 'Nursing care plan for rheumatoid arthritis management',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Chronic pain related to joint inflammation and morning stiffness',
        'Impaired physical mobility related to joint deformity and pain',
        'Fatigue related to chronic inflammatory process and pain',
        'Disturbed body image related to joint deformity and functional limitations'
      ],
      goals: [
        'Patient will report pain controlled at acceptable level for daily activities',
        'Patient will maintain optimal joint function and mobility',
        'Patient will demonstrate improved energy levels and activity tolerance',
        'Patient will maintain positive self-image and social relationships'
      ],
      interventions: [
        'Assess pain and joint function using standardized assessment tools',
        'Administer prescribed DMARDs, biologics, and anti-inflammatory medications',
        'Apply heat therapy for morning stiffness and cold for acute inflammation',
        'Encourage regular exercise and range of motion within tolerance',
        'Provide splints and assistive devices to support joint function',
        'Monitor for medication side effects and therapeutic effectiveness',
        'Educate on joint protection techniques and energy conservation',
        'Facilitate support group participation and counseling as needed'
      ],
      evaluation: [
        'Patient reports improved pain control and functional ability',
        'Joint mobility and function maintained or improved',
        'Patient demonstrates better energy management and less fatigue',
        'Patient maintains social activities and positive outlook despite limitations'
      ]
    }
  },
  {
    id: 'scoliosis',
    specialtyId: 'musculoskeletal-care',
    title: 'Scoliosis',
    description: 'Nursing care plan for scoliosis management',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Disturbed body image related to spinal curvature and postural changes',
        'Risk for impaired respiratory function related to thoracic deformity',
        'Chronic pain related to muscle imbalance and spinal curvature',
        'Risk for injury related to balance issues and postural instability'
      ],
      goals: [
        'Patient will maintain positive body image and self-esteem',
        'Patient will maintain adequate respiratory function',
        'Patient will report pain controlled at acceptable levels',
        'Patient will remain free from injury related to balance or posture'
      ],
      interventions: [
        'Assess degree of spinal curvature and functional limitations regularly',
        'Monitor respiratory status and encourage deep breathing exercises',
        'Provide education on proper posture and body mechanics',
        'Assist with brace fitting and wearing schedule as prescribed',
        'Encourage participation in approved physical activities and exercises',
        'Provide emotional support and counseling for body image concerns',
        'Monitor for signs of brace-related skin breakdown',
        'Coordinate with orthopedic surgeon for monitoring and potential surgical intervention'
      ],
      evaluation: [
        'Patient demonstrates positive adaptation to body changes',
        'Respiratory function remains adequate without compromise',
        'Pain managed effectively allowing for normal activities',
        'No injuries related to balance or postural issues occur'
      ]
    }
  },
  {
    id: 'spinal-cord-injury',
    specialtyId: 'musculoskeletal-care',
    title: 'Spinal Cord Injury',
    description: 'Comprehensive care plan for spinal cord injury',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to paralysis and loss of motor function',
        'Risk for autonomic dysreflexia related to uninhibited sympathetic response',
        'Risk for impaired skin integrity related to immobility and loss of sensation',
        'Self-care deficit related to paralysis and functional limitations'
      ],
      goals: [
        'Patient will achieve maximum functional mobility and independence',
        'Patient will not experience autonomic dysreflexia episodes',
        'Patient will maintain intact skin integrity without pressure ulcers',
        'Patient will demonstrate independence in self-care activities within limitations'
      ],
      interventions: [
        'Assess neurological function and monitor for changes in sensation/motor function',
        'Implement strict spinal immobilization during acute phase',
        'Monitor for signs of autonomic dysreflexia: hypertension, bradycardia, sweating',
        'Establish turning schedule every 2 hours and inspect skin regularly',
        'Initiate bladder and bowel management programs',
        'Coordinate with rehabilitation team: PT, OT, psychology, social services',
        'Provide adaptive equipment and assistive technology training',
        'Educate patient and family on lifelong management and complications'
      ],
      evaluation: [
        'Patient demonstrates maximum possible mobility and transfer abilities',
        'No episodes of autonomic dysreflexia occur during care period',
        'Skin remains intact without signs of breakdown or pressure areas',
        'Patient achieves independence in self-care activities within functional limitations'
      ]
    }
  },
  {
    id: 'total-joint-replacement-musculoskeletal',
    specialtyId: 'musculoskeletal-care',
    title: 'Total Joint (Knee, Hip) Replacement',
    description: 'Musculoskeletal care for joint replacement surgery',
    category: 'Musculoskeletal Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and joint manipulation',
        'Impaired physical mobility related to surgical procedure and activity restrictions',
        'Risk for infection related to prosthetic joint and surgical wound',
        'Risk for dislocation related to improper positioning or movement'
      ],
      goals: [
        'Patient will report pain controlled at acceptable level (≤4/10)',
        'Patient will demonstrate progressive improvement in mobility and function',
        'Patient will not develop postoperative infections',
        'Patient will not experience joint dislocation'
      ],
      interventions: [
        'Assess pain level every 4 hours and administer analgesics as prescribed',
        'Monitor neurovascular status: circulation, sensation, movement every 2 hours',
        'Maintain proper positioning and movement restrictions per surgeon orders',
        'Monitor surgical site and drain output for signs of infection',
        'Encourage early mobilization and physical therapy participation',
        'Implement fall prevention measures and safe transfer techniques',
        'Provide education on activity restrictions and precautions',
        'Coordinate discharge planning with home health and outpatient therapy'
      ],
      evaluation: [
        'Pain adequately controlled allowing for mobility and therapy participation',
        'Patient demonstrates progressive improvement in joint function and mobility',
        'No signs of infection at surgical site or systemically',
        'Joint remains stable without dislocation or mechanical complications'
      ]
    }
  },

  // Additional Neurological Care (completing the section)
  {
    id: 'brain-tumor',
    specialtyId: 'neurological-care',
    title: 'Brain Tumor',
    description: 'Nursing care plan for brain tumor patients',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Risk for increased intracranial pressure related to mass effect and cerebral edema',
        'Acute pain (headache) related to increased intracranial pressure',
        'Risk for seizures related to tumor location and cerebral irritation',
        'Anxiety related to diagnosis and uncertain prognosis'
      ],
      goals: [
        'Patient will maintain stable intracranial pressure within normal limits',
        'Patient will report headache pain controlled at acceptable level',
        'Patient will remain free from seizure activity or injury',
        'Patient will demonstrate effective coping strategies and reduced anxiety'
      ],
      interventions: [
        'Monitor neurological status every 4 hours using Glasgow Coma Scale',
        'Assess for signs of increased ICP: headache, vomiting, changes in LOC',
        'Elevate head of bed 30 degrees and maintain head in neutral position',
        'Administer prescribed medications: steroids, anticonvulsants, analgesics',
        'Implement seizure precautions and maintain safe environment',
        'Monitor vital signs and watch for Cushing\'s triad',
        'Provide emotional support and facilitate communication with oncology team',
        'Educate patient and family about treatment options and side effects'
      ],
      evaluation: [
        'Intracranial pressure remains stable with no signs of herniation',
        'Headache pain managed effectively with appropriate medications',
        'No seizure activity observed during hospitalization',
        'Patient and family verbalize understanding of condition and demonstrate effective coping'
      ]
    }
  },
  {
    id: 'cerebral-palsy',
    specialtyId: 'neurological-care',
    title: 'Cerebral Palsy',
    description: 'Nursing care plan for cerebral palsy management',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to muscle spasticity and contractures',
        'Risk for aspiration related to impaired swallowing and oral motor dysfunction',
        'Self-care deficit related to neuromuscular impairment',
        'Risk for impaired skin integrity related to immobility and pressure points'
      ],
      goals: [
        'Patient will maintain optimal mobility and prevent contractures',
        'Patient will maintain clear airway and adequate nutrition without aspiration',
        'Patient will achieve maximum independence in activities of daily living',
        'Patient will maintain intact skin integrity without pressure ulcers'
      ],
      interventions: [
        'Implement range of motion exercises and positioning schedule',
        'Use adaptive equipment and assistive devices as appropriate',
        'Assess swallowing function and modify diet texture as needed',
        'Position patient properly during feeding to prevent aspiration',
        'Encourage participation in self-care activities within ability',
        'Implement pressure relief measures and skin assessment schedule',
        'Coordinate with multidisciplinary team: PT, OT, speech therapy',
        'Provide family education on home care management and therapy'
      ],
      evaluation: [
        'Patient maintains joint mobility with minimal contracture development',
        'No aspiration episodes occur and nutritional needs are met',
        'Patient participates in self-care to maximum potential',
        'Skin remains intact without signs of breakdown or pressure areas'
      ]
    }
  },
  {
    id: 'guillain-barre-syndrome',
    specialtyId: 'neurological-care',
    title: 'Guillain-Barre Syndrome',
    description: 'Nursing care plan for Guillain-Barre syndrome',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to ascending muscle weakness and paralysis',
        'Risk for respiratory failure related to respiratory muscle weakness',
        'Acute pain related to nerve inflammation and muscle weakness',
        'Anxiety related to rapid progression of paralysis and uncertain recovery'
      ],
      goals: [
        'Patient will maintain optimal function and prevent complications of immobility',
        'Patient will maintain adequate respiratory function without failure',
        'Patient will report pain controlled at acceptable levels',
        'Patient will demonstrate effective coping strategies and reduced anxiety'
      ],
      interventions: [
        'Monitor respiratory status closely including vital capacity and oxygen saturation',
        'Prepare for possible mechanical ventilation if respiratory function declines',
        'Implement range of motion exercises and positioning to prevent contractures',
        'Assess motor strength and sensory function regularly',
        'Administer prescribed treatments: IVIG or plasmapheresis',
        'Provide pain management with medications and comfort measures',
        'Encourage participation in physical and occupational therapy',
        'Provide emotional support and education about disease course and recovery'
      ],
      evaluation: [
        'Patient maintains joint mobility and muscle strength improves gradually',
        'Respiratory function remains adequate without need for mechanical ventilation',
        'Pain is well controlled and does not interfere with therapy',
        'Patient demonstrates understanding of condition and maintains positive outlook'
      ]
    }
  },
  {
    id: 'meningitis',
    specialtyId: 'neurological-care',
    title: 'Meningitis',
    description: 'Emergency care plan for meningitis',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Risk for increased intracranial pressure related to cerebral inflammation',
        'Hyperthermia related to infectious process and inflammation',
        'Acute pain (headache) related to meningeal irritation',
        'Risk for seizures related to cerebral irritation and inflammation'
      ],
      goals: [
        'Patient will maintain stable intracranial pressure within normal limits',
        'Patient will achieve normal body temperature within 24-48 hours',
        'Patient will report headache pain reduced to tolerable levels',
        'Patient will remain free from seizure activity'
      ],
      interventions: [
        'Monitor neurological status every 15-30 minutes initially, then every 2 hours',
        'Implement isolation precautions until bacterial cause ruled out',
        'Administer prescribed antibiotics/antivirals immediately as ordered',
        'Monitor and treat fever with antipyretics and cooling measures',
        'Elevate head of bed 30 degrees and maintain quiet, darkened environment',
        'Assess for signs of increased ICP: changes in LOC, vital signs, pupil response',
        'Implement seizure precautions and have emergency equipment ready',
        'Monitor fluid balance and avoid fluid overload'
      ],
      evaluation: [
        'Neurological status stable with no signs of increased intracranial pressure',
        'Temperature returns to normal range and remains stable',
        'Headache manageable with appropriate pain relief measures',
        'No seizure activity observed during treatment period'
      ]
    }
  },
  {
    id: 'multiple-sclerosis',
    specialtyId: 'neurological-care',
    title: 'Multiple Sclerosis',
    description: 'Nursing care plan for multiple sclerosis management',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to muscle weakness and spasticity',
        'Fatigue related to chronic neurological disease and demyelination',
        'Risk for injury related to decreased sensation and visual disturbances',
        'Ineffective coping related to unpredictable disease course'
      ],
      goals: [
        'Patient will maintain optimal mobility and independence in ADLs',
        'Patient will demonstrate effective energy conservation techniques',
        'Patient will remain free from injury and complications',
        'Patient will utilize effective coping strategies for disease management'
      ],
      interventions: [
        'Assess neurological status and functional abilities regularly',
        'Encourage regular exercise program within patient\'s tolerance',
        'Teach energy conservation techniques and pacing of activities',
        'Implement safety measures: remove obstacles, use assistive devices',
        'Administer prescribed disease-modifying medications as ordered',
        'Monitor for exacerbations and provide prompt treatment',
        'Provide education about triggers to avoid: stress, heat, infections',
        'Connect patient with MS support groups and resources'
      ],
      evaluation: [
        'Patient maintains mobility and functional independence within disease limitations',
        'Patient reports improved energy management and reduced fatigue',
        'No injuries or complications related to neurological deficits',
        'Patient demonstrates effective coping and adaptation to disease'
      ]
    }
  },
  {
    id: 'spinal-cord-injury-neurological',
    specialtyId: 'neurological-care',
    title: 'Spinal Cord Injury',
    description: 'Neurological care plan for spinal cord injury',
    category: 'Neurological Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to paralysis and loss of motor function',
        'Risk for autonomic dysreflexia related to uninhibited sympathetic response',
        'Risk for impaired skin integrity related to immobility and loss of sensation',
        'Bowel and bladder dysfunction related to neurological impairment'
      ],
      goals: [
        'Patient will achieve maximum functional mobility and independence',
        'Patient will not experience autonomic dysreflexia episodes',
        'Patient will maintain intact skin integrity without pressure ulcers',
        'Patient will establish effective bowel and bladder management'
      ],
      interventions: [
        'Assess neurological function and monitor for changes in sensation/motor function',
        'Implement strict spinal immobilization during acute phase',
        'Monitor for signs of autonomic dysreflexia: hypertension, bradycardia, sweating',
        'Establish turning schedule every 2 hours and inspect skin regularly',
        'Initiate bladder training program and intermittent catheterization',
        'Establish bowel program with dietary modification and scheduled evacuation',
        'Coordinate with rehabilitation team: PT, OT, social services',
        'Provide psychological support and counseling for adjustment to disability'
      ],
      evaluation: [
        'Patient demonstrates maximum possible mobility and transfer abilities',
        'No episodes of autonomic dysreflexia occur during care period',
        'Skin remains intact without signs of breakdown or pressure areas',
        'Patient achieves independent or assisted bowel and bladder management'
      ]
    }
  },

  // Ophthalmic Care
  {
    id: 'cataracts',
    specialtyId: 'ophthalmic-care',
    title: 'Cataracts',
    description: 'Nursing care plan for cataract surgery and management',
    category: 'Ophthalmic Care',
    pgie: {
      problems: [
        'Disturbed sensory perception (visual) related to lens opacity',
        'Risk for injury related to impaired vision and fall risk',
        'Anxiety related to surgical procedure and potential vision loss',
        'Deficient knowledge related to postoperative care and activity restrictions'
      ],
      goals: [
        'Patient will report improved vision following cataract surgery',
        'Patient will remain free from injury during recovery period',
        'Patient will demonstrate effective coping with surgical anxiety',
        'Patient will demonstrate understanding of postoperative care requirements'
      ],
      interventions: [
        'Assess visual acuity and impact on daily activities preoperatively',
        'Provide preoperative education about surgical procedure and expectations',
        'Implement fall prevention measures: clear pathways, adequate lighting',
        'Administer prescribed eye drops: antibiotics, anti-inflammatory medications',
        'Monitor for postoperative complications: infection, increased intraocular pressure',
        'Educate about activity restrictions: no heavy lifting, bending, eye rubbing',
        'Provide eye protection shield as ordered during sleep and activities',
        'Schedule follow-up appointments for vision assessment and lens prescription'
      ],
      evaluation: [
        'Patient reports significant improvement in vision clarity and function',
        'No falls or injuries occur during recovery period',
        'Patient expresses satisfaction with surgical outcome and reduced anxiety',
        'Patient demonstrates proper eye drop administration and follows restrictions'
      ]
    }
  },
  {
    id: 'glaucoma',
    specialtyId: 'ophthalmic-care',
    title: 'Glaucoma',
    description: 'Nursing care plan for glaucoma management',
    category: 'Ophthalmic Care',
    pgie: {
      problems: [
        'Risk for injury related to progressive visual field loss',
        'Disturbed sensory perception (visual) related to increased intraocular pressure',
        'Deficient knowledge related to chronic disease management and medication compliance',
        'Anxiety related to potential for permanent vision loss'
      ],
      goals: [
        'Patient will adapt safely to visual limitations and prevent injuries',
        'Patient will maintain stable intraocular pressure within target range',
        'Patient will demonstrate understanding of lifelong treatment requirements',
        'Patient will express manageable anxiety about disease progression'
      ],
      interventions: [
        'Monitor intraocular pressure measurements and visual field test results',
        'Administer prescribed eye drops on schedule and educate about proper technique',
        'Assess home environment for safety hazards and recommend modifications',
        'Provide education about glaucoma as chronic condition requiring lifelong management',
        'Monitor for medication side effects: eye irritation, systemic effects',
        'Encourage regular follow-up appointments with ophthalmologist',
        'Assess compliance with medication regimen and address barriers',
        'Refer to low vision services and support groups as appropriate'
      ],
      evaluation: [
        'Patient demonstrates safe navigation in environment without injuries',
        'Intraocular pressure maintained within prescribed target range',
        'Patient verbalizes understanding of medication importance and demonstrates compliance',
        'Patient expresses confidence in managing condition and realistic expectations'
      ]
    }
  },
  {
    id: 'macular-degeneration',
    specialtyId: 'ophthalmic-care',
    title: 'Macular Degeneration',
    description: 'Nursing care plan for macular degeneration',
    category: 'Ophthalmic Care',
    pgie: {
      problems: [
        'Disturbed sensory perception (visual) related to central vision loss',
        'Risk for injury related to impaired central vision and depth perception',
        'Compromised family coping related to progressive vision loss and role changes',
        'Ineffective role performance related to inability to perform usual activities'
      ],
      goals: [
        'Patient will adapt to vision changes using remaining peripheral vision',
        'Patient will remain free from falls and injuries in home environment',
        'Patient and family will demonstrate effective coping with vision loss',
        'Patient will maintain independence in modified daily activities'
      ],
      interventions: [
        'Assess degree of central vision loss and impact on daily activities',
        'Teach techniques for using peripheral vision effectively',
        'Provide magnification devices and adequate lighting for reading',
        'Arrange home safety evaluation and environmental modifications',
        'Refer to occupational therapy for adaptive equipment and techniques',
        'Connect with low vision rehabilitation services and support groups',
        'Educate family about ways to assist without promoting dependence',
        'Monitor for depression and provide emotional support and counseling referrals'
      ],
      evaluation: [
        'Patient demonstrates improved adaptation to central vision loss',
        'No falls or injuries related to vision impairment occur',
        'Patient and family show positive coping strategies and mutual support',
        'Patient maintains modified independence in meaningful daily activities'
      ]
    }
  },

  // Pediatric Nursing Care
  {
    id: 'child-abuse',
    specialtyId: 'pediatric-care',
    title: 'Child Abuse',
    description: 'Nursing care plan for suspected child abuse cases',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Risk for further injury related to continued exposure to abusive environment',
        'Fear related to threats, physical harm, and unpredictable caregiver behavior',
        'Ineffective coping related to trauma and developmental disruption',
        'Deficient knowledge (child) related to personal safety and body autonomy'
      ],
      goals: [
        'Child will be protected from further harm and placed in safe environment',
        'Child will demonstrate reduced fear responses and increased sense of safety',
        'Child will develop age-appropriate coping mechanisms for trauma recovery',
        'Child will learn and demonstrate personal safety skills and body autonomy'
      ],
      interventions: [
        'Assess for signs of physical, emotional, sexual, or neglect indicators',
        'Document all findings objectively with detailed descriptions and photographs',
        'Provide immediate safety by following mandatory reporting protocols',
        'Use therapeutic communication techniques appropriate for child\'s developmental level',
        'Collaborate with child protective services, social work, and legal teams',
        'Provide trauma-informed care and minimize re-traumatization during examinations',
        'Connect family with counseling services and community support resources',
        'Educate child about body safety, appropriate vs inappropriate touch'
      ],
      evaluation: [
        'Child removed from harmful environment and placed in protective custody',
        'Child demonstrates decreased anxiety and improved trust in safe caregivers',
        'Child shows evidence of trauma recovery through therapy and support',
        'Child verbalizes understanding of personal safety concepts age-appropriately'
      ]
    }
  },
  {
    id: 'cleft-lip-palate-pediatric',
    specialtyId: 'pediatric-care',
    title: 'Cleft Lip and Cleft Palate',
    description: 'Pediatric care plan for cleft lip and palate',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Ineffective infant feeding pattern related to cleft lip/palate and sucking difficulties',
        'Risk for aspiration related to abnormal oral-nasal communication',
        'Impaired verbal communication related to anatomical defect and speech development',
        'Risk for impaired parent-infant bonding related to visible facial defect'
      ],
      goals: [
        'Infant will maintain adequate nutrition and weight gain with adapted feeding methods',
        'Infant will not experience aspiration or respiratory complications',
        'Child will develop age-appropriate speech and communication skills',
        'Parents will demonstrate positive bonding and attachment behaviors'
      ],
      interventions: [
        'Assess feeding ability and recommend specialized bottles, nipples, or feeding devices',
        'Position infant upright during feedings and burp frequently to prevent aspiration',
        'Monitor weight gain and nutritional status closely',
        'Coordinate care with plastic surgeon for surgical repair timing',
        'Provide speech therapy referrals and monitor language development',
        'Support parents emotionally and provide education about condition',
        'Connect families with support groups and cleft palate organizations',
        'Educate about dental care needs and orthodontic follow-up'
      ],
      evaluation: [
        'Infant demonstrates successful feeding with appropriate weight gain',
        'No episodes of aspiration or feeding-related respiratory distress',
        'Child shows appropriate speech development with therapeutic support',
        'Parents demonstrate loving attachment and positive coping with condition'
      ]
    }
  },
  {
    id: 'dying-child',
    specialtyId: 'pediatric-care',
    title: 'Dying Child',
    description: 'Palliative care plan for terminally ill children',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Chronic pain related to terminal illness and disease progression',
        'Fear and anxiety related to death and dying process',
        'Anticipatory grieving (family) related to impending loss of child',
        'Spiritual distress related to questioning meaning and suffering'
      ],
      goals: [
        'Child will experience optimal comfort and pain management throughout care',
        'Child will express reduced fear and anxiety about dying process',
        'Family will receive support through anticipatory grief and bereavement process',
        'Child and family will find meaning and peace within their spiritual framework'
      ],
      interventions: [
        'Assess pain using age-appropriate pain scales and provide multimodal pain management',
        'Use honest, age-appropriate communication about condition and prognosis',
        'Encourage expression of feelings through art, play, music, or verbal communication',
        'Provide family-centered care and accommodate cultural and religious preferences',
        'Coordinate with palliative care team, chaplain, and social services',
        'Create memory-making opportunities and legacy projects with family',
        'Support siblings and extended family through grief counseling',
        'Ensure comfortable, peaceful environment and presence during final moments'
      ],
      evaluation: [
        'Child reports pain controlled and demonstrates comfort and peace',
        'Child expresses feelings and shows reduced anxiety about dying',
        'Family demonstrates healthy grieving process with appropriate support',
        'Child and family find comfort in spiritual practices and beliefs'
      ]
    }
  },
  {
    id: 'febrile-seizure',
    specialtyId: 'pediatric-care',
    title: 'Febrile Seizure',
    description: 'Emergency care plan for febrile seizures in children',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Risk for injury related to seizure activity and loss of consciousness',
        'Hyperthermia related to underlying infection or illness',
        'Ineffective airway clearance related to altered consciousness during seizure',
        'Anxiety (parental) related to witnessing child\'s seizure episode'
      ],
      goals: [
        'Child will remain safe from injury during and after seizure activity',
        'Child will achieve and maintain normal body temperature within acceptable range',
        'Child will maintain clear airway and adequate oxygenation',
        'Parents will demonstrate understanding of febrile seizures and appropriate responses'
      ],
      interventions: [
        'Monitor for seizure activity and maintain seizure precautions',
        'Position child on side during seizure to maintain airway patency',
        'Assess and monitor temperature every 30 minutes during febrile episodes',
        'Administer antipyretics as prescribed and implement cooling measures',
        'Assess respiratory status and provide oxygen support if needed',
        'Document seizure characteristics: duration, type, body parts involved',
        'Educate parents about febrile seizure triggers and prevention strategies',
        'Provide emotional support and reassurance to child and parents'
      ],
      evaluation: [
        'Child experiences no injuries related to seizure activity',
        'Temperature maintained below seizure threshold with interventions',
        'Airway remains clear with adequate oxygen saturation',
        'Parents verbalize understanding of condition and demonstrate confidence in care'
      ]
    }
  },
  {
    id: 'hospitalized-child',
    specialtyId: 'pediatric-care',
    title: 'Hospitalized Child',
    description: 'General nursing care plan for hospitalized children',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Anxiety and fear related to unfamiliar environment, procedures, and separation from family',
        'Risk for infection related to hospitalization and invasive procedures',
        'Interrupted family processes related to child\'s hospitalization and role changes',
        'Deficient diversional activity related to restricted mobility and hospital environment'
      ],
      goals: [
        'Child will demonstrate reduced anxiety and increased cooperation with care',
        'Child will remain free from hospital-acquired infections',
        'Family will maintain supportive relationships and adapt to hospitalization',
        'Child will engage in age-appropriate activities and developmental play'
      ],
      interventions: [
        'Use therapeutic play and age-appropriate communication to reduce anxiety',
        'Encourage family presence and participation in care when possible',
        'Implement strict infection control measures: hand hygiene, isolation precautions',
        'Provide consistent caregivers to establish trust and familiarity',
        'Coordinate with child life specialists for developmental and emotional support',
        'Maintain normal routines when possible: sleep schedules, meal times, activities',
        'Provide age-appropriate toys, books, and entertainment options',
        'Educate family about condition, procedures, and home care requirements'
      ],
      evaluation: [
        'Child shows decreased anxiety and increased cooperation with medical team',
        'No signs of hospital-acquired infections during admission',
        'Family demonstrates effective coping and involvement in child\'s care',
        'Child participates in play activities and shows age-appropriate responses'
      ]
    }
  },
  {
    id: 'hydrocephalus',
    specialtyId: 'pediatric-care',
    title: 'Hydrocephalus',
    description: 'Nursing care plan for hydrocephalus in children',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Risk for increased intracranial pressure related to cerebrospinal fluid accumulation',
        'Risk for infection related to ventriculoperitoneal shunt placement',
        'Impaired physical mobility related to enlarged head size and neurological deficits',
        'Delayed growth and development related to neurological impairment'
      ],
      goals: [
        'Child will maintain normal intracranial pressure with shunt function',
        'Child will remain free from shunt-related infections',
        'Child will achieve optimal mobility within physical limitations',
        'Child will meet developmental milestones appropriate for condition'
      ],
      interventions: [
        'Monitor for signs of increased ICP: irritability, vomiting, lethargy, bulging fontanelle',
        'Assess shunt function and monitor for malfunction signs',
        'Implement infection prevention: monitor surgical site, assess for fever',
        'Position infant with head elevated 30 degrees to promote drainage',
        'Measure head circumference daily and monitor growth patterns',
        'Provide developmental stimulation appropriate for child\'s abilities',
        'Educate parents about shunt care and signs of complications',
        'Coordinate with neurosurgery, physical therapy, and early intervention services'
      ],
      evaluation: [
        'Child shows no signs of increased intracranial pressure',
        'Shunt site remains free from infection with proper function',
        'Child demonstrates optimal motor function for developmental stage',
        'Child shows progress in developmental milestones with therapeutic support'
      ]
    }
  },
  {
    id: 'otitis-media',
    specialtyId: 'pediatric-care',
    title: 'Otitis Media',
    description: 'Pediatric care plan for ear infections',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Acute pain related to ear inflammation and increased pressure in middle ear',
        'Risk for infection (systemic) related to untreated bacterial otitis media',
        'Disturbed sleep pattern related to ear pain and discomfort',
        'Deficient knowledge (parents) related to prevention and treatment of ear infections'
      ],
      goals: [
        'Child will report reduced ear pain and demonstrate comfort',
        'Child will not develop complications from ear infection',
        'Child will achieve adequate sleep and rest periods',
        'Parents will demonstrate understanding of treatment and prevention strategies'
      ],
      interventions: [
        'Assess pain level using age-appropriate pain assessment tools',
        'Administer prescribed antibiotics and analgesics as ordered',
        'Apply warm compresses to affected ear for comfort (if no perforation)',
        'Monitor for signs of complications: fever, drainage, hearing loss',
        'Encourage adequate fluid intake to thin secretions',
        'Position child with affected ear down to promote drainage if perforated',
        'Educate parents about completing full antibiotic course',
        'Teach prevention strategies: hand hygiene, avoid secondhand smoke, proper bottle feeding'
      ],
      evaluation: [
        'Child reports significant reduction in ear pain and shows comfort behaviors',
        'No signs of systemic infection or complications develop',
        'Child sleeps through the night without awakening from ear pain',
        'Parents verbalize understanding of treatment completion and prevention methods'
      ]
    }
  },
  {
    id: 'spina-bifida',
    specialtyId: 'pediatric-care',
    title: 'Spina Bifida',
    description: 'Nursing care plan for spina bifida management',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Impaired physical mobility related to neural tube defect and paralysis',
        'Risk for infection related to exposed neural tissue or surgical repair site',
        'Bowel and bladder dysfunction related to neurological impairment',
        'Risk for latex allergy related to frequent exposure to latex products'
      ],
      goals: [
        'Child will achieve maximum mobility and independence within physical limitations',
        'Child will remain free from infections at surgical or defect sites',
        'Child will establish effective bowel and bladder management routine',
        'Child will avoid latex exposure and allergic reactions'
      ],
      interventions: [
        'Assess neurological function and level of spinal involvement',
        'Protect exposed neural tissue with sterile, moist dressings pre-operatively',
        'Monitor surgical site for signs of infection, CSF leakage, or healing problems',
        'Implement bowel and bladder training program appropriate for child\'s age',
        'Use latex-free environment and products for all care',
        'Coordinate with physical therapy for mobility aids and exercises',
        'Educate family about lifelong care needs and adaptive equipment',
        'Monitor for hydrocephalus development and shunt complications'
      ],
      evaluation: [
        'Child demonstrates optimal mobility using adaptive equipment and techniques',
        'No signs of infection at surgical sites or exposed areas',
        'Child maintains effective bowel and bladder management routine',
        'No allergic reactions occur due to latex-free environment maintenance'
      ]
    }
  },
  {
    id: 'tonsillitis-adenoiditis',
    specialtyId: 'pediatric-care',
    title: 'Tonsillitis and Adenoiditis',
    description: 'Pediatric care plan for tonsil and adenoid conditions',
    category: 'Pediatric Nursing Care',
    pgie: {
      problems: [
        'Acute pain related to throat inflammation and tissue swelling',
        'Risk for ineffective airway clearance related to enlarged tonsils and adenoids',
        'Impaired swallowing related to throat pain and tissue inflammation',
        'Risk for fluid volume deficit related to decreased oral intake'
      ],
      goals: [
        'Child will report reduced throat pain and demonstrate comfort',
        'Child will maintain clear airway and adequate breathing patterns',
        'Child will resume normal swallowing and eating patterns',
        'Child will maintain adequate hydration and nutritional status'
      ],
      interventions: [
        'Assess pain level using age-appropriate pain assessment tools',
        'Administer prescribed analgesics and anti-inflammatory medications',
        'Monitor respiratory status and position child upright to ease breathing',
        'Encourage cool, soft foods and avoid acidic or spicy items',
        'Provide throat lozenges or popsicles for older children (age-appropriate)',
        'Monitor fluid intake and output, assess for signs of dehydration',
        'Implement humidification to ease throat irritation and breathing',
        'Educate parents about signs requiring immediate medical attention'
      ],
      evaluation: [
        'Child reports significant reduction in throat pain and shows comfort',
        'Child breathes easily without signs of respiratory distress',
        'Child swallows comfortably and resumes normal eating patterns',
        'Child maintains adequate hydration with normal urine output'
      ]
    }
  },

  // Reproductive Care
  {
    id: 'cryptorchidism',
    specialtyId: 'reproductive-care',
    title: 'Cryptorchidism (Undescended Testes)',
    description: 'Nursing care plan for undescended testes',
    category: 'Reproductive Care',
    pgie: {
      problems: [
        'Risk for injury related to surgical orchiopexy procedure',
        'Acute pain related to surgical incision and scrotal edema',
        'Deficient knowledge related to condition and treatment options',
        'Anxiety (parental) related to child\'s condition and surgical outcome'
      ],
      goals: [
        'Patient will not experience surgical complications or injury',
        'Patient will report pain controlled at acceptable levels',
        'Parents will demonstrate understanding of condition and treatment',
        'Parents will express reduced anxiety about surgical procedure and outcomes'
      ],
      interventions: [
        'Assess testes location and monitor for descent in infants under 6 months',
        'Provide preoperative education about orchiopexy procedure to parents',
        'Monitor surgical site for bleeding, swelling, and signs of infection',
        'Administer prescribed pain medications and assess effectiveness',
        'Apply ice to scrotal area as ordered to reduce swelling',
        'Educate about activity restrictions post-surgery: no straddling, contact sports',
        'Monitor for complications: testicular atrophy, re-ascension of testes',
        'Schedule follow-up appointments for long-term monitoring of testicular development'
      ],
      evaluation: [
        'Surgery completed without complications and testes remain in scrotal position',
        'Pain managed effectively with medications and comfort measures',
        'Parents verbalize understanding of condition, treatment, and follow-up care',
        'Parents demonstrate reduced anxiety and confidence in care decisions'
      ]
    }
  },
  {
    id: 'hysterectomy-reproductive',
    specialtyId: 'reproductive-care',
    title: 'Hysterectomy',
    description: 'Reproductive health care for hysterectomy',
    category: 'Reproductive Care',
    pgie: {
      problems: [
        'Acute pain related to surgical incision and tissue trauma',
        'Risk for infection related to surgical wound and invasive procedure',
        'Grieving related to loss of reproductive capacity and role changes',
        'Risk for sexual dysfunction related to anatomical changes and psychological factors'
      ],
      goals: [
        'Patient will report pain controlled at acceptable levels for mobility and comfort',
        'Patient will not develop postoperative infections or complications',
        'Patient will demonstrate healthy grieving process and adjustment to changes',
        'Patient will maintain satisfactory sexual function with partner communication'
      ],
      interventions: [
        'Assess pain level using 0-10 scale and administer analgesics as prescribed',
        'Monitor surgical incision for signs of infection, dehiscence, or bleeding',
        'Encourage early ambulation and progressive activity as ordered',
        'Provide emotional support and validate feelings about reproductive loss',
        'Educate about hormone replacement therapy options if ovaries removed',
        'Discuss sexual health and provide counseling resources as appropriate',
        'Monitor for complications: urinary retention, bowel obstruction, bleeding',
        'Coordinate with social services and support groups as needed'
      ],
      evaluation: [
        'Pain adequately controlled allowing for progressive mobility and recovery',
        'Surgical incision heals without signs of infection or complications',
        'Patient expresses feelings appropriately and shows signs of healthy adjustment',
        'Patient and partner communicate about sexual health and receive appropriate support'
      ]
    }
  },
  {
    id: 'hypospadias-epispadias',
    specialtyId: 'reproductive-care',
    title: 'Hypospadias and Epispadias',
    description: 'Nursing care plan for urogenital anomalies',
    category: 'Reproductive Care',
    pgie: {
      problems: [
        'Impaired urinary elimination related to abnormal urethral opening position',
        'Risk for infection related to surgical repair and urinary diversion',
        'Acute pain related to surgical procedure and catheter placement',
        'Anxiety (parental) related to child\'s condition and surgical outcomes'
      ],
      goals: [
        'Patient will achieve normal urinary elimination following surgical repair',
        'Patient will not develop urinary tract infections or surgical complications',
        'Patient will report pain controlled at comfortable levels',
        'Parents will demonstrate understanding of condition and express reduced anxiety'
      ],
      interventions: [
        'Monitor urinary output and assess for signs of urinary retention',
        'Maintain urinary catheter patency and monitor for complications',
        'Assess surgical site for bleeding, swelling, and signs of infection',
        'Administer prescribed pain medications and assess effectiveness',
        'Provide gentle cleansing of genital area and catheter care',
        'Educate parents about normal healing process and expected outcomes',
        'Monitor for complications: fistula formation, stricture, meatal stenosis',
        'Coordinate follow-up care with pediatric urologist for long-term management'
      ],
      evaluation: [
        'Urinary elimination normal with appropriate stream and direction',
        'No signs of infection at surgical site or in urinary tract',
        'Pain managed effectively with comfort measures and medications',
        'Parents verbalize understanding of care and demonstrate confidence in management'
      ]
    }
  },
  {
    id: 'mastectomy-reproductive',
    specialtyId: 'reproductive-care',
    title: 'Mastectomy',
    description: 'Reproductive health care for mastectomy',
    category: 'Reproductive Care',
    pgie: {
      problems: [
        'Disturbed body image related to loss of breast and altered appearance',
        'Acute pain related to surgical incision and tissue manipulation',
        'Risk for infection related to surgical wound and lymph node removal',
        'Risk for impaired arm mobility related to axillary lymph node dissection'
      ],
      goals: [
        'Patient will demonstrate adaptation to body image changes with positive coping',
        'Patient will report pain controlled at acceptable levels for comfort',
        'Patient will not develop surgical site infections or complications',
        'Patient will maintain optimal arm and shoulder mobility and function'
      ],
      interventions: [
        'Provide emotional support and encourage expression of feelings about body changes',
        'Assess pain level and administer analgesics as prescribed',
        'Monitor surgical sites and drains for bleeding, infection, or complications',
        'Encourage arm exercises as prescribed to prevent lymphedema and contractures',
        'Educate about lymphedema prevention: avoid blood pressure cuffs, injections on affected side',
        'Coordinate with plastic surgeon for reconstruction consultation if desired',
        'Provide information about prostheses, support garments, and support groups',
        'Monitor arm circumference and assess for signs of lymphedema development'
      ],
      evaluation: [
        'Patient demonstrates healthy adaptation to body image changes',
        'Pain adequately controlled allowing for comfort and mobility',
        'Surgical sites heal without signs of infection or complications',
        'Arm and shoulder mobility maintained without lymphedema development'
      ]
    }
  },
  {
    id: 'menopause',
    specialtyId: 'reproductive-care',
    title: 'Menopause',
    description: 'Nursing care plan for menopause management',
    category: 'Reproductive Care',
    pgie: {
      problems: [
        'Ineffective thermoregulation related to hormonal changes and hot flashes',
        'Sleep pattern disturbance related to night sweats and hormonal fluctuations',
        'Sexual dysfunction related to vaginal dryness and decreased libido',
        'Risk for osteoporosis related to decreased estrogen levels'
      ],
      goals: [
        'Patient will demonstrate effective management of menopausal symptoms',
        'Patient will achieve improved sleep quality and duration',
        'Patient will maintain satisfactory sexual function and intimacy',
        'Patient will maintain bone health and prevent osteoporosis'
      ],
      interventions: [
        'Assess menopausal symptoms: hot flashes, night sweats, mood changes',
        'Educate about lifestyle modifications: layered clothing, cool environment',
        'Discuss hormone replacement therapy options and contraindications',
        'Recommend calcium and vitamin D supplementation for bone health',
        'Provide information about vaginal lubricants and moisturizers',
        'Encourage regular weight-bearing exercise and healthy diet',
        'Monitor bone density screening and cardiovascular risk factors',
        'Provide emotional support and connect with menopause support groups'
      ],
      evaluation: [
        'Patient reports decreased frequency and severity of menopausal symptoms',
        'Sleep quality improved with fewer nighttime awakenings',
        'Patient maintains satisfactory sexual health and relationship intimacy',
        'Bone density maintained or improved with preventive measures'
      ]
    }
  },
  {
    id: 'prostatectomy',
    specialtyId: 'reproductive-care',
    title: 'Prostatectomy',
    description: 'Nursing care plan for prostate surgery',
    category: 'Reproductive Care',
    pgie: {
      problems: [
        'Impaired urinary elimination related to catheter placement and surgical edema',
        'Risk for infection related to surgical wound and urinary catheter',
        'Sexual dysfunction related to nerve damage and psychological factors',
        'Acute pain related to surgical incision and bladder spasms'
      ],
      goals: [
        'Patient will regain normal urinary elimination without complications',
        'Patient will not develop urinary tract infections or surgical site infections',
        'Patient will adapt to changes in sexual function with appropriate support',
        'Patient will report pain controlled at acceptable levels'
      ],
      interventions: [
        'Monitor urinary catheter patency and output every 4 hours',
        'Assess for bladder spasms and administer antispasmodics as prescribed',
        'Monitor surgical incision for bleeding, swelling, and signs of infection',
        'Provide pelvic floor exercises (Kegels) education for continence training',
        'Discuss sexual health concerns and provide counseling resources',
        'Educate about temporary nature of incontinence and potential for improvement',
        'Monitor for complications: bleeding, infection, thromboembolism',
        'Coordinate with urologist for follow-up care and PSA monitoring'
      ],
      evaluation: [
        'Urinary continence improves progressively with pelvic floor strengthening',
        'No signs of urinary tract infection or surgical complications',
        'Patient and partner receive appropriate support for sexual health concerns',
        'Pain adequately managed allowing for mobility and recovery'
      ]
    }
  },

  // Additional Respiratory Care (completing the section)
  {
    id: 'airway-clearance',
    specialtyId: 'respiratory-care',
    title: 'Airway Clearance Therapy & Coughing',
    description: 'Nursing care plan for airway clearance',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to thick secretions or weak cough',
        'Risk for infection related to retained secretions in respiratory tract',
        'Impaired gas exchange related to airway obstruction',
        'Fatigue related to increased work of breathing'
      ],
      goals: [
        'Patient will demonstrate effective cough and ability to clear secretions',
        'Patient will remain free from respiratory infections',
        'Patient will maintain adequate gas exchange with clear breath sounds',
        'Patient will report reduced fatigue and improved breathing comfort'
      ],
      interventions: [
        'Assess cough effectiveness and sputum characteristics every 4 hours',
        'Encourage deep breathing and coughing exercises every 2 hours while awake',
        'Administer prescribed bronchodilators and mucolytics as ordered',
        'Provide adequate hydration (2-3 L/day) to thin secretions',
        'Position patient upright or in position of comfort for optimal coughing',
        'Perform chest physiotherapy and postural drainage as prescribed',
        'Use humidity and nebulizer treatments to moisten airways',
        'Suction airway if patient unable to clear secretions independently'
      ],
      evaluation: [
        'Patient demonstrates effective cough with improved secretion clearance',
        'No signs of respiratory infection present',
        'Breath sounds clear with improved air movement',
        'Patient reports decreased fatigue and easier breathing'
      ]
    }
  },
  {
    id: 'apnea',
    specialtyId: 'respiratory-care',
    title: 'Apnea',
    description: 'Nursing care plan for apnea management',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to periodic cessation of breathing',
        'Risk for injury related to hypoxemia and altered consciousness',
        'Sleep pattern disturbance related to frequent awakening and poor sleep quality',
        'Anxiety related to fear of suffocation and sleep disruption'
      ],
      goals: [
        'Patient will maintain effective breathing patterns during sleep',
        'Patient will remain free from injury related to apneic episodes',
        'Patient will achieve improved sleep quality with fewer interruptions',
        'Patient will demonstrate reduced anxiety about breathing during sleep'
      ],
      interventions: [
        'Monitor respiratory patterns and oxygen saturation continuously',
        'Position patient to maintain open airway (side-lying, elevated head)',
        'Administer CPAP or BiPAP therapy as prescribed',
        'Monitor for signs of hypoxemia: restlessness, confusion, cyanosis',
        'Educate patient on sleep hygiene and positional therapy',
        'Assess compliance with prescribed breathing devices',
        'Monitor weight and encourage weight loss if obese',
        'Refer for sleep study evaluation if indicated'
      ],
      evaluation: [
        'Breathing patterns normalized with reduced apneic episodes',
        'No injuries or complications related to hypoxemia',
        'Patient reports improved sleep quality and feeling more rested',
        'Patient demonstrates understanding and compliance with treatment'
      ]
    }
  },
  {
    id: 'aspiration-risk',
    specialtyId: 'respiratory-care',
    title: 'Aspiration Risk & Aspiration Pneumonia',
    description: 'Care plan for aspiration prevention and pneumonia',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Risk for aspiration related to impaired swallowing or altered consciousness',
        'Impaired gas exchange related to aspiration pneumonia',
        'Ineffective airway clearance related to aspirated material',
        'Risk for infection related to bacterial contamination from aspiration'
      ],
      goals: [
        'Patient will remain free from aspiration episodes',
        'Patient will maintain adequate gas exchange with clear breath sounds',
        'Patient will demonstrate effective airway clearance',
        'Patient will not develop aspiration pneumonia or respiratory infection'
      ],
      interventions: [
        'Assess swallowing ability and gag reflex before oral intake',
        'Position patient upright (90 degrees) during and after meals',
        'Provide thickened liquids and pureed foods as prescribed',
        'Monitor for signs of aspiration: coughing, choking, wet voice',
        'Keep suction equipment readily available at bedside',
        'Maintain NPO status if high aspiration risk',
        'Monitor respiratory status and breath sounds every 4 hours',
        'Collaborate with speech therapist for swallowing evaluation'
      ],
      evaluation: [
        'No aspiration episodes occur during care period',
        'Respiratory function remains stable with normal gas exchange',
        'Airway remains clear with effective cough reflex',
        'No signs of aspiration pneumonia or respiratory complications'
      ]
    }
  },
  {
    id: 'bronchiolitis',
    specialtyId: 'respiratory-care',
    title: 'Bronchiolitis',
    description: 'Pediatric care plan for bronchiolitis',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to increased mucus production and bronchial edema',
        'Impaired gas exchange related to ventilation-perfusion imbalance',
        'Risk for fluid volume deficit related to increased respiratory rate and decreased oral intake',
        'Anxiety (parental) related to child\'s respiratory distress'
      ],
      goals: [
        'Child will maintain patent airway with effective cough and clear breath sounds',
        'Child will maintain oxygen saturation ≥ 95% on room air or prescribed oxygen',
        'Child will maintain adequate hydration as evidenced by moist mucous membranes',
        'Parents will demonstrate reduced anxiety and understanding of condition'
      ],
      interventions: [
        'Monitor respiratory status every 2-4 hours: rate, effort, oxygen saturation',
        'Position child upright or semi-Fowler\'s to facilitate breathing',
        'Administer humidified oxygen as prescribed and monitor response',
        'Suction nasal passages gently as needed using bulb syringe',
        'Encourage frequent small feedings to maintain nutrition and hydration',
        'Provide comfort measures: calm environment, parental presence',
        'Administer bronchodilators and steroids as prescribed',
        'Educate parents on signs of respiratory distress requiring immediate attention'
      ],
      evaluation: [
        'Airway clearance improved with decreased respiratory distress',
        'Oxygen saturation maintained within acceptable parameters',
        'Child tolerating oral fluids and maintaining hydration',
        'Parents verbalize understanding and demonstrate appropriate care techniques'
      ]
    }
  },
  {
    id: 'bronchopulmonary-dysplasia',
    specialtyId: 'respiratory-care',
    title: 'Bronchopulmonary Dysplasia (BPD)',
    description: 'Neonatal care plan for BPD management',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to damaged alveolar-capillary membrane',
        'Ineffective breathing pattern related to increased work of breathing',
        'Risk for infection related to chronic lung disease and oxygen therapy',
        'Imbalanced nutrition related to increased caloric needs and feeding difficulties'
      ],
      goals: [
        'Infant will maintain adequate oxygenation with minimal respiratory distress',
        'Infant will demonstrate improved breathing patterns with decreased work of breathing',
        'Infant will remain free from respiratory infections',
        'Infant will achieve appropriate weight gain and nutritional status'
      ],
      interventions: [
        'Monitor respiratory status continuously: rate, effort, oxygen saturation',
        'Administer oxygen therapy as prescribed and wean gradually as tolerated',
        'Position infant to optimize lung expansion and comfort',
        'Provide chest physiotherapy and suction as needed',
        'Administer prescribed medications: bronchodilators, diuretics, steroids',
        'Monitor fluid balance and daily weights',
        'Provide high-calorie feedings and nutritional support',
        'Minimize environmental stimuli to reduce oxygen consumption'
      ],
      evaluation: [
        'Respiratory distress decreased with stable oxygen requirements',
        'Breathing pattern improved with less retractions and nasal flaring',
        'No signs of respiratory infection present',
        'Steady weight gain achieved with adequate nutritional intake'
      ]
    }
  },
  {
    id: 'croup-syndrome',
    specialtyId: 'respiratory-care',
    title: 'Croup Syndrome',
    description: 'Pediatric care plan for croup management',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to laryngeal edema and inflammation',
        'Ineffective breathing pattern related to upper airway obstruction',
        'Anxiety (child and parent) related to respiratory distress and barking cough',
        'Risk for dehydration related to fever and decreased oral intake'
      ],
      goals: [
        'Child will maintain patent airway with decreased stridor and respiratory distress',
        'Child will demonstrate improved breathing patterns with normal respiratory rate',
        'Child and parents will exhibit reduced anxiety and increased comfort',
        'Child will maintain adequate hydration and fluid balance'
      ],
      interventions: [
        'Monitor respiratory status every 15-30 minutes during acute phase',
        'Position child upright or in position of comfort',
        'Provide cool mist therapy or humidified air',
        'Administer corticosteroids as prescribed to reduce inflammation',
        'Keep child calm and minimize crying to prevent increased obstruction',
        'Offer frequent small amounts of clear fluids if tolerated',
        'Monitor for signs of increasing respiratory distress or cyanosis',
        'Prepare for emergency intubation if severe obstruction develops'
      ],
      evaluation: [
        'Airway obstruction decreased with reduced stridor and improved air entry',
        'Breathing pattern normalized with age-appropriate respiratory rate',
        'Child appears calm and comfortable with reduced anxiety',
        'Adequate hydration maintained with normal urine output'
      ]
    }
  },
  {
    id: 'cystic-fibrosis',
    specialtyId: 'respiratory-care',
    title: 'Cystic Fibrosis',
    description: 'Comprehensive care plan for cystic fibrosis',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to thick, tenacious secretions',
        'Imbalanced nutrition related to malabsorption and increased metabolic needs',
        'Risk for infection related to impaired pulmonary clearance mechanisms',
        'Activity intolerance related to compromised respiratory function'
      ],
      goals: [
        'Patient will maintain patent airway with effective secretion clearance',
        'Patient will maintain adequate nutritional status and normal growth patterns',
        'Patient will remain free from respiratory infections',
        'Patient will participate in age-appropriate activities with minimal limitations'
      ],
      interventions: [
        'Perform chest physiotherapy 2-3 times daily or as prescribed',
        'Administer prescribed bronchodilators and mucolytics',
        'Encourage high-calorie, high-protein diet with pancreatic enzymes',
        'Monitor for signs of respiratory infection and report promptly',
        'Encourage regular exercise and physical activity as tolerated',
        'Provide education on airway clearance techniques and medication administration',
        'Monitor growth parameters and nutritional status regularly',
        'Coordinate care with multidisciplinary team including respiratory therapy'
      ],
      evaluation: [
        'Airway clearance techniques effective with improved breath sounds',
        'Nutritional goals met with appropriate weight gain and growth',
        'Respiratory infections prevented or managed promptly',
        'Patient maintains active lifestyle within disease limitations'
      ]
    }
  },
  {
    id: 'epiglottitis',
    specialtyId: 'respiratory-care',
    title: 'Epiglottitis',
    description: 'Emergency care plan for epiglottitis',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Risk for airway obstruction related to epiglottal swelling and inflammation',
        'Ineffective breathing pattern related to upper airway compromise',
        'High anxiety related to difficulty breathing and fear of suffocation',
        'Risk for aspiration related to drooling and inability to swallow'
      ],
      goals: [
        'Patient will maintain patent airway without complete obstruction',
        'Patient will demonstrate stable breathing patterns with adequate oxygenation',
        'Patient will exhibit reduced anxiety with appropriate calming measures',
        'Patient will not experience aspiration of secretions'
      ],
      interventions: [
        'Do NOT examine throat or place anything in mouth - risk of complete obstruction',
        'Keep patient in upright position of comfort, often tripod position',
        'Have emergency intubation and tracheostomy equipment immediately available',
        'Administer humidified oxygen if tolerated without agitation',
        'Provide calm, quiet environment to minimize anxiety and oxygen demand',
        'Prepare for emergency airway management in operating room',
        'Administer antibiotics and corticosteroids as prescribed',
        'Monitor continuously for signs of complete airway obstruction'
      ],
      evaluation: [
        'Airway remains patent with no signs of complete obstruction',
        'Respiratory status stable with adequate oxygen saturation',
        'Patient demonstrates reduced anxiety and remains calm',
        'No aspiration events occur during care period'
      ]
    }
  },
  {
    id: 'hemothorax-pneumothorax',
    specialtyId: 'respiratory-care',
    title: 'Hemothorax and Pneumothorax',
    description: 'Emergency care plan for chest trauma',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to collapsed lung and chest pain',
        'Impaired gas exchange related to decreased lung expansion',
        'Acute pain related to chest trauma and invasive procedures',
        'Risk for shock related to blood loss (hemothorax) or tension pneumothorax'
      ],
      goals: [
        'Patient will maintain effective breathing pattern with improved lung expansion',
        'Patient will achieve adequate gas exchange as evidenced by normal ABGs',
        'Patient will report pain level of 3 or less on 0-10 scale',
        'Patient will maintain hemodynamic stability without signs of shock'
      ],
      interventions: [
        'Position patient in semi-Fowler\'s or sitting position to facilitate breathing',
        'Monitor respiratory status every 15 minutes initially, then every hour',
        'Prepare for and assist with chest tube insertion as ordered',
        'Monitor chest tube drainage and maintain proper functioning',
        'Administer oxygen therapy as prescribed and monitor saturation',
        'Assess pain level and administer analgesics as prescribed',
        'Monitor vital signs for signs of hemodynamic compromise',
        'Encourage deep breathing and coughing exercises once stable'
      ],
      evaluation: [
        'Breathing pattern normalized with symmetrical chest expansion',
        'Gas exchange adequate with oxygen saturation > 95%',
        'Pain adequately controlled and manageable',
        'Hemodynamic status stable with no signs of shock'
      ]
    }
  },
  {
    id: 'ineffective-breathing',
    specialtyId: 'respiratory-care',
    title: 'Ineffective Breathing Pattern (Dyspnea)',
    description: 'Care plan for breathing difficulties and dyspnea',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to anxiety, pain, or respiratory muscle fatigue',
        'Activity intolerance related to shortness of breath and decreased oxygenation',
        'Anxiety related to feeling of suffocation and air hunger',
        'Risk for respiratory failure related to progressive breathing difficulty'
      ],
      goals: [
        'Patient will demonstrate effective breathing patterns with normal rate and depth',
        'Patient will tolerate increased activity levels without excessive dyspnea',
        'Patient will exhibit reduced anxiety and improved comfort with breathing',
        'Patient will maintain adequate oxygenation without signs of respiratory failure'
      ],
      interventions: [
        'Assess breathing pattern, rate, depth, and use of accessory muscles',
        'Position patient in high-Fowler\'s or tripod position for optimal breathing',
        'Teach and encourage pursed-lip breathing and diaphragmatic breathing',
        'Administer oxygen therapy as prescribed and monitor saturation',
        'Provide calm environment and coaching during breathing exercises',
        'Monitor for signs of respiratory distress and fatigue',
        'Administer bronchodilators and medications as prescribed',
        'Encourage rest periods between activities to prevent exhaustion'
      ],
      evaluation: [
        'Breathing pattern improved with decreased dyspnea and normal rate',
        'Patient able to perform activities with manageable shortness of breath',
        'Anxiety reduced with improved confidence in breathing ability',
        'Respiratory status stable without signs of impending failure'
      ]
    }
  },
  {
    id: 'impaired-gas-exchange',
    specialtyId: 'respiratory-care',
    title: 'Impairment of Gas Exchange',
    description: 'Nursing care plan for gas exchange problems',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to ventilation-perfusion imbalance',
        'Activity intolerance related to inadequate oxygenation',
        'Risk for respiratory acidosis related to CO2 retention',
        'Anxiety related to hypoxemia and feeling of air hunger'
      ],
      goals: [
        'Patient will maintain adequate gas exchange with improved ABG values',
        'Patient will demonstrate improved activity tolerance without excessive fatigue',
        'Patient will maintain acid-base balance within normal limits',
        'Patient will exhibit reduced anxiety and improved comfort'
      ],
      interventions: [
        'Monitor arterial blood gas values and oxygen saturation continuously',
        'Administer oxygen therapy as prescribed to maintain SpO2 > 95%',
        'Position patient to optimize ventilation-perfusion matching',
        'Encourage deep breathing exercises and use of incentive spirometer',
        'Monitor for signs of hypoxemia: restlessness, confusion, cyanosis',
        'Assess respiratory rate, depth, and use of accessory muscles',
        'Administer bronchodilators and respiratory medications as prescribed',
        'Coordinate with respiratory therapy for breathing treatments'
      ],
      evaluation: [
        'Gas exchange improved with ABG values within acceptable limits',
        'Patient able to perform activities without excessive dyspnea or fatigue',
        'Acid-base balance maintained within normal parameters',
        'Patient demonstrates reduced anxiety and improved respiratory comfort'
      ]
    }
  },
  {
    id: 'influenza-respiratory',
    specialtyId: 'respiratory-care',
    title: 'Influenza (Flu)',
    description: 'Respiratory care plan for influenza',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Hyperthermia related to viral infection and inflammatory response',
        'Ineffective airway clearance related to increased respiratory secretions',
        'Risk for fluid volume deficit related to fever and decreased oral intake',
        'Activity intolerance related to fatigue and respiratory symptoms'
      ],
      goals: [
        'Patient will maintain normal body temperature within 24-48 hours',
        'Patient will maintain clear airway with effective cough and expectoration',
        'Patient will maintain adequate hydration as evidenced by normal urine output',
        'Patient will gradually return to normal activity level as symptoms resolve'
      ],
      interventions: [
        'Monitor temperature every 4 hours and administer antipyretics as prescribed',
        'Encourage fluid intake of 2-3 liters daily unless contraindicated',
        'Provide humidified air and encourage deep breathing exercises',
        'Administer antiviral medications as prescribed within 48 hours of onset',
        'Encourage rest and limit activities during acute phase',
        'Monitor respiratory status and oxygen saturation regularly',
        'Provide comfort measures: cool cloths, frequent linen changes',
        'Educate on infection control measures and when to seek medical attention'
      ],
      evaluation: [
        'Temperature returns to normal range and remains stable',
        'Airway clearance improved with productive cough and clear breath sounds',
        'Adequate hydration maintained with normal mucous membranes',
        'Patient reports improved energy and gradual return to activities'
      ]
    }
  },
  {
    id: 'mechanical-ventilation',
    specialtyId: 'respiratory-care',
    title: 'Mechanical Ventilation',
    description: 'Critical care plan for mechanical ventilation',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to underlying respiratory failure',
        'Risk for ventilator-associated pneumonia related to invasive airway',
        'Anxiety related to inability to speak and dependence on machine',
        'Risk for injury related to ventilator malfunction or disconnection'
      ],
      goals: [
        'Patient will maintain adequate gas exchange as evidenced by normal ABGs',
        'Patient will remain free from ventilator-associated infections',
        'Patient will demonstrate reduced anxiety and effective communication',
        'Patient will remain safe without ventilator-related complications'
      ],
      interventions: [
        'Monitor ventilator settings, alarms, and patient synchrony continuously',
        'Assess arterial blood gases and adjust ventilator parameters as ordered',
        'Provide oral care every 2 hours to prevent ventilator-associated pneumonia',
        'Monitor for signs of infection: fever, increased secretions, abnormal breath sounds',
        'Establish alternative communication methods: writing, picture boards',
        'Provide sedation and comfort measures as prescribed',
        'Ensure proper positioning and frequent turning to prevent complications',
        'Collaborate with respiratory therapy for weaning protocols when appropriate'
      ],
      evaluation: [
        'Gas exchange adequate with acceptable ABG results',
        'No signs of ventilator-associated pneumonia or other infections',
        'Patient communicates effectively and demonstrates reduced anxiety',
        'No ventilator-related injuries or complications occurred'
      ]
    }
  },
  {
    id: 'near-drowning',
    specialtyId: 'respiratory-care',
    title: 'Near-Drowning',
    description: 'Emergency care plan for near-drowning victims',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Impaired gas exchange related to water aspiration and pulmonary edema',
        'Risk for aspiration related to vomiting and altered consciousness',
        'Hypothermia related to cold water exposure',
        'Risk for cerebral edema related to hypoxic brain injury'
      ],
      goals: [
        'Patient will maintain adequate oxygenation and ventilation',
        'Patient will not experience further aspiration episodes',
        'Patient will achieve and maintain normal body temperature',
        'Patient will maintain normal neurological function without brain injury'
      ],
      interventions: [
        'Assess respiratory status and provide immediate ventilatory support',
        'Monitor arterial blood gases and oxygen saturation continuously',
        'Position patient to prevent aspiration and facilitate drainage',
        'Suction airway as needed to remove water and secretions',
        'Monitor core body temperature and initiate rewarming measures',
        'Assess neurological status using Glasgow Coma Scale',
        'Monitor for signs of pulmonary edema and cerebral edema',
        'Provide supportive care and monitor for delayed complications'
      ],
      evaluation: [
        'Respiratory function stabilized with adequate gas exchange',
        'No additional aspiration events occurred',
        'Body temperature normalized without complications',
        'Neurological status maintained or improved from baseline'
      ]
    }
  },
  {
    id: 'pleural-effusion',
    specialtyId: 'respiratory-care',
    title: 'Pleural Effusion',
    description: 'Nursing care plan for pleural effusion',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective breathing pattern related to decreased lung expansion',
        'Impaired gas exchange related to ventilation-perfusion imbalance',
        'Acute pain related to pleural inflammation and thoracentesis procedure',
        'Activity intolerance related to dyspnea and decreased oxygenation'
      ],
      goals: [
        'Patient will demonstrate improved breathing patterns with increased lung expansion',
        'Patient will maintain adequate gas exchange with oxygen saturation > 95%',
        'Patient will report pain level of 3 or less on 0-10 scale',
        'Patient will tolerate increased activity levels without excessive dyspnea'
      ],
      interventions: [
        'Position patient in high-Fowler\'s or semi-Fowler\'s position',
        'Monitor respiratory rate, depth, and use of accessory muscles',
        'Assess breath sounds and percuss chest for dullness',
        'Prepare patient for and assist with thoracentesis procedure',
        'Monitor for complications post-thoracentesis: pneumothorax, bleeding',
        'Administer oxygen therapy as prescribed and monitor saturation',
        'Encourage deep breathing exercises and incentive spirometry',
        'Monitor chest x-ray results and fluid reaccumulation'
      ],
      evaluation: [
        'Breathing pattern improved with better lung expansion',
        'Gas exchange adequate with normal oxygen saturation',
        'Pain controlled and manageable during procedures and recovery',
        'Activity tolerance improved with reduced dyspnea on exertion'
      ]
    }
  },
  {
    id: 'pulmonary-tuberculosis-respiratory',
    specialtyId: 'respiratory-care',
    title: 'Pulmonary Tuberculosis',
    description: 'Respiratory care plan for tuberculosis',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Risk for infection transmission related to airborne pathogens',
        'Ineffective airway clearance related to increased secretions',
        'Imbalanced nutrition related to decreased appetite and weight loss',
        'Fatigue related to chronic infection and inflammatory process'
      ],
      goals: [
        'Patient will not transmit infection to others',
        'Patient will maintain clear airway with effective cough',
        'Patient will maintain adequate nutritional status and stable weight',
        'Patient will report improved energy levels and reduced fatigue'
      ],
      interventions: [
        'Implement airborne isolation precautions and negative pressure room',
        'Ensure patient wears mask when outside isolation room',
        'Administer anti-tuberculosis medications as prescribed (RIPE therapy)',
        'Monitor for medication side effects and drug interactions',
        'Encourage high-calorie, high-protein diet and nutritional supplements',
        'Provide education on medication compliance and infection control',
        'Monitor sputum cultures and response to treatment',
        'Coordinate with public health for contact tracing and follow-up'
      ],
      evaluation: [
        'No secondary infections or transmission events documented',
        'Airway clearance improved with decreased productive cough',
        'Nutritional status stable with appropriate weight gain',
        'Patient reports increased energy and improved functional status'
      ]
    }
  },
  {
    id: 'tracheostomy',
    specialtyId: 'respiratory-care',
    title: 'Tracheostomy',
    description: 'Nursing care plan for tracheostomy management',
    category: 'Respiratory Care',
    pgie: {
      problems: [
        'Ineffective airway clearance related to artificial airway and secretions',
        'Risk for infection related to stoma site and disrupted skin integrity',
        'Impaired verbal communication related to altered airflow',
        'Risk for injury related to accidental decannulation or tube obstruction'
      ],
      goals: [
        'Patient will maintain patent airway with effective secretion clearance',
        'Patient will remain free from tracheostomy-related infections',
        'Patient will establish effective alternative communication methods',
        'Patient will remain safe without tracheostomy-related complications'
      ],
      interventions: [
        'Assess tracheostomy site and stoma for signs of infection or irritation',
        'Provide tracheostomy care every 8 hours: cleaning, suctioning, dressing change',
        'Suction tracheostomy as needed using sterile technique',
        'Secure tracheostomy tube properly and check ties for appropriate tension',
        'Establish communication board, writing materials, or speaking valve',
        'Humidify inspired air to prevent secretion thickening',
        'Keep spare tracheostomy tube and obturator at bedside',
        'Educate patient and family on tracheostomy care and emergency procedures'
      ],
      evaluation: [
        'Airway remains patent with effective cough and secretion clearance',
        'Tracheostomy site clean and dry without signs of infection',
        'Patient communicates needs effectively using alternative methods',
        'No complications such as accidental decannulation or tube obstruction'
      ]
    }
  },

  // Urinary Care
  {
    id: 'acute-glomerulonephritis',
    specialtyId: 'urinary-care',
    title: 'Acute Glomerulonephritis',
    description: 'Nursing care plan for acute kidney inflammation',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Excess fluid volume related to decreased glomerular filtration and sodium retention',
        'Risk for infection related to compromised immune response and inflammatory process',
        'Impaired urinary elimination related to decreased kidney function and oliguria',
        'Activity intolerance related to fatigue, fluid retention, and decreased oxygen-carrying capacity'
      ],
      goals: [
        'Patient will achieve fluid balance with absence of edema and stable weight',
        'Patient will remain free from secondary infections',
        'Patient will demonstrate improved urinary output with normal characteristics',
        'Patient will tolerate activities of daily living without excessive fatigue'
      ],
      interventions: [
        'Monitor daily weights and intake/output strictly, noting trends',
        'Assess for signs of fluid overload: edema, hypertension, dyspnea',
        'Restrict sodium and fluid intake as prescribed by physician',
        'Monitor laboratory values: BUN, creatinine, electrolytes, urinalysis',
        'Administer diuretics and antihypertensives as prescribed',
        'Monitor blood pressure every 4 hours and report elevations',
        'Implement infection prevention measures and monitor for signs of infection',
        'Provide high-calorie, low-protein, low-sodium diet as ordered'
      ],
      evaluation: [
        'Patient maintains stable weight without peripheral or pulmonary edema',
        'No signs of infection present, vital signs within normal limits',
        'Urine output improved with decreasing proteinuria and hematuria',
        'Patient participates in self-care activities with minimal fatigue'
      ]
    }
  },
  {
    id: 'acute-renal-failure',
    specialtyId: 'urinary-care',
    title: 'Acute Renal Failure',
    description: 'Emergency care plan for acute kidney failure',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Excess fluid volume related to decreased kidney function and inability to excrete fluid',
        'Risk for electrolyte imbalance related to impaired kidney filtration',
        'Impaired urinary elimination related to acute kidney injury and oliguria',
        'Risk for infection related to uremic toxins and compromised immune function'
      ],
      goals: [
        'Patient will maintain fluid balance without signs of fluid overload',
        'Patient will maintain electrolyte levels within normal limits',
        'Patient will demonstrate improved kidney function with increased urine output',
        'Patient will remain free from complications and infections'
      ],
      interventions: [
        'Monitor strict intake and output every hour, report oliguria (<30ml/hr)',
        'Weigh patient daily at same time with same scale and clothing',
        'Monitor laboratory values: BUN, creatinine, electrolytes, ABGs hourly',
        'Restrict fluid intake as ordered, typically 500ml plus previous day output',
        'Monitor for signs of fluid overload: JVD, crackles, peripheral edema',
        'Prepare for potential dialysis and maintain vascular access patency',
        'Administer medications as prescribed and adjust doses for kidney function',
        'Monitor vital signs every 2 hours and assess for arrhythmias'
      ],
      evaluation: [
        'Fluid balance maintained without pulmonary edema or severe hypertension',
        'Electrolyte levels stable without dangerous arrhythmias',
        'Urine output gradually improving, trending toward normal volumes',
        'No signs of uremic complications or secondary infections'
      ]
    }
  },
  {
    id: 'benign-prostatic-hyperplasia',
    specialtyId: 'urinary-care',
    title: 'Benign Prostatic Hyperplasia (BPH)',
    description: 'Nursing care plan for enlarged prostate',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Impaired urinary elimination related to mechanical obstruction of urethra',
        'Risk for urinary tract infection related to incomplete bladder emptying',
        'Sleep pattern disturbance related to nocturia and frequent urination',
        'Anxiety related to urinary symptoms and potential need for surgery'
      ],
      goals: [
        'Patient will demonstrate improved urinary flow with decreased residual volume',
        'Patient will remain free from urinary tract infections',
        'Patient will experience improved sleep quality with reduced nocturia',
        'Patient will verbalize reduced anxiety and understanding of treatment options'
      ],
      interventions: [
        'Assess urinary patterns: frequency, urgency, hesitancy, incomplete emptying',
        'Monitor post-void residual volumes and bladder scan results',
        'Encourage adequate fluid intake during day, limit fluids before bedtime',
        'Teach bladder training techniques and scheduled voiding',
        'Monitor for signs of UTI: dysuria, frequency, urgency, cloudy urine',
        'Administer prescribed medications: alpha-blockers, 5-alpha reductase inhibitors',
        'Educate about avoiding bladder irritants: caffeine, alcohol, spicy foods',
        'Provide preoperative teaching if surgical intervention planned'
      ],
      evaluation: [
        'Patient reports improved urinary stream and decreased voiding difficulty',
        'No signs of UTI, clear urine with normal urinalysis results',
        'Patient sleeps through night with minimal interruptions for voiding',
        'Patient demonstrates understanding of condition and treatment plan'
      ]
    }
  },
  {
    id: 'chronic-renal-failure',
    specialtyId: 'urinary-care',
    title: 'Chronic Renal Failure',
    description: 'Long-term care plan for chronic kidney disease',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Excess fluid volume related to decreased kidney function and impaired sodium excretion',
        'Imbalanced nutrition: less than body requirements related to uremic toxins and dietary restrictions',
        'Fatigue related to anemia and buildup of metabolic waste products',
        'Risk for infection related to compromised immune function and potential dialysis access'
      ],
      goals: [
        'Patient will maintain fluid balance within acceptable limits for kidney function',
        'Patient will maintain adequate nutritional status within dietary restrictions',
        'Patient will report improved energy levels and ability to perform activities',
        'Patient will remain free from infections and access site complications'
      ],
      interventions: [
        'Monitor daily weights and fluid restrictions as prescribed (usually 1-2L/day)',
        'Assess for signs of fluid overload: peripheral edema, dyspnea, hypertension',
        'Provide renal diet education: protein, phosphorus, potassium, sodium restrictions',
        'Monitor laboratory values: BUN, creatinine, electrolytes, hemoglobin, phosphorus',
        'Administer prescribed medications: phosphate binders, EPO, vitamin D',
        'Assess and protect dialysis access site if present (fistula, graft, catheter)',
        'Educate patient on medication compliance and dietary adherence',
        'Coordinate care with nephrologist and dialysis team as needed'
      ],
      evaluation: [
        'Patient maintains target weight without significant fluid retention',
        'Nutritional parameters stable with adherence to renal diet',
        'Patient reports increased energy and improved quality of life',
        'Dialysis access remains patent without signs of infection or complications'
      ]
    }
  },
  {
    id: 'hemodialysis',
    specialtyId: 'urinary-care',
    title: 'Hemodialysis',
    description: 'Nursing care plan for hemodialysis patients',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Risk for infection related to vascular access and exposure to bloodborne pathogens',
        'Risk for bleeding related to anticoagulation during dialysis procedure',
        'Fluid volume excess related to inability of kidneys to excrete excess fluid',
        'Risk for dialysis disequilibrium syndrome related to rapid fluid and electrolyte shifts'
      ],
      goals: [
        'Patient will remain free from access site infections and bloodstream infections',
        'Patient will maintain hemostasis without excessive bleeding complications',
        'Patient will achieve target dry weight with minimal fluid retention between treatments',
        'Patient will tolerate dialysis treatments without severe complications or symptoms'
      ],
      interventions: [
        'Assess vascular access before, during, and after dialysis for patency and infection',
        'Use strict aseptic technique for all access manipulations and needle insertions',
        'Monitor vital signs every 30 minutes during dialysis treatment',
        'Assess for bleeding at access site and apply appropriate pressure post-dialysis',
        'Monitor fluid removal rate and assess for hypotension or cramping',
        'Observe for signs of dialysis disequilibrium: headache, nausea, confusion',
        'Educate patient on access care, fluid restrictions, and dietary modifications',
        'Coordinate with dialysis team for treatment parameters and schedule adjustments'
      ],
      evaluation: [
        'Access site remains clean, dry, and free from signs of infection',
        'Hemostasis achieved within appropriate time frame post-dialysis',
        'Patient maintains target weight with acceptable interdialytic weight gain',
        'Patient tolerates treatments well with minimal adverse effects'
      ]
    }
  },
  {
    id: 'nephrotic-syndrome',
    specialtyId: 'urinary-care',
    title: 'Nephrotic Syndrome',
    description: 'Nursing care plan for nephrotic syndrome',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Excess fluid volume related to decreased oncotic pressure and sodium retention',
        'Risk for infection related to loss of immunoglobulins in urine',
        'Imbalanced nutrition: less than body requirements related to protein loss in urine',
        'Risk for thromboembolism related to loss of anticoagulant proteins and hemoconcentration'
      ],
      goals: [
        'Patient will achieve fluid balance with reduction in edema and ascites',
        'Patient will remain free from infections during immunocompromised state',
        'Patient will maintain adequate protein levels and nutritional status',
        'Patient will remain free from thromboembolic complications'
      ],
      interventions: [
        'Monitor daily weights and measure abdominal girth for ascites',
        'Assess degree and location of edema, particularly periorbital and pedal',
        'Restrict sodium intake as prescribed and monitor fluid restrictions',
        'Monitor serum protein, albumin, and cholesterol levels',
        'Assess urine for proteinuria using dipstick and 24-hour protein collection',
        'Implement infection prevention measures and monitor for signs of infection',
        'Administer prescribed medications: diuretics, ACE inhibitors, corticosteroids',
        'Educate patient on high-protein, low-sodium diet and medication compliance'
      ],
      evaluation: [
        'Patient demonstrates decreased edema and stable weight without fluid retention',
        'No signs of infection present, immunoglobulin levels improving',
        'Serum protein and albumin levels trending toward normal ranges',
        'No evidence of thrombotic events, adequate circulation maintained'
      ]
    }
  },
  {
    id: 'peritoneal-dialysis',
    specialtyId: 'urinary-care',
    title: 'Peritoneal Dialysis',
    description: 'Nursing care plan for peritoneal dialysis',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Risk for infection related to peritoneal catheter and repeated access to peritoneal cavity',
        'Risk for fluid imbalance related to dialysis exchanges and varying osmotic gradients',
        'Risk for catheter malfunction related to kinking, migration, or fibrin clots',
        'Deficient knowledge related to home dialysis procedure and self-care management'
      ],
      goals: [
        'Patient will remain free from peritonitis and catheter-related infections',
        'Patient will maintain fluid balance with adequate ultrafiltration',
        'Patient will maintain patent catheter function without mechanical complications',
        'Patient will demonstrate competency in performing dialysis procedure independently'
      ],
      interventions: [
        'Assess peritoneal catheter exit site for signs of infection daily',
        'Use strict aseptic technique for all catheter connections and exchanges',
        'Monitor dialysate effluent for clarity, color, and signs of infection',
        'Assess fluid balance by monitoring weights, blood pressure, and edema',
        'Check catheter function and position, assess for drainage problems',
        'Educate patient on proper hand hygiene and sterile technique',
        'Teach recognition of peritonitis symptoms: abdominal pain, cloudy effluent, fever',
        'Provide comprehensive training on dialysis procedure and troubleshooting'
      ],
      evaluation: [
        'Exit site clean and dry without erythema, drainage, or tenderness',
        'Fluid removal adequate with stable weight and blood pressure control',
        'Catheter drains and fills appropriately without mechanical issues',
        'Patient performs dialysis independently with confidence and proper technique'
      ]
    }
  },
  {
    id: 'urolithiasis',
    specialtyId: 'urinary-care',
    title: 'Urolithiasis (Renal Calculi)',
    description: 'Nursing care plan for kidney stones',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Acute pain related to obstruction of urinary tract by calculi and resulting spasms',
        'Impaired urinary elimination related to obstruction or irritation from stone passage',
        'Risk for infection related to urinary stasis and potential tissue trauma',
        'Deficient knowledge related to prevention strategies and dietary modifications'
      ],
      goals: [
        'Patient will report pain relief to acceptable level (≤3/10)',
        'Patient will pass stone spontaneously or have successful stone removal',
        'Patient will remain free from urinary tract infections',
        'Patient will demonstrate understanding of prevention strategies and dietary changes'
      ],
      interventions: [
        'Assess pain intensity using 0-10 scale and characteristics of pain',
        'Administer prescribed analgesics and antispasmodics as needed',
        'Encourage increased fluid intake (3-4 liters/day unless contraindicated)',
        'Strain all urine and save any stones for analysis',
        'Monitor urinary output and assess for hematuria, oliguria, or anuria',
        'Position patient for comfort and encourage ambulation to facilitate stone passage',
        'Monitor for signs of infection: fever, dysuria, frequency, urgency',
        'Educate on dietary modifications based on stone composition when known'
      ],
      evaluation: [
        'Patient reports pain controlled and manageable with prescribed medications',
        'Stone passed successfully or removed without complications',
        'Urine clear without signs of infection, normal urinalysis results',
        'Patient verbalizes understanding of prevention measures and demonstrates dietary compliance'
      ]
    }
  },
  {
    id: 'urinary-elimination',
    specialtyId: 'urinary-care',
    title: 'Urinary Elimination (Urinary Incontinence & Urinary Retention)',
    description: 'Care plan for urinary elimination problems',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Impaired urinary elimination related to neurological dysfunction, obstruction, or muscular weakness',
        'Risk for impaired skin integrity related to exposure to urine and moisture',
        'Social isolation related to embarrassment and fear of incontinence episodes',
        'Risk for urinary tract infection related to incomplete bladder emptying or catheter use'
      ],
      goals: [
        'Patient will achieve optimal urinary elimination pattern with minimal incontinence',
        'Patient will maintain intact skin integrity in perineal area',
        'Patient will participate in social activities without fear or embarrassment',
        'Patient will remain free from urinary tract infections'
      ],
      interventions: [
        'Assess baseline voiding patterns and bladder function using voiding diary',
        'Implement scheduled toileting or prompted voiding every 2-3 hours',
        'Teach pelvic floor exercises (Kegel exercises) to strengthen muscles',
        'Assess post-void residual volumes to evaluate complete emptying',
        'Provide perineal care after each voiding and apply barrier creams as needed',
        'Use appropriate incontinence products: pads, briefs, or external catheters',
        'Educate on fluid management: adequate intake with timed restrictions',
        'Monitor for signs of UTI and maintain catheter care if indwelling catheter present'
      ],
      evaluation: [
        'Patient demonstrates improved bladder control with fewer incontinence episodes',
        'Skin remains intact without breakdown, redness, or irritation',
        'Patient expresses increased confidence and willingness to engage socially',
        'No signs of UTI present, urine clear with normal characteristics'
      ]
    }
  },
  {
    id: 'urinary-tract-infection',
    specialtyId: 'urinary-care',
    title: 'Urinary Tract Infection',
    description: 'Nursing care plan for UTI management',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Impaired urinary elimination related to inflammation and irritation of urinary tract',
        'Acute pain related to inflammation and irritation of bladder and urethra',
        'Risk for ascending infection related to inadequate treatment or immune compromise',
        'Deficient knowledge related to prevention strategies and medication compliance'
      ],
      goals: [
        'Patient will demonstrate normal urinary elimination without dysuria or frequency',
        'Patient will report relief of pain and burning with urination',
        'Patient will remain free from complications such as pyelonephritis',
        'Patient will verbalize understanding of prevention measures and complete antibiotic course'
      ],
      interventions: [
        'Assess urinary symptoms: frequency, urgency, dysuria, suprapubic pain',
        'Obtain clean-catch urine specimen for culture and sensitivity before antibiotics',
        'Administer prescribed antibiotics as ordered and monitor for side effects',
        'Encourage increased fluid intake (2-3 liters/day) to flush bacteria',
        'Monitor temperature and assess for signs of systemic infection',
        'Educate on proper perineal hygiene: wiping front to back',
        'Teach about voiding after intercourse and avoiding bladder irritants',
        'Assess for resolution of symptoms and need for follow-up urine culture'
      ],
      evaluation: [
        'Patient voids without pain, burning, or urgency symptoms',
        'Pain and discomfort resolved with return to normal voiding patterns',
        'No signs of ascending infection or systemic complications',
        'Patient demonstrates understanding of prevention and completes full antibiotic course'
      ]
    }
  },
  {
    id: 'vesicoureteral-reflux',
    specialtyId: 'urinary-care',
    title: 'Vesicoureteral Reflux (VUR)',
    description: 'Pediatric care plan for urinary reflux',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Risk for urinary tract infection related to retrograde flow of urine',
        'Risk for kidney damage related to chronic reflux and repeated infections',
        'Impaired urinary elimination related to incomplete bladder emptying',
        'Anxiety (family) related to risk of kidney damage and need for monitoring'
      ],
      goals: [
        'Child will remain free from urinary tract infections',
        'Child will maintain normal kidney function without scarring',
        'Child will demonstrate complete bladder emptying with normal voiding patterns',
        'Family will verbalize understanding of condition and demonstrate compliance with treatment'
      ],
      interventions: [
        'Monitor for signs of UTI: fever, foul-smelling urine, irritability in infants',
        'Obtain clean urine specimens for culture when infection suspected',
        'Administer prophylactic antibiotics as prescribed (usually low-dose)',
        'Teach proper hygiene: frequent diaper changes, wiping front to back',
        'Encourage complete bladder emptying with double voiding technique',
        'Monitor growth and development, assess for failure to thrive',
        'Schedule regular follow-up imaging studies (VCUG, ultrasound)',
        'Educate family on importance of antibiotic compliance and infection prevention'
      ],
      evaluation: [
        'No UTIs documented during treatment period',
        'Kidney function tests remain normal without evidence of scarring',
        'Child voids completely without post-void residual volumes',
        'Family demonstrates understanding and adherence to treatment regimen'
      ]
    }
  },
  {
    id: 'wilms-tumor',
    specialtyId: 'urinary-care',
    title: 'Wilms Tumor (Nephroblastoma)',
    description: 'Pediatric care plan for kidney tumor',
    category: 'Urinary Care',
    pgie: {
      problems: [
        'Risk for injury related to tumor rupture and potential for metastasis',
        'Anxiety (family and child) related to cancer diagnosis and treatment uncertainties',
        'Risk for infection related to immunosuppression from chemotherapy',
        'Imbalanced nutrition: less than body requirements related to treatment effects and decreased appetite'
      ],
      goals: [
        'Child will remain free from tumor rupture and complications',
        'Family and child will demonstrate effective coping with reduced anxiety',
        'Child will remain free from infections during treatment',
        'Child will maintain adequate nutritional status and growth parameters'
      ],
      interventions: [
        'Avoid palpating abdomen to prevent tumor rupture, post signs at bedside',
        'Monitor for signs of tumor rupture: sudden abdominal pain, distension, hypotension',
        'Provide age-appropriate explanations and emotional support to child and family',
        'Implement neutropenic precautions during chemotherapy as indicated',
        'Monitor for signs of infection: fever, changes in behavior, poor feeding',
        'Coordinate with pediatric oncology team for treatment protocol',
        'Assess nutritional status and provide high-calorie supplements as needed',
        'Educate family on treatment plan, side effects, and home care requirements'
      ],
      evaluation: [
        'No evidence of tumor rupture or abdominal complications',
        'Family and child demonstrate improved coping and reduced anxiety levels',
        'Child shows no signs of infection throughout treatment course',
        'Child maintains weight and growth within acceptable parameters for age'
      ]
    }
  }
]
